import React, { useState, useEffect, useMemo } from 'react';
import type { 
    User, Organization, Pathway, PathwayBlock, BlockType, PathwayProgress, OjtSession,
    Activity as ActivityType, ActivityEntityType, ActivityAction,
    FieldChange, AdminActivityMetadata, CandidateActivityMetadata, PathwayActivityMetadata,
    BlockActivityMetadata, OjtSessionActivityMetadata, TeamActivityMetadata, ActivityMetadataMap,
    OjtBlockConfig, OjtObjective, OjtEvidenceConfig, OjtHandshakeConfig, OjtPreStartChecklistItem,
    OjtMode,
    CourseTemplate, BlockTrainingRoomLink,
    PracticalBlockConfig,
    AssessmentBlockConfig,
    UploadBlockConfig, ApiBlockConfig, AuthorisationBlockConfig,
    AssessmentAttempt, BlockUpload, PracticalEvaluation, PathwayAuthorisation,
    LevelReviewEntry,
    OjtAgentRun,
    OjtMessage
} from '../types';
import { BUILT_IN_OJT_MODES } from '../types';
import { DataService, AuthService, auth, db } from '../services/firebase';
import { renderBlockIcon, getDropdownBlockTypes, getDesignCardStyle, getBlockTypeDisplayName } from '../config/blockTypes';
import { Button, Input, Modal, Card, Tabs } from '../components/Common';
import { PathwayProgressViewerModal } from '../components/PathwayProgressViewerModal';
import { PathwayProgressFlow } from '../components/PathwayProgressFlow';
import { VisualFlowCanvas } from './admin/VisualFlowCanvas';
import type { AdminViewType } from '../App';
import { useTeamData } from '../contexts/TeamDataContext';
import type { TeamTrainee } from '../contexts/TeamDataContext';
import { countries } from '../data/countrycodes';
import { getFirestore, doc, updateDoc, collection, query, where, onSnapshot, getDocs, orderBy, limit } from 'firebase/firestore';

// Helper to parse phone number into country code and national number
const parsePhoneNumber = (phoneNumber: string | undefined): { country: typeof countries[0], nationalNumber: string } | null => {
    if (!phoneNumber) return null;
    
    // Sort countries by dialCode length descending for longest match first
    const sortedCountries = [...countries].sort((a, b) => b.dialCode.length - a.dialCode.length);
    
    for (const country of sortedCountries) {
        if (phoneNumber.startsWith(country.dialCode)) {
            return {
                country,
                nationalNumber: phoneNumber.slice(country.dialCode.length)
            };
        }
    }
    return null;
};

/**
 * Helper to compute field changes between old and new values.
 * Only includes fields that actually changed.
 */
const computeChanges = (oldValues: Record<string, any>, newValues: Record<string, any>, fields: string[]): FieldChange[] => {
    const changes: FieldChange[] = [];
    for (const field of fields) {
        const oldVal = oldValues[field] ?? '';
        const newVal = newValues[field] ?? '';
        if (oldVal !== newVal) {
            changes.push({ field, from: String(oldVal), to: String(newVal) });
        }
    }
    return changes;
};

/**
 * Type-safe activity logging for ADMIN entity.
 * @example logAdminActivity(orgId, 'UPDATED', userId, userName, performer, { changes: [...] });
 */
const logAdminActivity = async (
    orgId: string,
    action: ActivityAction,
    entityId: string,
    entityName: string,
    performedById: string,
    performedByName: string,
    metadata?: AdminActivityMetadata
) => logActivityInternal(orgId, 'ADMIN', action, entityId, entityName, performedById, performedByName, metadata);

/**
 * Type-safe activity logging for CANDIDATE entity.
 * @example logCandidateActivity(orgId, 'ASSIGNED', userId, userName, performer, { pathwayName: '...' });
 */
const logCandidateActivity = async (
    orgId: string,
    action: ActivityAction,
    entityId: string,
    entityName: string,
    performedById: string,
    performedByName: string,
    metadata?: CandidateActivityMetadata
) => logActivityInternal(orgId, 'CANDIDATE', action, entityId, entityName, performedById, performedByName, metadata);

/**
 * Type-safe activity logging for PATHWAY entity.
 * @example logPathwayActivity(orgId, 'CREATED', pathwayId, pathwayTitle, performer, { blocksCount: 5 });
 */
const logPathwayActivity = async (
    orgId: string,
    action: ActivityAction,
    entityId: string,
    entityName: string,
    performedById: string,
    performedByName: string,
    metadata?: PathwayActivityMetadata
) => logActivityInternal(orgId, 'PATHWAY', action, entityId, entityName, performedById, performedByName, metadata);

/**
 * Type-safe activity logging for BLOCK entity.
 * IMPORTANT: Always include pathwayName in metadata for context.
 * @example logBlockActivity(orgId, 'CREATED', blockId, blockName, performer, { pathwayName: '...', blockType: 'practical' });
 */
const logBlockActivity = async (
    orgId: string,
    action: ActivityAction,
    entityId: string,
    entityName: string,
    performedById: string,
    performedByName: string,
    metadata: BlockActivityMetadata // Required, not optional
) => logActivityInternal(orgId, 'BLOCK', action, entityId, entityName, performedById, performedByName, metadata);

/**
 * Type-safe activity logging for OJT_SESSION entity.
 * IMPORTANT: Always include candidateName and leadHandName for readable messages.
 * @example logOjtActivity(orgId, 'CREATED', sessionId, sessionTitle, performer, { candidateName: '...', leadHandName: '...' });
 */
const logOjtActivity = async (
    orgId: string,
    action: ActivityAction,
    entityId: string,
    entityName: string,
    performedById: string,
    performedByName: string,
    metadata: OjtSessionActivityMetadata // Required, not optional
) => logActivityInternal(orgId, 'OJT_SESSION', action, entityId, entityName, performedById, performedByName, metadata);

/**
 * Type-safe activity logging for TEAM entity.
 */
const logTeamActivity = async (
    orgId: string,
    action: ActivityAction,
    entityId: string,
    entityName: string,
    performedById: string,
    performedByName: string,
    metadata?: TeamActivityMetadata
) => logActivityInternal(orgId, 'TEAM', action, entityId, entityName, performedById, performedByName, metadata);

/**
 * Internal activity logger - use the type-safe helpers above instead.
 */
const logActivityInternal = async (
    orgId: string,
    entityType: ActivityEntityType,
    action: ActivityAction,
    entityId: string,
    entityName: string,
    performedById: string,
    performedByName: string,
    metadata?: ActivityMetadataMap[ActivityEntityType]
) => {
    try {
        const activityData: any = {
            orgId,
            entityType,
            action,
            entityId,
            entityName,
            performedById,
            performedByName,
            timestamp: Date.now()
        };
        // Only add metadata if it has values
        if (metadata && Object.keys(metadata).length > 0) {
            activityData.metadata = metadata;
        }
        console.log('📝 Logging activity:', activityData);
        await DataService.createActivity(activityData);
        console.log('✅ Activity logged successfully');
    } catch (e) {
        console.error('Failed to log activity:', e);
    }
};

import { 
  Building2, 
  Users, 
  Plus, 
  Trash2, 
  Edit2, 
  Layers, 
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Activity as ActivityIcon,
  AlertCircle,
  Clock,
  CheckCircle2,
  MousePointer2,
  GitMerge,
  Network,
  ClipboardList,
  Calendar,
  UserCircle,
  TrendingUp,
  FileText,
  BookOpen,
  Truck,
  RotateCcw,
  CheckSquare,
  ArrowDown,
  ArrowUp,
  ArrowRight,
  Shield,
  GripVertical,
  History,
  GitBranch,
  ShieldCheck,
  Award,
  UserPlus,
  UserCheck,
  Eye,
  EyeOff,
  ChevronLeft,
  Camera,
  PenTool,
  Pencil,
  Image as ImageIcon,
  MoveHorizontal,
  X,
  Check,
  Search,
  Zap,
  ArrowLeftRight,
  Crosshair,
  MapPin,
  ListChecks,
  Target,
  Fingerprint,
  Save,
  Upload,
  Stamp,
  Hash,
  XCircle,
  Bot,
  Copy,
  MessageSquare,
  ShieldAlert,
  AlertTriangle,
  HelpCircle,
  Hourglass,
  RefreshCw
} from 'lucide-react';

interface AdminDashboardProps {
  user: User;
  activeOrgId?: string;
  onUpdateUser: () => void;
  onSwitchOrg: (orgId: string, includeSubTeams: boolean) => void;
  currentView: AdminViewType;
  onSwitchView: (view: AdminViewType) => void;
}

// --- Types for Organization Tree ---
interface TreeOrganization extends Organization {
  children: TreeOrganization[];
}

// --- Types for Manager Components ---
interface AdminsManagerProps {
  admins: Array<{ id: string; name: string; email: string; phoneNumber?: string; isRemovable: boolean; teamsadminnest?: any }>;
  isLoading: boolean;
  activeOrgName: string;
  onOpenUserModal: (mode: 'CREATE' | 'EDIT', user?: any) => void;
  onRemoveAdmin: (userId: string, userName: string) => void;
}

interface CandidatesManagerProps {
  candidates: Array<{ id: string; name: string; email: string; phoneNumber?: string; role?: string; derived?: any }>;
  isLoading: boolean;
  activeOrgName: string;
  selectedCandidates: Array<{ id: string; name: string }>;
  onOpenUserModal: (mode: 'CREATE' | 'EDIT', user?: any) => void;
  onViewPathways: (user: any) => void;
  onToggleCandidateSelection: (candidate: { id: string; name: string }) => void;
  onSelectAllCandidates: () => void;
  onClearSelection: () => void;
  onOpenMoveRemoveModal: () => void;
}

// --- Extracted Manager Components (defined at module level to prevent re-creation) ---

const AdminsManager: React.FC<AdminsManagerProps> = ({
  admins,
  isLoading,
  activeOrgName,
  onOpenUserModal,
  onRemoveAdmin
}) => {
  const [adminSearch, setAdminSearch] = useState('');
  
  // Filter admins based on search
  const filteredAdmins = useMemo(() => {
    if (!adminSearch.trim()) return admins;
    const query = adminSearch.toLowerCase();
    return admins.filter(u => 
      u.name.toLowerCase().includes(query) ||
      (u.email && u.email.toLowerCase().includes(query)) ||
      (u.phoneNumber && u.phoneNumber.includes(adminSearch))
    );
  }, [admins, adminSearch]);
  
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h3 className="text-lg font-bold text-aero-900">Manage Admins</h3>
          <p className="text-sm text-aero-500">
            Managing administrators for {activeOrgName || 'this organization'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aero-400" />
            <input
              type="text"
              value={adminSearch}
              onChange={(e) => setAdminSearch(e.target.value)}
              placeholder="Search admins..."
              className="pl-9 pr-3 py-2 text-sm border border-aero-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aero-500 w-48 lg:w-64"
            />
            {adminSearch && (
              <button 
                onClick={() => setAdminSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-aero-100 rounded"
              >
                <X className="w-3 h-3 text-aero-400" />
              </button>
            )}
          </div>
          <Button variant="primary" onClick={() => onOpenUserModal('CREATE')}>
            <Shield className="w-4 h-4 mr-2" /> Add Admin
          </Button>
        </div>
      </div>
      <Card className="p-0 overflow-hidden border-aero-200 shadow-sm">
        <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-aero-50 text-aero-900 font-semibold border-b border-aero-200">
            <tr>
              <th className="px-6 py-4">User</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Phone</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-aero-100">
            {filteredAdmins.map(u => (
              <tr key={u.id} className="hover:bg-aero-50/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="font-bold text-aero-900">{u.name}</div>
                </td>
                <td className="px-6 py-4"><div className="text-aero-600">{u.email || '-'}</div></td>
                <td className="px-6 py-4"><div className="text-aero-600">{u.phoneNumber || '-'}</div></td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => onOpenUserModal('EDIT', u as any)} className="p-2 hover:bg-aero-100 rounded-lg text-aero-400 hover:text-aero-600 transition-colors">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {u.isRemovable ? (
                      <button 
                        onClick={() => onRemoveAdmin(u.id, u.name)} 
                        className="p-2 hover:bg-red-50 rounded-lg text-aero-400 hover:text-red-500 transition-colors"
                        title="Remove admin from this team"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <button 
                        disabled
                        className="p-2 rounded-lg text-aero-200 cursor-not-allowed"
                        title="Admins can only be removed from their top level team"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredAdmins.length === 0 && !isLoading && (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-aero-400 italic">
                  {adminSearch ? `No admins matching "${adminSearch}"` : 'No admins found for this organization.'}
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-aero-400 italic">
                  Loading admins...
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
};

const CandidatesManager: React.FC<CandidatesManagerProps> = ({
  candidates,
  isLoading,
  activeOrgName,
  selectedCandidates,
  onOpenUserModal,
  onViewPathways,
  onToggleCandidateSelection,
  onSelectAllCandidates,
  onClearSelection,
  onOpenMoveRemoveModal
}) => {
  const [candidateSearch, setCandidateSearch] = useState('');
  
  // Filter candidates based on search
  const filteredCandidates = useMemo(() => {
    if (!candidateSearch.trim()) return candidates;
    const query = candidateSearch.toLowerCase();
    return candidates.filter(u => 
      u.name.toLowerCase().includes(query) ||
      (u.email && u.email.toLowerCase().includes(query)) ||
      (u.phoneNumber && u.phoneNumber.includes(candidateSearch))
    );
  }, [candidates, candidateSearch]);
  
  const directCandidates = filteredCandidates.filter(c => !!c.role);
  const allDirectSelected = directCandidates.length > 0 && selectedCandidates.length === directCandidates.length;
  
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h3 className="text-lg font-bold text-aero-900">Manage Candidates</h3>
          <p className="text-sm text-aero-500">
            Managing trainees for {activeOrgName || 'this organization'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aero-400" />
            <input
              type="text"
              value={candidateSearch}
              onChange={(e) => setCandidateSearch(e.target.value)}
              placeholder="Search candidates..."
              className="pl-9 pr-3 py-2 text-sm border border-aero-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aero-500 w-40 lg:w-64"
            />
            {candidateSearch && (
              <button 
                onClick={() => setCandidateSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-aero-100 rounded"
              >
                <X className="w-3 h-3 text-aero-400" />
              </button>
            )}
          </div>
          <Button 
            variant="secondary" 
            onClick={onOpenMoveRemoveModal}
            disabled={selectedCandidates.length === 0}
          >
            <MoveHorizontal className="w-4 h-4 mr-2" /> Move/Remove Candidates
          </Button>
          <Button variant="primary" onClick={() => onOpenUserModal('CREATE')}>
            <UserPlus className="w-4 h-4 mr-2" /> Add Candidate
          </Button>
        </div>
      </div>
      
      {/* Selection Counter */}
      {selectedCandidates.length > 0 && (
        <div className="flex items-center gap-3 bg-aero-50 border border-aero-200 rounded-lg px-4 py-2">
          <span className="text-sm font-medium text-aero-700">
            Selected: {selectedCandidates.length}
          </span>
          <button 
            onClick={onClearSelection}
            className="text-xs px-2 py-1 bg-aero-200 hover:bg-aero-300 rounded text-aero-700 transition-colors"
          >
            Clear Selection
          </button>
        </div>
      )}
      
      <Card className="p-0 overflow-hidden border-aero-200 shadow-sm">
        <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-aero-50 text-aero-900 font-semibold border-b border-aero-200">
            <tr>
              <th className="px-4 py-4 w-12">
                <input 
                  type="checkbox"
                  checked={allDirectSelected}
                  onChange={onSelectAllCandidates}
                  className="w-4 h-4 rounded border-aero-300 text-aero-600 focus:ring-aero-500"
                  title="Select all direct candidates"
                />
              </th>
              <th className="px-6 py-4">User</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Phone</th>
              <th className="px-6 py-4">Role</th>
              <th className="px-6 py-4">Context</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-aero-100">
            {filteredCandidates.map(u => {
              const hasDerived = !!u.derived;
              const isDirect = !!u.role;
              const isSelected = selectedCandidates.some(c => c.id === u.id);
              
              return (
                <tr key={u.id} className={`transition-colors ${isSelected ? 'bg-aero-100' : 'hover:bg-aero-50/50'}`}>
                  <td className="px-4 py-4">
                    {isDirect ? (
                      <input 
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleCandidateSelection({ id: u.id, name: u.name })}
                        className="w-4 h-4 rounded border-aero-300 text-aero-600 focus:ring-aero-500"
                      />
                    ) : (
                      <span className="text-aero-300" title="Sub-team candidates cannot be selected">
                        <input 
                          type="checkbox"
                          disabled
                          className="w-4 h-4 rounded border-aero-200 text-aero-300 cursor-not-allowed"
                        />
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-bold text-aero-900">{u.name}</div>
                  </td>
                  <td className="px-6 py-4"><div className="text-aero-600">{u.email || '-'}</div></td>
                  <td className="px-6 py-4"><div className="text-aero-600">{u.phoneNumber || '-'}</div></td>
                  <td className="px-6 py-4">
                    <span className="inline-flex px-2 py-1 rounded-md text-xs font-bold border bg-blue-50 text-blue-700 border-blue-100">
                      CANDIDATE
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {hasDerived && !isDirect ? (
                      <span className="text-[10px] uppercase font-bold text-aero-400 bg-aero-50 px-2 py-1 rounded border border-aero-100">Sub-team</span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">Direct</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => onViewPathways(u as any)}
                        className="p-2 hover:bg-aero-100 rounded-lg text-aero-500 hover:text-aero-700 transition-colors"
                        title="View Pathways"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => onOpenUserModal('EDIT', u as any)} className="p-2 hover:bg-aero-100 rounded-lg text-aero-400 hover:text-aero-600 transition-colors">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredCandidates.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-aero-400 italic">
                  {candidateSearch ? `No candidates matching "${candidateSearch}"` : 'No candidates found for this organization.'}
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-aero-400 italic">
                  Loading candidates...
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
};

// --- Dashboard Components ---

const RecentlyEditedPathways = ({ activeOrgId }: { activeOrgId?: string }) => {
  const [pathways, setPathways] = useState<{ id: string; title: string; lastEdited: string; role: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPathways = async () => {
      if (!activeOrgId) {
        setPathways([]);
        setIsLoading(false);
        return;
      }
      try {
        const data = await DataService.getPathways(activeOrgId);
        // Sort by lastEdited and take top 4
        const sorted = data.sort((a, b) => {
          const dateA = new Date(a.lastEdited).getTime() || 0;
          const dateB = new Date(b.lastEdited).getTime() || 0;
          return dateB - dateA;
        }).slice(0, 4);
        setPathways(sorted.map(p => ({
          id: p.id,
          title: p.title,
          lastEdited: p.lastEdited,
          role: p.role
        })));
      } catch (e) {
        console.error('Failed to load pathways', e);
      } finally {
        setIsLoading(false);
      }
    };
    loadPathways();
  }, [activeOrgId]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-full space-y-4">
        <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
          <Network className="w-5 h-5 text-aero-600" />
          Recently Edited Pathways
        </h3>
        <div className="bg-white rounded-xl border border-aero-200 shadow-sm flex-1 flex items-center justify-center">
          <div className="text-aero-400 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-4">
      <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
        <Network className="w-5 h-5 text-aero-600" />
        Recently Edited Pathways
      </h3>
      <div className="bg-white rounded-xl border border-aero-200 shadow-sm flex-1 overflow-hidden">
        {pathways.length === 0 ? (
          <div className="p-8 text-center text-aero-400 italic">No pathways found. Create one to get started.</div>
        ) : (
        <div className="divide-y divide-aero-50">
          {pathways.map((p) => (
            <div key={p.id} className="p-4 hover:bg-aero-50 transition-colors group cursor-pointer">
               <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-sm text-aero-800 group-hover:text-aero-600 transition-colors">{p.title}</h4>
                    <div className="flex items-center gap-1.5 mt-1">
                      <UserCircle className="w-3 h-3 text-aero-400" />
                      <span className="text-[10px] text-aero-500">{p.role}</span>
                    </div>
                  </div>
                  <span className="text-[10px] font-medium bg-aero-50 text-aero-400 px-2 py-1 rounded-full border border-aero-100 whitespace-nowrap">
                    {p.lastEdited}
                  </span>
               </div>
            </div>
          ))}
        </div>
        )}
        <div className="p-3 bg-aero-50 border-t border-aero-100 text-center">
            <button className="text-xs font-bold text-aero-600 hover:text-aero-800 transition-colors">View All Pathways</button>
        </div>
      </div>
    </div>
  );
};

const OJTAssignmentsPanel = ({ activeOrgId }: { activeOrgId?: string }) => {
  const [view, setView] = useState<'TODAY' | 'LAST_7' | 'NEXT_7'>('TODAY');
  const [sessions, setSessions] = useState<OjtSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSessions = async () => {
      if (!activeOrgId) {
        setSessions([]);
        setIsLoading(false);
        return;
      }
      try {
        const data = await DataService.getOjtSessions(activeOrgId);
        setSessions(data);
      } catch (e) {
        console.error('Failed to load OJT sessions', e);
      } finally {
        setIsLoading(false);
      }
    };
    loadSessions();
  }, [activeOrgId]);

  const getAssignments = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysAhead = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

    let filtered: OjtSession[] = [];
    if (view === 'TODAY') {
      filtered = sessions.filter(s => {
        const d = new Date(s.date);
        return d >= today && d < tomorrow;
      });
    } else if (view === 'LAST_7') {
      filtered = sessions.filter(s => {
        const d = new Date(s.date);
        return d >= sevenDaysAgo && d < today;
      });
    } else {
      filtered = sessions.filter(s => {
        const d = new Date(s.date);
        return d >= tomorrow && d < sevenDaysAhead;
      });
    }

    return filtered.map(s => ({
      id: s.id,
      title: s.title,
      candidate: s.candidateName,
      time: new Date(s.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: s.status === 'COMPLETED' ? 'COMPLETED' : s.status === 'REVIEW_PENDING' ? 'PENDING' : 'SCHEDULED'
    }));
  };

  const items = getAssignments();

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-aero-600" />
            OJT Assignments
        </h3>
        
        <div className="bg-aero-100 p-1 rounded-lg flex text-[10px] sm:text-xs font-medium self-start sm:self-auto">
            <button 
                onClick={() => setView('TODAY')}
                className={`px-3 py-1.5 rounded-md transition-all ${view === 'TODAY' ? 'bg-white text-aero-900 shadow-sm' : 'text-aero-500 hover:text-aero-700'}`}
            >
                Today
            </button>
            <button 
                onClick={() => setView('LAST_7')}
                className={`px-3 py-1.5 rounded-md transition-all ${view === 'LAST_7' ? 'bg-white text-aero-900 shadow-sm' : 'text-aero-500 hover:text-aero-700'}`}
            >
                Last 7 Days
            </button>
            <button 
                onClick={() => setView('NEXT_7')}
                className={`px-3 py-1.5 rounded-md transition-all ${view === 'NEXT_7' ? 'bg-white text-aero-900 shadow-sm' : 'text-aero-500 hover:text-aero-700'}`}
            >
                Next 7 Days
            </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-aero-200 shadow-sm flex-1 min-h-[250px] overflow-hidden flex flex-col">
         <div className="flex-1 overflow-y-auto custom-scrollbar">
             {items.length > 0 ? (
                 <div className="divide-y divide-aero-50">
                    {items.map(item => (
                        <div key={item.id} className="p-4 flex items-center justify-between hover:bg-aero-50 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className={`w-1.5 h-10 rounded-full ${
                                    item.status === 'COMPLETED' ? 'bg-green-500' : 
                                    item.status === 'PENDING' ? 'bg-orange-400' : 'bg-blue-500'
                                }`}></div>
                                <div>
                                    <h4 className="font-bold text-sm text-aero-900">{item.title}</h4>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <UserCircle className="w-3 h-3 text-aero-400" />
                                        <span className="text-xs text-aero-600">{item.candidate}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right pl-4">
                                <span className="block text-xs font-bold text-aero-700 whitespace-nowrap">{item.time}</span>
                                <span className={`text-[9px] font-bold uppercase tracking-wider ${
                                    item.status === 'COMPLETED' ? 'text-green-600' : 
                                    item.status === 'PENDING' ? 'text-orange-500' : 'text-blue-500'
                                }`}>{item.status}</span>
                            </div>
                        </div>
                    ))}
                 </div>
             ) : (
                 <div className="h-full flex flex-col items-center justify-center text-aero-400 p-8">
                     <Calendar className="w-10 h-10 mb-2 opacity-20" />
                     <p className="text-sm">No assignments found.</p>
                 </div>
             )}
         </div>
         <div className="p-3 bg-aero-50 border-t border-aero-100 text-center">
            <button className="text-xs font-bold text-aero-600 hover:text-aero-800 transition-colors">Go to Assignments</button>
        </div>
      </div>
    </div>
  );
};

const ActiveCandidatesPanel = ({ activeOrgId }: { activeOrgId?: string }) => {
  const { candidates: teamCandidates, isLoading: teamIsLoading } = useTeamData();
  const [candidates, setCandidates] = useState<{ id: string; name: string; pathway: string; pathwayId: string; selectedTrack?: string; progress: number; status: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [progressViewer, setProgressViewer] = useState<{ isOpen: boolean; candidateId: string; candidateName: string; pathwayId: string; selectedTrack?: string }>({ isOpen: false, candidateId: '', candidateName: '', pathwayId: '' });

  useEffect(() => {
    const loadCandidates = async () => {
      if (!activeOrgId) {
        setCandidates([]);
        setIsLoading(false);
        return;
      }
      if (teamIsLoading) {
        setIsLoading(true);
        return;
      }
      try {
        // For each candidate, get their pathway progress
        const candidateProgress = await Promise.all(teamCandidates.slice(0, 5).map(async (user) => {
          const pathways = await DataService.getPathwaysForCandidate(user.id);
          const activePathway = pathways.find(p => p.status === 'IN_PROGRESS') || pathways[0];
          return {
            id: user.id,
            name: user.name,
            pathway: activePathway?.pathwayTitle || 'No pathway',
            pathwayId: activePathway?.pathwayId || '',
            selectedTrack: activePathway?.selectedTrack,
            progress: activePathway?.progress || 0,
            status: activePathway?.status === 'IN_PROGRESS' ? 'In Progress' : activePathway?.status === 'COMPLETED' ? 'Completed' : 'Not Started'
          };
        }));
        setCandidates(candidateProgress);
      } catch (e) {
        console.error('Failed to load candidates', e);
      } finally {
        setIsLoading(false);
      }
    };
    loadCandidates();
  }, [activeOrgId, teamCandidates, teamIsLoading]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-aero-600" />
          Active Candidates
        </h3>
        <div className="bg-white rounded-xl border border-aero-200 p-8 text-center text-aero-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-end">
          <div>
            <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-aero-600" />
                Active Candidates
            </h3>
            <p className="text-sm text-aero-500 mt-1">Candidates currently in a pathway but not yet SoloReady.</p>
          </div>
          <Button variant="secondary" className="h-8 text-xs">View All</Button>
      </div>
      <div className="bg-white rounded-xl border border-aero-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
                <thead className="bg-aero-50 text-aero-600 font-semibold border-b border-aero-100">
                    <tr>
                        <th className="px-6 py-3">Candidate</th>
                        <th className="px-6 py-3">Current Pathway</th>
                        <th className="px-6 py-3">Progress</th>
                        <th className="px-6 py-3 text-right">Status</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-aero-50">
                    {candidates.map(c => (
                        <tr
                            key={c.id}
                            className={`transition-colors ${c.pathwayId ? 'cursor-pointer hover:bg-aero-50' : 'hover:bg-aero-50'}`}
                            onClick={() => c.pathwayId && setProgressViewer({ isOpen: true, candidateId: c.id, candidateName: c.name, pathwayId: c.pathwayId, selectedTrack: c.selectedTrack })}
                        >
                            <td className="px-6 py-3 font-medium text-aero-900">{c.name}</td>
                            <td className="px-6 py-3 text-aero-600">{c.pathway}</td>
                            <td className="px-6 py-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-24 bg-aero-100 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full ${c.progress > 80 ? 'bg-green-500' : 'bg-blue-500'}`}
                                            style={{ width: `${c.progress}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-xs font-bold text-aero-500">{c.progress}%</span>
                                </div>
                            </td>
                            <td className="px-6 py-3 text-right">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                    c.status === 'In Progress'
                                        ? 'bg-blue-50 text-blue-700 border-blue-100'
                                        : 'bg-orange-50 text-orange-700 border-orange-100'
                                }`}>
                                    {c.status}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      </div>
      <PathwayProgressViewerModal
          isOpen={progressViewer.isOpen}
          onClose={() => setProgressViewer(v => ({ ...v, isOpen: false }))}
          candidateName={progressViewer.candidateName}
          candidateId={progressViewer.candidateId}
          pathwayId={progressViewer.pathwayId}
          selectedTrack={progressViewer.selectedTrack}
      />
    </div>
  );
};

const ActivityItem = ({ text, time, index, details }: { text: React.ReactNode, time: string, index: number, details?: string | null }) => (
    <div 
        className="flex items-start gap-4 p-4 bg-white border-b border-aero-100 last:border-0 hover:bg-aero-50 transition-colors group relative cursor-default"
        title={details || undefined}
    >
        <div className="flex-1 text-sm text-aero-700">
            {text}
            {details && (
                <span className="ml-2 text-xs text-aero-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    ⓘ
                </span>
            )}
        </div>
        <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full whitespace-nowrap">{time}</span>
        {/* Hover tooltip for details */}
        {details && (
            <div className="absolute left-4 top-full mt-1 z-50 hidden group-hover:block bg-aero-900 text-white text-xs rounded-lg p-3 shadow-lg max-w-sm whitespace-pre-line">
                {details}
            </div>
        )}
    </div>
);

// Helper to format relative time
const formatRelativeTime = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return new Date(timestamp).toLocaleDateString();
};

/**
 * Format the activity metadata into a readable tooltip string.
 * Returns null if no meaningful details to show.
 */
const formatActivityDetails = (activity: ActivityType): string | null => {
    const metadata = activity.metadata as any;
    if (!metadata) return null;
    
    const details: string[] = [];
    
    // Handle field changes (for UPDATED actions)
    if (metadata.changes && Array.isArray(metadata.changes) && metadata.changes.length > 0) {
        for (const change of metadata.changes) {
            const fromVal = change.from || '(empty)';
            const toVal = change.to || '(empty)';
            details.push(`${change.field}: "${fromVal}" → "${toVal}"`);
        }
    }
    
    // Handle pathway context for blocks
    if (metadata.pathwayName) {
        details.push(`Pathway: ${metadata.pathwayName}`);
    }
    
    // Handle OJT session details
    if (metadata.candidateName && metadata.leadHandName) {
        details.push(`${metadata.candidateName} with ${metadata.leadHandName}`);
    }
    if (metadata.scheduledDate) {
        details.push(`Scheduled: ${new Date(metadata.scheduledDate).toLocaleDateString()}`);
    }
    if (metadata.duration) {
        details.push(`Duration: ${metadata.duration} minutes`);
    }
    
    // Handle status changes
    if (metadata.statusChange) {
        details.push(`Status: ${metadata.statusChange.from} → ${metadata.statusChange.to}`);
    }
    
    return details.length > 0 ? details.join('\n') : null;
};

// Helper to format activity message
const formatActivityMessage = (activity: ActivityType): React.ReactNode => {
    const entityName = <strong>{activity.entityName}</strong>;
    const performerName = activity.performedByName;
    const metadata = activity.metadata as any;
    
    switch (activity.entityType) {
        case 'ADMIN':
            switch (activity.action) {
                case 'CREATED': return <span>{performerName} added {entityName} as an admin</span>;
                case 'UPDATED': return <span>{performerName} updated admin {entityName}</span>;
                case 'DELETED': return <span>{performerName} removed {entityName} as admin</span>;
                default: return <span>{performerName} {activity.action.toLowerCase()} admin {entityName}</span>;
            }
        case 'CANDIDATE':
            switch (activity.action) {
                case 'CREATED': return <span>{performerName} added {entityName} as a candidate</span>;
                case 'UPDATED': return <span>{performerName} updated candidate {entityName}</span>;
                case 'DELETED': return <span>{performerName} removed candidate {entityName}</span>;
                case 'ASSIGNED': {
                    const pathwayName = metadata?.pathwayName;
                    return pathwayName 
                        ? <span>{performerName} assigned {entityName} to <strong>{pathwayName}</strong></span>
                        : <span>{performerName} assigned {entityName} to a pathway</span>;
                }
                case 'COMPLETED': return <span>{entityName} completed their training</span>;
                case 'SIGNED_OFF': return <span>{performerName} signed off {entityName}'s assessment</span>;
                default: return <span>{performerName} {activity.action.toLowerCase()} candidate {entityName}</span>;
            }
        case 'PATHWAY':
            switch (activity.action) {
                case 'CREATED': return <span>{performerName} created pathway {entityName}</span>;
                case 'UPDATED': return <span>{performerName} updated pathway {entityName}</span>;
                case 'DELETED': return <span>{performerName} deleted pathway {entityName}</span>;
                default: return <span>{performerName} {activity.action.toLowerCase()} pathway {entityName}</span>;
            }
        case 'BLOCK': {
            const pathwayName = metadata?.pathwayName;
            const inPathway = pathwayName ? <> in <strong>{pathwayName}</strong></> : '';
            switch (activity.action) {
                case 'CREATED': return <span>{performerName} added block {entityName}{inPathway}</span>;
                case 'UPDATED': return <span>{performerName} updated block {entityName}{inPathway}</span>;
                case 'DELETED': return <span>{performerName} deleted block {entityName}{inPathway}</span>;
                default: return <span>{performerName} {activity.action.toLowerCase()} block {entityName}{inPathway}</span>;
            }
        }
        case 'OJT_SESSION': {
            const candidateName = metadata?.candidateName;
            const leadHandName = metadata?.leadHandName;
            switch (activity.action) {
                case 'CREATED': {
                    if (candidateName && leadHandName) {
                        return <span>{performerName} scheduled OJT: <strong>{candidateName}</strong> with <strong>{leadHandName}</strong></span>;
                    }
                    return <span>{performerName} scheduled OJT session {entityName}</span>;
                }
                case 'COMPLETED': {
                    if (candidateName && leadHandName) {
                        return <span><strong>{candidateName}</strong> completed OJT with <strong>{leadHandName}</strong></span>;
                    }
                    return <span>{entityName} OJT session completed</span>;
                }
                case 'SIGNED_OFF': return <span>{performerName} signed off {entityName}</span>;
                default: return <span>{performerName} {activity.action.toLowerCase()} OJT session {entityName}</span>;
            }
        }
        case 'TEAM':
            switch (activity.action) {
                case 'CREATED': return <span>{performerName} created team {entityName}</span>;
                case 'UPDATED': return <span>{performerName} updated team {entityName}</span>;
                case 'DELETED': return <span>{performerName} deleted team {entityName}</span>;
                default: return <span>{performerName} {activity.action.toLowerCase()} team {entityName}</span>;
            }
        default:
            return <span>{performerName} {activity.action.toLowerCase()} {entityName}</span>;
    }
};

const ACTIVITY_PAGE_SIZE = 10;

const ActivityFeed = ({ activeOrgId, refreshKey }: { activeOrgId?: string; refreshKey?: number }) => {
    const [activities, setActivities] = useState<ActivityType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentLimit, setCurrentLimit] = useState(ACTIVITY_PAGE_SIZE);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        const loadActivities = async () => {
            if (!activeOrgId) {
                setActivities([]);
                setIsLoading(false);
                setHasMore(false);
                return;
            }
            try {
                console.log('📋 Loading activities for org:', activeOrgId);
                const data = await DataService.getActivities(activeOrgId, ACTIVITY_PAGE_SIZE);
                console.log('📋 Loaded activities:', data.length, data);
                setActivities(data);
                setHasMore(data.length === ACTIVITY_PAGE_SIZE);
                setCurrentLimit(ACTIVITY_PAGE_SIZE);
            } catch (e) {
                console.error('Failed to load activities', e);
            } finally {
                setIsLoading(false);
            }
        };
        loadActivities();
    }, [activeOrgId, refreshKey]);

    const handleLoadMore = async () => {
        if (!activeOrgId || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const newLimit = currentLimit + ACTIVITY_PAGE_SIZE;
            const data = await DataService.getActivities(activeOrgId, newLimit);
            setActivities(data);
            setCurrentLimit(newLimit);
            setHasMore(data.length === newLimit);
        } catch (e) {
            console.error('Failed to load more activities', e);
        } finally {
            setIsLoadingMore(false);
        }
    };

    if (isLoading) {
        return (
            <div className="space-y-4">
                <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
                    <ActivityIcon className="w-5 h-5 text-aero-600" />
                    Activity Feed
                </h3>
                <div className="bg-white rounded-lg border border-aero-200 overflow-hidden shadow-sm p-8 text-center text-aero-400">
                    Loading...
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
                <ActivityIcon className="w-5 h-5 text-aero-600" />
                Activity Feed
            </h3>
            <div className="bg-white rounded-lg border border-aero-200 overflow-hidden shadow-sm">
                {activities.length === 0 ? (
                    <div className="p-8 text-center text-aero-400 italic">No recent activity</div>
                ) : (
                    <>
                        {activities.map((activity, index) => (
                            <ActivityItem
                                key={activity.id}
                                index={index}
                                text={formatActivityMessage(activity)}
                                time={formatRelativeTime(activity.timestamp)}
                                details={formatActivityDetails(activity)}
                            />
                        ))}
                        {hasMore && (
                            <div className="p-3 border-t border-aero-100 bg-aero-50 text-center">
                                <button
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                    className="text-xs font-bold text-aero-600 hover:text-aero-800 transition-colors disabled:opacity-50"
                                >
                                    {isLoadingMore ? 'Loading...' : 'Load More'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const DashboardOverview = ({ activeOrgId, activityRefreshKey }: { activeOrgId?: string; activityRefreshKey?: number }) => {
    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Top Section: Pathways & OJT */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <RecentlyEditedPathways activeOrgId={activeOrgId} />
                <OJTAssignmentsPanel activeOrgId={activeOrgId} />
            </div>
            <ActiveCandidatesPanel activeOrgId={activeOrgId} />
            <ActivityFeed activeOrgId={activeOrgId} refreshKey={activityRefreshKey} />
        </div>
    );
};

// --- Org Manager Components ---

const OrgTreeNode: React.FC<{ 
    node: TreeOrganization, 
    depth: number, 
    activeContextIds: Set<string>, 
    activeOrgId?: string,
    onOpenModal: (org?: Organization, parentId?: string) => void,
    onDelete: (id: string) => void,
    onSwitch: (id: string, sub: boolean) => void
}> = ({ node, depth, activeContextIds, activeOrgId, onOpenModal, onDelete, onSwitch }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const hasChildren = node.children.length > 0;
    
    // Determine if this node is in the active context (Selected or Child of Selected)
    const isInActiveContext = activeContextIds.has(node.id);
    
    // Check if this specific node is the primary selected one
    const isPrimarySelected = activeOrgId === node.id;

    return (
        <li className="select-none">
            <div 
              className={`group flex items-center justify-between p-3 rounded-xl mb-1 transition-all border ${
                  isInActiveContext 
                  ? 'bg-aero-50 border-aero-500 ring-1 ring-aero-500 shadow-md' 
                  : 'bg-white border-aero-100 hover:border-aero-300'
              }`}
              style={{ marginLeft: `${depth * 24}px` }}
            >
               <div className="flex items-center gap-3 flex-1 overflow-hidden">
                   {hasChildren ? (
                      <button 
                          onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }} 
                          className="p-1 rounded-md hover:bg-aero-100 text-aero-500"
                      >
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                   ) : (
                       <span className="w-6" /> // Spacer
                   )}
                   
                   <div className="flex flex-col min-w-0">
                       <div className="flex items-center gap-2">
                          <span className="font-bold text-aero-900 truncate">{node.name}</span>
                          {isInActiveContext && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider shadow-sm ${isPrimarySelected ? 'bg-aero-600 text-white' : 'bg-aero-200 text-aero-700'}`}>
                                  {isPrimarySelected ? 'Selected' : 'Included'}
                              </span>
                          )}
                       </div>
                       {node.description && <span className="text-xs text-aero-500 truncate">{node.description}</span>}
                   </div>
               </div>

               <div className="flex items-center gap-2">
                  <div className={`gap-1 ${isInActiveContext ? 'flex' : 'hidden group-hover:flex'}`}>
                      <button onClick={() => onOpenModal(undefined, node.id)} className="p-1.5 text-aero-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Add Sub Team">
                          <Plus className="w-4 h-4" />
                      </button>
                      <button onClick={() => onOpenModal(node)} className="p-1.5 text-aero-400 hover:text-aero-700 hover:bg-aero-100 rounded-lg transition-colors" title="Edit">
                          <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => onDelete(node.id)} className="p-1.5 text-aero-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 className="w-4 h-4" />
                      </button>
                  </div>
                  
                  {!isPrimarySelected && (
                      <div className="flex gap-1 pl-2 border-l border-aero-100 ml-1">
                           <Button 
                              variant="secondary" 
                              onClick={() => onSwitch(node.id, false)} 
                              className="h-7 text-[10px] px-2 bg-white hover:bg-aero-50 border-aero-200 text-aero-600 shadow-sm"
                              title="Select this team only"
                           >
                              <MousePointer2 className="w-3 h-3 mr-1" /> Team
                           </Button>
                           <Button 
                              variant="secondary" 
                              onClick={() => onSwitch(node.id, true)} 
                              className="h-7 text-[10px] px-2 bg-aero-50 hover:bg-aero-100 border-aero-200 text-aero-700 font-medium shadow-sm"
                              title="Select this team and all sub teams"
                           >
                              <GitMerge className="w-3 h-3 mr-1" /> All Subs
                           </Button>
                      </div>
                  )}
               </div>
            </div>

            {hasChildren && isExpanded && (
                <ul className="relative">
                     <div className="absolute left-[11px] top-0 bottom-4 w-px bg-aero-200" style={{ left: `${(depth * 24) + 26}px` }}></div>
                     {node.children.map(child => (
                         <OrgTreeNode 
                            key={child.id} 
                            node={child} 
                            depth={depth + 1}
                            activeContextIds={activeContextIds}
                            activeOrgId={activeOrgId}
                            onOpenModal={onOpenModal}
                            onDelete={onDelete}
                            onSwitch={onSwitch}
                         />
                     ))}
                </ul>
            )}
        </li>
    );
};

// --- OJT Agent Monitor Component ---
type RunCategory = 'active' | 'stale' | 'abuse' | 'unhandled' | 'completed' | 'no_show' | 'scheduled';

const OjtAgentMonitor = ({ activeOrgId, sessions }: { activeOrgId?: string; sessions: OjtSession[] }) => {
    const [agentRuns, setAgentRuns] = useState<OjtAgentRun[]>([]);
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState<RunCategory | 'all'>('all');
    const [showTechnicalLogs, setShowTechnicalLogs] = useState(false);
    const [activeRunMessages, setActiveRunMessages] = useState<OjtMessage[]>([]);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);

    // Subscribe to ojt_agent_runs in real-time
    useEffect(() => {
        if (!activeOrgId) {
            setAgentRuns([]);
            return;
        }
        const runsRef = collection(db, 'soloready', 'data', 'ojt_agent_runs');
        const unsub = onSnapshot(runsRef, (snapshot) => {
            const data: OjtAgentRun[] = [];
            snapshot.forEach((doc) => {
                data.push({ sessionId: doc.id, ...doc.data() } as OjtAgentRun);
            });
            setAgentRuns(data);
        }, (err) => {
            console.error("Failed to load agent runs:", err);
        });
        return () => unsub();
    }, [activeOrgId]);

    // Subscribe to ojt_messages for the selected sessionId
    useEffect(() => {
        if (!selectedRunId) {
            setActiveRunMessages([]);
            return;
        }
        setIsLoadingMessages(true);
        const messagesRef = collection(db, 'soloready', 'data', 'ojt_messages');
        const q = query(
            messagesRef,
            where('sessionId', '==', selectedRunId)
        );
        const unsub = onSnapshot(q, (snapshot) => {
            const msgs: OjtMessage[] = [];
            snapshot.forEach((doc) => {
                msgs.push({ id: doc.id, ...doc.data() } as OjtMessage);
            });
            // Client-side sort by createdAt
            msgs.sort((a, b) => a.createdAt - b.createdAt);
            setActiveRunMessages(msgs);
            setIsLoadingMessages(false);
        }, (error) => {
            console.error("Error subscribing to ojt_messages:", error);
            setIsLoadingMessages(false);
        });
        return () => unsub();
    }, [selectedRunId]);

    // Helper classification function
    const classifyRun = (run: OjtAgentRun, session?: OjtSession): RunCategory => {
        const isCompleted = run.phase === 'COMPLETE' || 
                            session?.status === 'COMPLETED' || 
                            session?.status === 'REVIEW_PENDING';
        if (isCompleted) return 'completed';
        if (run.phase === 'NO_SHOW') return 'no_show';
        if (run.phase === 'SCHEDULED_WAITING') return 'scheduled';

        if (run.phase === 'FAILED') {
            const textToSearch = (
                (run.failureMode || '') + ' ' + 
                ((session as any)?.agentSummary || '')
            ).toLowerCase();
            
            const abuseKeywords = ['abuse', 'abusive', 'argue', 'arguing', 'rude', 'swearing', 'insult', 'fuck', 'shit', 'hostile', 'threat', 'offensive', 'harass'];
            const unhandledKeywords = ['question', 'answer', 'escalat', 'handle', 'human', 'unable', 'unhandled', 'support', 'help', 'don\'t know', 'dont know'];

            if (abuseKeywords.some(kw => textToSearch.includes(kw))) {
                return 'abuse';
            }
            if (unhandledKeywords.some(kw => textToSearch.includes(kw))) {
                return 'unhandled';
            }
            return 'unhandled'; // default fallback for FAILED is unhandled question
        }

        // Check if stale (expectedReplyBy has passed, or 15 mins since last tick)
        const now = Date.now();
        const isStale = (run.expectedReplyBy && now > run.expectedReplyBy) || 
                        (now - run.lastTickAt > 15 * 60 * 1000);
        if (isStale) return 'stale';

        return 'active';
    };

    // Map and filter runs
    const mappedRuns = useMemo(() => {
        const orgSessionIds = new Set(sessions.map(s => s.id));
        return agentRuns
            .map(run => {
                const session = sessions.find(s => s.id === run.sessionId);
                return {
                    run,
                    session,
                    category: classifyRun(run, session)
                };
            })
            .filter(item => item.session && orgSessionIds.has(item.run.sessionId));
    }, [agentRuns, sessions]);

    // Calculate counts
    const stats = useMemo(() => {
        const counts = {
            all: 0,
            active: 0,
            stale: 0,
            abuse: 0,
            unhandled: 0,
            completed: 0,
            no_show: 0,
            scheduled: 0
        };
        mappedRuns.forEach(item => {
            counts.all++;
            counts[item.category]++;
        });
        return counts;
    }, [mappedRuns]);

    // Filtered list to render
    const filteredRuns = useMemo(() => {
        return mappedRuns.filter(item => {
            const matchesFilter = activeFilter === 'all' || item.category === activeFilter;
            
            const q = searchQuery.toLowerCase();
            const matchesSearch = !searchQuery || 
                (item.session?.candidateName || '').toLowerCase().includes(q) ||
                (item.session?.leadHandName || '').toLowerCase().includes(q) ||
                (item.session?.title || '').toLowerCase().includes(q);

            return matchesFilter && matchesSearch;
        });
    }, [mappedRuns, activeFilter, searchQuery]);

    const selectedItem = useMemo(() => {
        return mappedRuns.find(item => item.run.sessionId === selectedRunId);
    }, [mappedRuns, selectedRunId]);

    const getFriendlyPhaseName = (phase: string) => {
        switch (phase) {
            case 'SCHEDULED_WAITING': return 'Scheduled Waiting';
            case 'READINESS_CHECK': return 'Readiness Check';
            case 'SELFIE_VERIFICATION': return 'Selfie Verification';
            case 'OBJECTIVE_SETTING': return 'Objective Setting';
            case 'PRE_START_CHECKLIST': return 'Pre-Start Checklist';
            case 'SESSION_BODY': return 'Session Active';
            case 'WRAP_UP': return 'Wrap Up';
            case 'SIGN_OFF': return 'Sign-off';
            case 'COMPLETE': return 'Completed';
            case 'NO_SHOW': return 'No Show';
            case 'FAILED': return 'Failed';
            default: return phase;
        }
    };

    const getCategoryStyles = (category: RunCategory) => {
        switch (category) {
            case 'active': return { badge: 'bg-emerald-100 text-emerald-800 border-emerald-200', text: 'Active Comms' };
            case 'stale': return { badge: 'bg-amber-100 text-amber-800 border-amber-200', text: 'Stale' };
            case 'abuse': return { badge: 'bg-rose-100 text-rose-800 border-rose-200', text: 'Abuse / Conflict' };
            case 'unhandled': return { badge: 'bg-indigo-100 text-indigo-800 border-indigo-200', text: 'Unhandled Qs' };
            case 'completed': return { badge: 'bg-aero-100 text-aero-800 border-aero-200', text: 'Completed' };
            case 'no_show': return { badge: 'bg-gray-100 text-gray-800 border-gray-200', text: 'No Show' };
            case 'scheduled': return { badge: 'bg-blue-100 text-blue-800 border-blue-200', text: 'Scheduled' };
        }
    };

    return (
        <div className="space-y-6">
            {/* Category Stats Dashboard */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Active Card */}
                <button
                    onClick={() => setActiveFilter(activeFilter === 'active' ? 'all' : 'active')}
                    className={`flex flex-col p-4 rounded-xl border transition-all text-left group ${
                        activeFilter === 'active' 
                            ? 'bg-emerald-600 border-emerald-700 text-white shadow-md' 
                            : 'bg-white border-aero-200 text-aero-800 hover:border-emerald-300 hover:shadow-sm'
                    }`}
                >
                    <div className="flex justify-between items-start w-full mb-2">
                        <span className={`p-2 rounded-lg ${activeFilter === 'active' ? 'bg-emerald-700 text-white' : 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100'}`}>
                            <MessageSquare className="w-5 h-5" />
                        </span>
                        <span className="text-2xl font-black">{stats.active}</span>
                    </div>
                    <span className={`text-xs font-bold ${activeFilter === 'active' ? 'text-emerald-100' : 'text-aero-500'}`}>Active Comms</span>
                </button>
                
                {/* Stale Card */}
                <button
                    onClick={() => setActiveFilter(activeFilter === 'stale' ? 'all' : 'stale')}
                    className={`flex flex-col p-4 rounded-xl border transition-all text-left group ${
                        activeFilter === 'stale' 
                            ? 'bg-amber-500 border-amber-600 text-white shadow-md' 
                            : 'bg-white border-aero-200 text-aero-800 hover:border-amber-300 hover:shadow-sm'
                    }`}
                >
                    <div className="flex justify-between items-start w-full mb-2">
                        <span className={`p-2 rounded-lg ${activeFilter === 'stale' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-600 group-hover:bg-amber-100'}`}>
                            <Hourglass className="w-5 h-5" />
                        </span>
                        <span className="text-2xl font-black">{stats.stale}</span>
                    </div>
                    <span className={`text-xs font-bold ${activeFilter === 'stale' ? 'text-amber-100' : 'text-aero-500'}`}>Stale Runs</span>
                </button>

                {/* Abuse Card */}
                <button
                    onClick={() => setActiveFilter(activeFilter === 'abuse' ? 'all' : 'abuse')}
                    className={`flex flex-col p-4 rounded-xl border transition-all text-left group ${
                        activeFilter === 'abuse' 
                            ? 'bg-rose-500 border-rose-600 text-white shadow-md' 
                            : 'bg-white border-aero-200 text-aero-800 hover:border-rose-300 hover:shadow-sm'
                    }`}
                >
                    <div className="flex justify-between items-start w-full mb-2">
                        <span className={`p-2 rounded-lg ${activeFilter === 'abuse' ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-600 group-hover:bg-rose-100'}`}>
                            <ShieldAlert className="w-5 h-5" />
                        </span>
                        <span className="text-2xl font-black">{stats.abuse}</span>
                    </div>
                    <span className={`text-xs font-bold ${activeFilter === 'abuse' ? 'text-rose-100' : 'text-aero-500'}`}>Abuse / Conflict</span>
                </button>

                {/* Unhandled Qs Card */}
                <button
                    onClick={() => setActiveFilter(activeFilter === 'unhandled' ? 'all' : 'unhandled')}
                    className={`flex flex-col p-4 rounded-xl border transition-all text-left group ${
                        activeFilter === 'unhandled' 
                            ? 'bg-indigo-500 border-indigo-600 text-white shadow-md' 
                            : 'bg-white border-aero-200 text-aero-800 hover:border-indigo-300 hover:shadow-sm'
                    }`}
                >
                    <div className="flex justify-between items-start w-full mb-2">
                        <span className={`p-2 rounded-lg ${activeFilter === 'unhandled' ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100'}`}>
                            <HelpCircle className="w-5 h-5" />
                        </span>
                        <span className="text-2xl font-black">{stats.unhandled}</span>
                    </div>
                    <span className={`text-xs font-bold ${activeFilter === 'unhandled' ? 'text-indigo-100' : 'text-aero-500'}`}>Unhandled Qs</span>
                </button>

                {/* Completed Card */}
                <button
                    onClick={() => setActiveFilter(activeFilter === 'completed' ? 'all' : 'completed')}
                    className={`flex flex-col p-4 rounded-xl border transition-all text-left group ${
                        activeFilter === 'completed' 
                            ? 'bg-aero-800 border-aero-900 text-white shadow-md' 
                            : 'bg-white border-aero-200 text-aero-800 hover:border-aero-400 hover:shadow-sm'
                    }`}
                >
                    <div className="flex justify-between items-start w-full mb-2">
                        <span className={`p-2 rounded-lg ${activeFilter === 'completed' ? 'bg-aero-700 text-white' : 'bg-aero-50 text-aero-600 group-hover:bg-aero-100'}`}>
                            <CheckCircle2 className="w-5 h-5" />
                        </span>
                        <span className="text-2xl font-black">{stats.completed}</span>
                    </div>
                    <span className={`text-xs font-bold ${activeFilter === 'completed' ? 'text-aero-100' : 'text-aero-500'}`}>Completed</span>
                </button>
            </div>

            {/* Search and Secondary filters */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl border border-aero-200 shadow-sm">
                <div className="relative w-full md:w-72">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-aero-400" />
                    <input
                        type="text"
                        placeholder="Search candidate, lead hand..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-aero-200 rounded-lg text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent outline-none"
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="absolute right-3 top-2.5 text-xs text-aero-400 hover:text-aero-600">Clear</button>
                    )}
                </div>

                <div className="flex items-center gap-2 self-start md:self-auto overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
                    <span className="text-xs text-aero-400 font-bold uppercase tracking-wider whitespace-nowrap">Other Phases:</span>
                    <button
                        onClick={() => setActiveFilter(activeFilter === 'scheduled' ? 'all' : 'scheduled')}
                        className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors whitespace-nowrap ${
                            activeFilter === 'scheduled'
                                ? 'bg-blue-100 border-blue-300 text-blue-800'
                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        Waiting ({stats.scheduled})
                    </button>
                    <button
                        onClick={() => setActiveFilter(activeFilter === 'no_show' ? 'all' : 'no_show')}
                        className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors whitespace-nowrap ${
                            activeFilter === 'no_show'
                                ? 'bg-gray-200 border-gray-400 text-gray-800'
                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        No Show ({stats.no_show})
                    </button>
                    {activeFilter !== 'all' && (
                        <button
                            onClick={() => setActiveFilter('all')}
                            className="text-xs text-aero-500 hover:text-aero-800 font-bold underline whitespace-nowrap ml-2"
                        >
                            Reset Filters
                        </button>
                    )}
                </div>
            </div>

            {/* List Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredRuns.length > 0 ? (
                    filteredRuns.map(({ run, session, category }) => {
                        const styles = getCategoryStyles(category);
                        const initials = (session?.candidateName || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                        
                        return (
                            <div
                                key={run.sessionId}
                                className="bg-white border border-aero-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-aero-400 transition-all flex flex-col justify-between"
                            >
                                <div>
                                    <div className="flex justify-between items-start gap-2 mb-3">
                                        <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border ${styles.badge}`}>
                                            {styles.text}
                                        </span>
                                        <div className="text-[10px] font-bold text-aero-400 font-mono">
                                            Phase: <span className="text-aero-700">{getFriendlyPhaseName(run.phase)}</span>
                                        </div>
                                    </div>

                                    <h4 className="font-extrabold text-aero-900 leading-tight mb-3 text-base">{session?.title}</h4>
                                    
                                    <div className="space-y-2.5 py-3 border-y border-aero-50">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-aero-100 flex items-center justify-center text-[10px] font-bold text-aero-700">{initials}</div>
                                            <div className="text-xs">
                                                <div className="text-aero-400 font-bold text-[9px] uppercase tracking-wider">Candidate</div>
                                                <div className="font-bold text-aero-800">{session?.candidateName}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-[10px] font-bold text-purple-700">LH</div>
                                            <div className="text-xs">
                                                <div className="text-purple-400 font-bold text-[9px] uppercase tracking-wider">Lead Hand</div>
                                                <div className="font-bold text-aero-800">{session?.leadHandName}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 pt-3 flex flex-col gap-2">
                                    <div className="flex justify-between text-[10px] text-aero-400 font-bold">
                                        <span>Retries: {run.retryCount}</span>
                                        <span>Last Tick: {new Date(run.lastTickAt).toLocaleTimeString()}</span>
                                    </div>
                                    {run.expectedReplyBy && run.phase !== 'COMPLETE' && run.phase !== 'FAILED' && run.phase !== 'NO_SHOW' && (
                                        <div className={`text-[10px] font-bold ${Date.now() > run.expectedReplyBy ? 'text-red-500' : 'text-aero-500'}`}>
                                            Expected Reply: {new Date(run.expectedReplyBy).toLocaleTimeString()} {Date.now() > run.expectedReplyBy && '(Overdue)'}
                                        </div>
                                    )}
                                    {run.failureMode && (
                                        <div className="text-[10px] text-red-500 bg-red-50 p-2 rounded border border-red-100 line-clamp-2 mt-1">
                                            <span className="font-bold">Failure:</span> {run.failureMode}
                                        </div>
                                    )}
                                    <Button
                                        variant="secondary"
                                        className="w-full mt-2 text-xs font-bold py-2 border border-aero-200 text-aero-700 hover:bg-aero-50 flex items-center justify-center gap-1.5"
                                        onClick={() => setSelectedRunId(run.sessionId)}
                                    >
                                        <MessageSquare className="w-3.5 h-3.5" /> View Live Transcript
                                    </Button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="col-span-full py-16 text-center text-aero-400 border-2 border-dashed border-aero-200 rounded-xl bg-white shadow-sm">
                        <Bot className="w-10 h-10 mx-auto mb-2 opacity-50 text-aero-300" />
                        <h5 className="font-bold text-aero-700 text-sm">No agent sessions match filter</h5>
                        <p className="text-xs text-aero-400 mt-1">Try adjusting your filters or search query.</p>
                    </div>
                )}
            </div>

            {/* Transcript Modal */}
            <Modal
                isOpen={!!selectedRunId}
                onClose={() => setSelectedRunId(null)}
                title={`OJT Agent Live Transcript: ${selectedItem?.session?.title || 'Loading...'}`}
                maxWidth="max-w-3xl"
            >
                {selectedItem && (
                    <div className="flex flex-col h-[75vh] overflow-hidden -mx-4 -my-4">
                        {/* Header metadata summary */}
                        <div className="bg-aero-50/50 p-4 border-b border-aero-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                            <div className="space-y-1">
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-aero-500 font-bold">Candidate:</span>
                                    <span className="font-bold text-aero-800">{selectedItem.session?.candidateName}</span>
                                    <span className="text-aero-300">|</span>
                                    <span className="text-aero-500 font-bold">Lead Hand:</span>
                                    <span className="font-bold text-aero-800">{selectedItem.session?.leadHandName}</span>
                                </div>
                                <div className="text-[10px] text-aero-400 font-bold">
                                    Last active: {new Date(selectedItem.run.lastTickAt).toLocaleString()} • Retries: {selectedItem.run.retryCount}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border ${getCategoryStyles(selectedItem.category).badge}`}>
                                    {getCategoryStyles(selectedItem.category).text}
                                </span>
                                <span className="bg-white border border-aero-200 text-aero-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                    {getFriendlyPhaseName(selectedItem.run.phase)}
                                </span>
                            </div>
                        </div>

                        {/* Stale or failed alerts */}
                        {selectedItem.category === 'stale' && (
                            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-850 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                                <span><span className="font-bold">Stale Conversation:</span> Candidate/Lead Hand has not replied within the expected time. Expected reply was by {selectedItem.run.expectedReplyBy ? new Date(selectedItem.run.expectedReplyBy).toLocaleTimeString() : 'N/A'}.</span>
                            </div>
                        )}
                        {selectedItem.run.failureMode && (
                            <div className="bg-red-50 border-b border-red-200 px-4 py-2.5 text-xs text-red-800 flex items-center gap-2">
                                <ShieldAlert className="w-4.5 h-4.5 text-red-500 flex-shrink-0" />
                                <div>
                                    <span className="font-bold">Session Failed / Terminated:</span> {selectedItem.run.failureMode}
                                </div>
                            </div>
                        )}

                        {/* Toolbar / Log Filter */}
                        <div className="px-4 py-2 border-b border-aero-100 bg-white flex justify-between items-center">
                            <label className="flex items-center gap-2 text-xs text-aero-600 cursor-pointer font-semibold select-none">
                                <input
                                    type="checkbox"
                                    checked={showTechnicalLogs}
                                    onChange={(e) => setShowTechnicalLogs(e.target.checked)}
                                    className="rounded border-aero-300 text-aero-800 focus:ring-aero-400"
                                />
                                Show Technical Logs (system events, tool execution)
                            </label>
                            <span className="text-[10px] text-aero-400 font-bold">{activeRunMessages.length} total events</span>
                        </div>

                        {/* Transcript Area */}
                        <div className="flex-1 overflow-y-auto p-4 bg-aero-50/20 space-y-4">
                            {isLoadingMessages ? (
                                <div className="flex flex-col items-center justify-center h-full text-aero-400">
                                    <RefreshCw className="w-6 h-6 animate-spin mb-2" />
                                    <span className="text-xs font-bold">Loading transcript...</span>
                                </div>
                            ) : activeRunMessages.filter(msg => showTechnicalLogs || (msg.kind !== 'tool_call' && msg.kind !== 'tool_result' && msg.kind !== 'phase_change' && msg.role !== 'system')).length === 0 ? (
                                <div className="text-center py-12 text-aero-400 italic text-xs">
                                    No transcript events to display.
                                </div>
                            ) : (
                                activeRunMessages
                                    .filter(msg => showTechnicalLogs || (msg.kind !== 'tool_call' && msg.kind !== 'tool_result' && msg.kind !== 'phase_change' && msg.role !== 'system'))
                                    .map((msg) => {
                                        const isSystem = msg.role === 'system' || msg.kind === 'tool_call' || msg.kind === 'tool_result' || msg.kind === 'phase_change';
                                        
                                        if (isSystem) {
                                            return (
                                                <div key={msg.id} className="flex justify-center my-1.5">
                                                    <div className="bg-aero-50 border border-aero-100 rounded-lg p-2.5 max-w-[90%] text-[10px] font-mono text-aero-600 shadow-sm">
                                                        <div className="flex justify-between items-center gap-6 mb-1 text-[9px] text-aero-400 font-bold uppercase tracking-wider">
                                                            <span>⚙️ {msg.kind.toUpperCase()} ({msg.role})</span>
                                                            <span>{new Date(msg.createdAt).toLocaleTimeString()}</span>
                                                        </div>
                                                        <div className="whitespace-pre-wrap break-words">{msg.body}</div>
                                                        {msg.payload && (
                                                            <details className="mt-1.5 cursor-pointer">
                                                                <summary className="hover:underline text-[9px] font-bold text-aero-500">View details payload</summary>
                                                                <pre className="mt-1 bg-gray-900 text-green-400 p-2 rounded overflow-x-auto text-[9px] max-h-40">
                                                                    {JSON.stringify(msg.payload, null, 2)}
                                                                </pre>
                                                            </details>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        const isAgent = msg.role === 'agent';
                                        const isLeadHand = msg.role === 'lead_hand';
                                        const senderName = isAgent 
                                            ? 'Coach Bot' 
                                            : isLeadHand 
                                                ? (selectedItem.session?.leadHandName || 'Lead Hand') 
                                                : (selectedItem.session?.candidateName || 'Candidate');

                                        return (
                                            <div key={msg.id} className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
                                                <div className={`flex flex-col max-w-[80%] ${isAgent ? 'items-start' : 'items-end'}`}>
                                                    <span className="text-[10px] text-aero-400 font-bold mb-1 px-1">
                                                        {senderName} • {new Date(msg.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                    </span>
                                                    <div
                                                        className={`p-3.5 rounded-2xl shadow-sm border ${
                                                            isAgent 
                                                                ? 'bg-white border-aero-200 text-aero-900 rounded-tl-none' 
                                                                : isLeadHand
                                                                    ? 'bg-purple-50 border-purple-200 text-purple-900 rounded-tr-none'
                                                                    : 'bg-blue-50 border-blue-200 text-blue-900 rounded-tr-none'
                                                        }`}
                                                    >
                                                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                                                        {msg.payload && msg.kind === 'minipage_response' && (
                                                            <div className="mt-2.5 pt-2.5 border-t border-aero-100 text-xs">
                                                                <div className="font-bold text-[10px] text-aero-400 uppercase tracking-wide mb-1">Form Response:</div>
                                                                <pre className="bg-black/5 p-2 rounded font-mono text-[10px] overflow-x-auto whitespace-pre-wrap max-h-32 text-aero-800">
                                                                    {typeof msg.payload === 'object' && msg.payload !== null 
                                                                        ? Object.entries(msg.payload).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('\n') 
                                                                        : String(msg.payload)}
                                                                </pre>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                            )}
                        </div>

                        {/* Footer controls */}
                        <div className="bg-white p-4 border-t border-aero-100 flex justify-between items-center">
                            <span className="text-xs text-aero-400 font-medium">Session ID: <span className="font-mono">{selectedRunId}</span></span>
                            <Button variant="secondary" onClick={() => setSelectedRunId(null)}>Close Transcript</Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

// --- OJT Scheduler Component ---

const OjtScheduler = ({ activeOrgId }: { activeOrgId?: string }) => {
    const [activeTab, setActiveTab] = useState<'calendar' | 'agent'>('calendar');
    const [date, setDate] = useState(new Date());
    const [zoom, setZoom] = useState<'DAY' | 'WEEK' | 'MONTH' | 'YEAR'>('DAY');
    const [sessions, setSessions] = useState<OjtSession[]>([]);
    const [selectedSession, setSelectedSession] = useState<OjtSession | null>(null);
    const [filterReviewPending, setFilterReviewPending] = useState(false);
    const [reviewingObjectiveId, setReviewingObjectiveId] = useState<string | null>(null);
    const [objectiveFailureReason, setObjectiveFailureReason] = useState('');
    const [isSubmittingReview, setIsSubmittingReview] = useState(false);
    const [localObjectiveReviews, setLocalObjectiveReviews] = useState<Record<string, { status: 'SUCCESS' | 'FAILURE'; failureReason?: string }>>({});

    useEffect(() => {
        const loadSessions = async () => {
            if (!activeOrgId) {
                setSessions([]);
                return;
            }
            try {
                const data = await DataService.getOjtSessions(activeOrgId);
                setSessions(data);
            } catch (e) {
                console.error('Failed to load OJT sessions', e);
            }
        };
        loadSessions();
    }, [activeOrgId]);

    // Initialize local reviews when a session is selected
    useEffect(() => {
        if (selectedSession?.objectiveReviews) {
            const existing: Record<string, { status: 'SUCCESS' | 'FAILURE'; failureReason?: string }> = {};
            selectedSession.objectiveReviews.forEach(r => {
                existing[r.objectiveId] = { status: r.status, failureReason: r.failureReason };
            });
            setLocalObjectiveReviews(existing);
        } else {
            setLocalObjectiveReviews({});
        }
        setReviewingObjectiveId(null);
        setObjectiveFailureReason('');
    }, [selectedSession]);

    const handleObjectiveReview = (objectiveId: string, status: 'SUCCESS' | 'FAILURE') => {
        if (status === 'FAILURE') {
            setReviewingObjectiveId(objectiveId);
            setObjectiveFailureReason('');
        } else {
            setLocalObjectiveReviews(prev => ({ ...prev, [objectiveId]: { status: 'SUCCESS' } }));
            setReviewingObjectiveId(null);
        }
    };

    const confirmObjectiveFailure = (objectiveId: string) => {
        setLocalObjectiveReviews(prev => ({
            ...prev,
            [objectiveId]: { status: 'FAILURE', failureReason: objectiveFailureReason.trim() || undefined }
        }));
        setReviewingObjectiveId(null);
        setObjectiveFailureReason('');
    };

    const handleSubmitSessionReview = async (overallStatus: 'APPROVED' | 'REJECTED') => {
        if (!selectedSession) return;
        setIsSubmittingReview(true);
        try {
            const currentUser = auth.currentUser;
            const objectiveReviewsList = Object.entries(localObjectiveReviews).map(([objectiveId, review]) => ({
                objectiveId,
                status: review.status,
                failureReason: review.failureReason,
                reviewedAt: new Date().toISOString(),
            }));
            const updatedSession: OjtSession = {
                ...selectedSession,
                reviewStatus: overallStatus === 'APPROVED' ? 'APPROVED' : 'REJECTED',
                reviewedBy: currentUser?.uid || 'unknown',
                reviewedByName: currentUser?.displayName || currentUser?.email || 'Admin',
                reviewedAt: new Date().toISOString(),
                objectiveReviews: objectiveReviewsList,
                status: overallStatus === 'APPROVED' ? 'COMPLETED' : 'REVIEW_PENDING',
            };
            await DataService.updateOjtSession(updatedSession);
            // Update local state
            setSessions(prev => prev.map(s => s.id === updatedSession.id ? updatedSession : s));
            setSelectedSession(updatedSession);
        } catch (e) {
            console.error('Failed to submit review:', e);
        } finally {
            setIsSubmittingReview(false);
        }
    };

    const reviewPendingCount = sessions.filter(s => s.status === 'REVIEW_PENDING').length;
    const displaySessions = filterReviewPending ? sessions.filter(s => s.status === 'REVIEW_PENDING') : sessions;

    const changeDate = (days: number) => {
        const newDate = new Date(date);
        newDate.setDate(date.getDate() + days);
        setDate(newDate);
    };

    const StatusBadge = ({ status }: { status: OjtSession['status'] }) => {
        const styles = {
            'SCHEDULED': 'bg-gray-100 text-gray-600',
            'IN_PROGRESS': 'bg-blue-100 text-blue-700',
            'REVIEW_PENDING': 'bg-orange-100 text-orange-700',
            'COMPLETED': 'bg-green-100 text-green-700'
        };
        const labels = {
            'SCHEDULED': 'Scheduled',
            'IN_PROGRESS': 'In Progress',
            'REVIEW_PENDING': 'Pending Review',
            'COMPLETED': 'Signed Off'
        };
        return (
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${styles[status]}`}>
                {labels[status]}
            </span>
        );
    };

    const DayView = () => {
        // Filter sessions for today
        const todaysSessions = displaySessions.filter(s => new Date(s.date).toDateString() === date.toDateString());
        
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {todaysSessions.length > 0 ? todaysSessions.map(session => (
                    <div 
                        key={session.id} 
                        onClick={() => setSelectedSession(session)}
                        className="bg-white border border-aero-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-aero-400 transition-all cursor-pointer group"
                    >
                        <div className="flex justify-between items-start mb-3">
                            <StatusBadge status={session.status} />
                            <div className="text-xs font-mono text-aero-400 font-bold">
                                {new Date(session.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                        </div>
                        <h4 className="font-bold text-aero-900 mb-2 leading-tight group-hover:text-aero-700">{session.title}</h4>
                        <div className="space-y-2 pt-2 border-t border-aero-50">
                            <div className="flex items-center gap-2">
                                <UserCircle className="w-4 h-4 text-aero-400" />
                                <span className="text-xs text-aero-600"><span className="font-semibold">Candidate:</span> {session.candidateName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-aero-400" />
                                <span className="text-xs text-aero-600"><span className="font-semibold">Lead Hand:</span> {session.leadHandName}</span>
                            </div>
                        </div>
                    </div>
                )) : (
                    <div className="col-span-full py-12 text-center text-aero-400 border-2 border-dashed border-aero-200 rounded-xl">
                        <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        No sessions scheduled for {date.toLocaleDateString()}.
                    </div>
                )}
            </div>
        );
    };

    const WeekView = () => {
        const weekDays = Array.from({length: 7}, (_, i) => {
            const d = new Date(date);
            d.setDate(date.getDate() - date.getDay() + i);
            return d;
        });

        return (
            <div className="grid grid-cols-7 gap-2 h-[600px] bg-white border border-aero-200 rounded-xl overflow-hidden shadow-sm">
                {weekDays.map((day, i) => {
                    const daySessions = displaySessions.filter(s => new Date(s.date).toDateString() === day.toDateString());
                    const isToday = day.toDateString() === new Date().toDateString();
                    return (
                        <div key={i} className={`flex flex-col border-r border-aero-100 last:border-0 ${isToday ? 'bg-aero-50/50' : ''}`}>
                            <div className={`p-2 text-center text-xs font-bold border-b border-aero-100 ${isToday ? 'text-aero-700 bg-aero-100' : 'text-aero-500'}`}>
                                {day.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric'})}
                            </div>
                            <div className="flex-1 p-1 space-y-1 overflow-y-auto">
                                {daySessions.map(s => (
                                    <div key={s.id} onClick={() => setSelectedSession(s)} className="p-1.5 rounded bg-white border border-aero-200 shadow-sm text-[10px] cursor-pointer hover:border-aero-400">
                                        <div className={`w-1.5 h-1.5 rounded-full mb-1 ${
                                            s.status === 'COMPLETED' ? 'bg-green-500' : 
                                            s.status === 'REVIEW_PENDING' ? 'bg-orange-400' : 
                                            s.status === 'IN_PROGRESS' ? 'bg-blue-500' : 'bg-gray-300'
                                        }`}></div>
                                        <div className="font-bold truncate text-aero-800">{s.candidateName}</div>
                                        <div className="truncate text-aero-500">{s.title}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };
    
    const MonthView = () => {
        // Simplified grid
        const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const startDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
        
        return (
             <div className="bg-white border border-aero-200 rounded-xl p-4 shadow-sm">
                 <div className="grid grid-cols-7 gap-px bg-aero-100 border border-aero-100 rounded overflow-hidden">
                     {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                         <div key={d} className="bg-aero-50 p-2 text-center text-xs font-bold text-aero-500">{d}</div>
                     ))}
                     {Array.from({length: startDay}).map((_, i) => <div key={`empty-${i}`} className="bg-white h-24"></div>)}
                     {Array.from({length: daysInMonth}).map((_, i) => {
                         const currentDay = i + 1;
                         const currentDate = new Date(date.getFullYear(), date.getMonth(), currentDay);
                         const daySessions = displaySessions.filter(s => new Date(s.date).toDateString() === currentDate.toDateString());
                         return (
                             <div key={i} className="bg-white h-24 p-1 hover:bg-aero-50 transition-colors border-t border-l border-aero-50 relative group">
                                 <span className={`text-xs font-bold p-1 rounded-full ${daySessions.length > 0 ? 'text-aero-700' : 'text-aero-300'}`}>{currentDay}</span>
                                 <div className="mt-1 space-y-0.5 overflow-hidden max-h-[60px]">
                                     {daySessions.slice(0, 3).map(s => (
                                         <div key={s.id} onClick={() => setSelectedSession(s)} className="px-1 py-0.5 text-[9px] bg-aero-100 text-aero-700 rounded truncate cursor-pointer hover:bg-aero-200">
                                             {s.candidateName}
                                         </div>
                                     ))}
                                     {daySessions.length > 3 && <div className="text-[9px] text-aero-400 pl-1">+{daySessions.length - 3} more</div>}
                                 </div>
                             </div>
                         )
                     })}
                 </div>
             </div>
        );
    };

    const YearView = () => (
        <div className="grid grid-cols-4 gap-4">
             {Array.from({length: 12}).map((_, monthIndex) => {
                 const monthDate = new Date(date.getFullYear(), monthIndex, 1);
                 // Heatmap simplified logic: random density for visual
                 const density = Math.floor(Math.random() * 4); 
                 return (
                     <div key={monthIndex} className="bg-white border border-aero-200 rounded-lg p-3">
                         <h5 className="font-bold text-sm text-aero-800 mb-2">{monthDate.toLocaleDateString('en-US', {month: 'long'})}</h5>
                         <div className="grid grid-cols-7 gap-0.5">
                             {Array.from({length: 30}).map((__, d) => (
                                 <div key={d} className={`w-full pt-[100%] rounded-sm ${
                                     Math.random() > 0.7 ? ['bg-aero-100', 'bg-aero-200', 'bg-aero-400', 'bg-green-400'][Math.floor(Math.random()*4)] : 'bg-gray-50'
                                 }`}></div>
                             ))}
                         </div>
                     </div>
                 );
             })}
        </div>
    );

    return (
        <div className="h-full flex flex-col space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Tab Selection */}
            <div className="flex border-b border-aero-200">
                <button
                    onClick={() => setActiveTab('calendar')}
                    className={`px-6 py-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
                        activeTab === 'calendar'
                            ? 'border-aero-800 text-aero-900 font-extrabold'
                            : 'border-transparent text-aero-500 hover:text-aero-700 font-medium'
                    }`}
                >
                    📅 Session Calendar
                </button>
                <button
                    onClick={() => setActiveTab('agent')}
                    className={`px-6 py-3 font-bold text-sm border-b-2 transition-all flex items-center gap-2 ${
                        activeTab === 'agent'
                            ? 'border-aero-800 text-aero-900 font-extrabold'
                            : 'border-transparent text-aero-500 hover:text-aero-700 font-medium'
                    }`}
                >
                    🤖 Agent Monitor
                    <span className="bg-aero-100 text-aero-850 px-2 py-0.5 rounded-full text-[10px] font-bold">Beta</span>
                </button>
            </div>

            {activeTab === 'calendar' ? (
                <>
                    {/* Toolbar */}
                    <div className="flex flex-col md:flex-row justify-between items-center bg-white p-4 rounded-xl border border-aero-200 shadow-sm gap-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center bg-aero-50 rounded-lg p-1 border border-aero-100">
                        <button onClick={() => changeDate(-1)} className="p-1 hover:bg-white rounded-md transition-all text-aero-500 hover:text-aero-800 shadow-sm hover:shadow"><ChevronLeft className="w-5 h-5" /></button>
                        <button onClick={() => setDate(new Date())} className="px-4 py-1 text-sm font-bold text-aero-800 hover:text-aero-600">
                            {zoom === 'DAY' ? date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'}) : 
                             zoom === 'MONTH' ? date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) :
                             zoom === 'YEAR' ? date.getFullYear() :
                             'Week of ' + date.toLocaleDateString()}
                        </button>
                        <button onClick={() => changeDate(1)} className="p-1 hover:bg-white rounded-md transition-all text-aero-500 hover:text-aero-800 shadow-sm hover:shadow"><ChevronLeft className="w-5 h-5 rotate-180" /></button>
                    </div>
                </div>
                
                <div className="flex items-center gap-3">
                    <div className="flex p-1 bg-aero-100 rounded-lg">
                        {(['DAY', 'WEEK', 'MONTH', 'YEAR'] as const).map((z) => (
                            <button
                                key={z}
                                onClick={() => setZoom(z)}
                                className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${zoom === z ? 'bg-white text-aero-800 shadow-sm' : 'text-aero-500 hover:text-aero-700'}`}
                            >
                                {z}
                            </button>
                        ))}
                    </div>
                    {reviewPendingCount > 0 && (
                        <button
                            onClick={() => setFilterReviewPending(!filterReviewPending)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                                filterReviewPending 
                                    ? 'bg-orange-100 text-orange-700 border border-orange-300' 
                                    : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100'
                            }`}
                        >
                            <AlertCircle className="w-3.5 h-3.5" />
                            {reviewPendingCount} Review Pending
                        </button>
                    )}
                </div>
            </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto">
                        {zoom === 'DAY' && <DayView />}
                        {zoom === 'WEEK' && <WeekView />}
                        {zoom === 'MONTH' && <MonthView />}
                        {zoom === 'YEAR' && <YearView />}
                    </div>
                </>
            ) : (
                <OjtAgentMonitor activeOrgId={activeOrgId} sessions={sessions} />
            )}

            {/* Audit Modal (Compliance View) */}
            <Modal isOpen={!!selectedSession} onClose={() => setSelectedSession(null)} title="OJT Session Compliance Audit" maxWidth="max-w-3xl">
                {selectedSession && (
                    <div className="space-y-6 max-h-[75vh] overflow-y-auto pr-1">
                        {/* 1. Header Details */}
                        <div className="bg-aero-50 p-4 rounded-xl border border-aero-100 flex flex-col gap-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="font-bold text-aero-900 text-lg leading-tight">{selectedSession.title}</h3>
                                    <p className="text-sm text-aero-500">{new Date(selectedSession.date).toLocaleDateString()} • {new Date(selectedSession.date).toLocaleTimeString()}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {selectedSession.reviewStatus && (
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                            selectedSession.reviewStatus === 'APPROVED' ? 'bg-green-100 text-green-700' :
                                            selectedSession.reviewStatus === 'REJECTED' ? 'bg-red-100 text-red-700' :
                                            'bg-yellow-100 text-yellow-700'
                                        }`}>
                                            {selectedSession.reviewStatus}
                                        </span>
                                    )}
                                    <StatusBadge status={selectedSession.status} />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 pt-2">
                                <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-aero-100">
                                    <div className="w-8 h-8 bg-aero-100 rounded-full flex items-center justify-center text-aero-600"><UserCircle className="w-5 h-5" /></div>
                                    <div>
                                        <div className="text-[10px] text-aero-400 uppercase font-bold">Candidate</div>
                                        <div className="text-sm font-bold text-aero-900">{selectedSession.candidateName}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-aero-100">
                                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600"><Shield className="w-5 h-5" /></div>
                                    <div>
                                        <div className="text-[10px] text-purple-400 uppercase font-bold">Lead Hand</div>
                                        <div className="text-sm font-bold text-aero-900">{selectedSession.leadHandName}</div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Time Log */}
                            <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-aero-200">
                                <div className="flex items-center gap-2 text-aero-700 font-medium">
                                    <Clock className="w-4 h-4" />
                                    <span>Time Logged</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-lg font-bold ${selectedSession.loggedTimeMinutes >= selectedSession.requiredTimeMinutes ? 'text-green-600' : 'text-orange-500'}`}>
                                        {selectedSession.loggedTimeMinutes}m
                                    </span>
                                    <span className="text-aero-400 text-sm">/ {selectedSession.requiredTimeMinutes}m required</span>
                                </div>
                            </div>

                            {/* Handshake + Pre-Start Status Row */}
                            <div className="flex gap-3">
                                {selectedSession.handshakeType && (
                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${selectedSession.handshakeVerified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        <Fingerprint className="w-3.5 h-3.5" />
                                        {selectedSession.handshakeType.toUpperCase()} {selectedSession.handshakeVerified ? '✓ Verified' : '⏳ Pending'}
                                    </div>
                                )}
                                {selectedSession.preStartChecklistCompleted !== undefined && (
                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${selectedSession.preStartChecklistCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        <ListChecks className="w-3.5 h-3.5" />
                                        Pre-Start {selectedSession.preStartChecklistCompleted ? '✓ Complete' : '⏳ Incomplete'}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 2. Start Proof (Selfie) */}
                        <div className="space-y-2">
                             <h4 className="text-xs font-bold text-aero-500 uppercase tracking-widest flex items-center gap-2">
                                 <Camera className="w-4 h-4" /> Start Verification
                             </h4>
                             <div className="h-40 bg-gray-100 rounded-xl border border-dashed border-gray-300 flex items-center justify-center relative overflow-hidden group">
                                 {selectedSession.startSelfieUrl ? (
                                     <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
                                         <div className="flex flex-col items-center gap-2 text-gray-400">
                                              <Users className="w-12 h-12" />
                                              <span className="text-xs font-medium">[Start Selfie Placeholder]</span>
                                         </div>
                                     </div>
                                 ) : (
                                     <span className="text-sm text-gray-400 italic">No start verification captured</span>
                                 )}
                             </div>
                        </div>

                        {/* 3. Legacy Checklist Audit (old elements) */}
                        {selectedSession.elements && selectedSession.elements.length > 0 && (
                        <div className="space-y-3">
                             <h4 className="text-xs font-bold text-aero-500 uppercase tracking-widest flex items-center gap-2">
                                 <ClipboardList className="w-4 h-4" /> Checklist Elements
                             </h4>
                             <div className="bg-white border border-aero-200 rounded-xl overflow-hidden divide-y divide-aero-50">
                                 {selectedSession.elements.map(el => (
                                     <div key={el.id} className="p-3 flex items-center justify-between hover:bg-aero-50">
                                         <div className="flex items-center gap-3">
                                             <div className={`w-5 h-5 rounded flex items-center justify-center ${el.isCompleted ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                                 {el.isCompleted ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
                                             </div>
                                             <span className={`text-sm ${el.isCompleted ? 'text-aero-900 font-medium' : 'text-gray-400'}`}>{el.label}</span>
                                         </div>
                                         {el.evidenceUrl && (
                                             <div className="px-2 py-1 bg-aero-50 border border-aero-100 rounded text-[10px] text-aero-500 font-bold flex items-center gap-1">
                                                 <ImageIcon className="w-3 h-3" /> Photo
                                             </div>
                                         )}
                                     </div>
                                 ))}
                             </div>
                        </div>
                        )}

                        {/* 4. Per-Objective Review */}
                        {selectedSession.objectives && selectedSession.objectives.length > 0 && (
                        <div className="space-y-3">
                            <h4 className="text-xs font-bold text-aero-500 uppercase tracking-widest flex items-center gap-2">
                                <Target className="w-4 h-4" /> Objectives Review ({selectedSession.objectives.filter(o => o.isCompleted).length}/{selectedSession.objectives.length} completed)
                            </h4>
                            <div className="space-y-3">
                                {selectedSession.objectives.map((obj, idx) => {
                                    const review = localObjectiveReviews[obj.objectiveId];
                                    return (
                                    <div key={obj.objectiveId} className={`bg-white border rounded-xl p-4 space-y-3 ${
                                        review?.status === 'SUCCESS' ? 'border-green-300 bg-green-50/30' :
                                        review?.status === 'FAILURE' ? 'border-red-300 bg-red-50/30' :
                                        'border-aero-200'
                                    }`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${obj.isCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                                                    {idx + 1}
                                                </div>
                                                <span className="text-sm font-medium text-aero-900">{obj.label}</span>
                                            </div>
                                            <span className={`text-[10px] font-bold uppercase ${obj.isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                                                {obj.isCompleted ? 'Completed' : 'Incomplete'}
                                            </span>
                                        </div>

                                        {/* Evidence */}
                                        {obj.evidence && (
                                            <div className="flex flex-wrap gap-2">
                                                {obj.evidence.photos && obj.evidence.photos.length > 0 && (
                                                    <div className="flex items-center gap-1 px-2 py-1 bg-aero-50 border border-aero-100 rounded text-[10px] text-aero-600 font-bold">
                                                        <ImageIcon className="w-3 h-3" /> {obj.evidence.photos.length} Photo{obj.evidence.photos.length !== 1 ? 's' : ''}
                                                    </div>
                                                )}
                                                {obj.evidence.videos && obj.evidence.videos.length > 0 && (
                                                    <div className="flex items-center gap-1 px-2 py-1 bg-aero-50 border border-aero-100 rounded text-[10px] text-aero-600 font-bold">
                                                        <Camera className="w-3 h-3" /> {obj.evidence.videos.length} Video{obj.evidence.videos.length !== 1 ? 's' : ''}
                                                    </div>
                                                )}
                                                {obj.evidence.voiceRecordings && obj.evidence.voiceRecordings.length > 0 && (
                                                    <div className="flex items-center gap-1 px-2 py-1 bg-aero-50 border border-aero-100 rounded text-[10px] text-aero-600 font-bold">
                                                        <Edit2 className="w-3 h-3" /> {obj.evidence.voiceRecordings.length} Voice Recording{obj.evidence.voiceRecordings.length !== 1 ? 's' : ''}
                                                    </div>
                                                )}
                                                {obj.evidence.leadHandSignature && (
                                                    <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 border border-purple-100 rounded text-[10px] text-purple-600 font-bold">
                                                        <PenTool className="w-3 h-3" /> Lead Hand Signature
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Review controls */}
                                        {selectedSession.status === 'REVIEW_PENDING' && !selectedSession.reviewStatus && (
                                            <div className="flex items-center gap-2 pt-2 border-t border-aero-100">
                                                {review ? (
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-xs font-bold ${review.status === 'SUCCESS' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {review.status === 'SUCCESS' ? '✓ Approved' : '✗ Rejected'}
                                                        </span>
                                                        {review.failureReason && <span className="text-xs text-red-500">— {review.failureReason}</span>}
                                                        <button onClick={() => { const { [obj.objectiveId]: _, ...rest } = localObjectiveReviews; setLocalObjectiveReviews(rest); }} className="text-xs text-aero-400 hover:text-aero-600 underline ml-2">Reset</button>
                                                    </div>
                                                ) : reviewingObjectiveId === obj.objectiveId ? (
                                                    <div className="flex-1 space-y-2">
                                                        <textarea
                                                            value={objectiveFailureReason}
                                                            onChange={(e) => setObjectiveFailureReason(e.target.value)}
                                                            placeholder="Reason for failure (optional)..."
                                                            className="w-full border border-red-200 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-red-300"
                                                            rows={2}
                                                        />
                                                        <div className="flex gap-2">
                                                            <button onClick={() => confirmObjectiveFailure(obj.objectiveId)} className="px-3 py-1 bg-red-600 text-white rounded text-xs font-bold hover:bg-red-700">Confirm Failure</button>
                                                            <button onClick={() => setReviewingObjectiveId(null)} className="px-3 py-1 border border-aero-200 text-aero-600 rounded text-xs hover:bg-aero-50">Cancel</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <button onClick={() => handleObjectiveReview(obj.objectiveId, 'SUCCESS')} className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-bold hover:bg-green-200 transition-colors flex items-center gap-1">
                                                            <Check className="w-3 h-3" /> Approve
                                                        </button>
                                                        <button onClick={() => handleObjectiveReview(obj.objectiveId, 'FAILURE')} className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-bold hover:bg-red-200 transition-colors flex items-center gap-1">
                                                            <X className="w-3 h-3" /> Reject
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Show existing review */}
                                        {selectedSession.reviewStatus && review && (
                                            <div className={`text-xs font-bold pt-1 ${review.status === 'SUCCESS' ? 'text-green-600' : 'text-red-600'}`}>
                                                {review.status === 'SUCCESS' ? '✓ Approved' : `✗ Rejected${review.failureReason ? ': ' + review.failureReason : ''}`}
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                        )}

                        {/* 5. Location Log */}
                        {selectedSession.locationLog && selectedSession.locationLog.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-xs font-bold text-aero-500 uppercase tracking-widest flex items-center gap-2">
                                <MapPin className="w-4 h-4" /> Location Log ({selectedSession.locationLog.length} points)
                            </h4>
                            <div className="bg-white border border-aero-200 rounded-xl p-3 max-h-32 overflow-y-auto">
                                <div className="space-y-1">
                                    {selectedSession.locationLog.map((loc, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-xs text-aero-600 hover:bg-aero-50 px-2 py-1 rounded">
                                            <span className="font-mono">{loc.lat.toFixed(6)}, {loc.lng.toFixed(6)}</span>
                                            <span className="text-aero-400">{new Date(loc.timestamp).toLocaleTimeString()}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        )}

                        {/* 6. Candidate Comments */}
                        {selectedSession.candidateComments && selectedSession.candidateComments.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-xs font-bold text-aero-500 uppercase tracking-widest flex items-center gap-2">
                                <Edit2 className="w-4 h-4" /> Candidate Comments ({selectedSession.candidateComments.length})
                            </h4>
                            <div className="bg-white border border-aero-200 rounded-xl divide-y divide-aero-50">
                                {selectedSession.candidateComments.map((comment, idx) => (
                                    <div key={idx} className="p-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-[10px] text-aero-400 font-bold">{new Date(comment.timestamp).toLocaleString()}</span>
                                            {comment.voiceUrl && <span className="text-[10px] text-blue-500 font-bold">🎤 Voice Note</span>}
                                        </div>
                                        {comment.transcript && <p className="text-xs text-aero-700">{comment.transcript}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}

                        {/* 7. End Proof (Sign-off) */}
                        <div className="space-y-2 pt-2 border-t border-aero-100">
                             <h4 className="text-xs font-bold text-aero-500 uppercase tracking-widest flex items-center gap-2">
                                 <PenTool className="w-4 h-4" /> Final Sign-Off
                             </h4>
                             <div className="h-32 bg-white rounded-xl border border-aero-200 flex items-center justify-center p-4">
                                 {selectedSession.endSignOff ? (
                                     <div className="w-full h-full flex flex-col items-center justify-center border border-dashed border-aero-100 rounded-lg bg-aero-50/30">
                                         <span className="font-script text-2xl text-aero-800 rotate-[-5deg] opacity-70">
                                             Signed by {selectedSession.leadHandName}
                                         </span>
                                         <span className="text-[10px] text-aero-400 mt-2 font-mono">
                                             Verified at {new Date(selectedSession.endSignOff.timestamp).toLocaleTimeString()}
                                         </span>
                                     </div>
                                 ) : (
                                     <span className="text-sm text-gray-400 italic">Pending Lead Hand Sign-off</span>
                                 )}
                             </div>
                        </div>

                        {/* 8. Review Metadata (if already reviewed) */}
                        {selectedSession.reviewStatus && selectedSession.reviewedByName && (
                        <div className={`p-3 rounded-lg border text-xs ${
                            selectedSession.reviewStatus === 'APPROVED' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                        }`}>
                            <span className="font-bold">Reviewed by {selectedSession.reviewedByName}</span>
                            {selectedSession.reviewedAt && <span className="text-aero-400 ml-2">on {new Date(selectedSession.reviewedAt).toLocaleDateString()}</span>}
                        </div>
                        )}

                        {/* 9. Action Buttons */}
                        <div className="flex justify-between items-center pt-4 sticky bottom-0 bg-white pb-1">
                            <Button variant="secondary" onClick={() => setSelectedSession(null)}>Close</Button>
                            {selectedSession.status === 'REVIEW_PENDING' && !selectedSession.reviewStatus && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleSubmitSessionReview('REJECTED')}
                                        disabled={isSubmittingReview}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <X className="w-4 h-4" /> Reject Session
                                    </button>
                                    <button
                                        onClick={() => handleSubmitSessionReview('APPROVED')}
                                        disabled={isSubmittingReview}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <Check className="w-4 h-4" /> Approve Session
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

// --- Pathway Manager Components (Hoisted to Module Scope) ---

const BlockNode = ({ block, index, onDelete, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isDropTarget, onAddHorizontal, onDoubleClick, onClick }: { 
    block: PathwayBlock, 
    index: number,
    onDelete?: () => void,
    onDragStart?: (e: React.DragEvent) => void,
    onDragOver?: (e: React.DragEvent) => void,
    onDrop?: (e: React.DragEvent) => void,
    onDragEnd?: () => void,
    isDragging?: boolean,
    isDropTarget?: boolean,
    onAddHorizontal?: () => void,
    onDoubleClick?: () => void,
    onClick?: () => void
}) => {
    const _dc = getDesignCardStyle(block.type);
    return (
    <div 
        className={`p-3 rounded-lg border shadow-sm w-48 text-left relative group hover:border-aero-400 transition-all z-20 ${_dc ? `${_dc.bg} ${_dc.border} ${_dc.hoverBorder}` : 'bg-white'} ${isDragging ? 'opacity-50 border-dashed border-aero-400' : block.horizontalGroup ? 'border-purple-300' : _dc ? _dc.border : 'border-aero-200'} ${isDropTarget ? 'ring-2 ring-aero-500 ring-offset-2' : ''} ${onDragStart ? 'cursor-grab active:cursor-grabbing' : ''}`}
        draggable={!!onDragStart}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
        onDoubleClick={block.type === 'pathway' && onDoubleClick ? (e) => { e.stopPropagation(); onDoubleClick(); } : undefined}
    >
        <div className={`absolute -top-2 -left-2 w-6 h-6 ${_dc?.badgeBg || 'bg-aero-900'} text-white rounded-full flex items-center justify-center text-xs font-bold z-30 border-2 border-white shadow-sm`}>
            {index + 1}
        </div>
        {block.horizontalGroup && (
            <div className="absolute -top-2 left-6 px-1.5 py-0.5 bg-purple-500 text-white rounded text-[8px] font-bold z-30 border border-white shadow-sm flex items-center gap-0.5">
                <ArrowLeftRight className="w-2.5 h-2.5" />
                <span>PARALLEL</span>
            </div>
        )}
        {onDelete && (
            <button 
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30 border-2 border-white shadow-sm hover:bg-red-600"
                title="Delete block"
            >
                <X className="w-3 h-3" />
            </button>
        )}
        {onAddHorizontal && (
            <button 
                onClick={(e) => { e.stopPropagation(); onAddHorizontal(); }}
                className="absolute top-1/2 -right-8 -translate-y-1/2 w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30 border-2 border-white shadow-sm hover:bg-purple-600"
                title="Add block horizontally"
            >
                <Plus className="w-3 h-3" />
            </button>
        )}
        <div className="flex items-center gap-2 mb-1">
            {renderBlockIcon(block.type)}
            <span className="text-[10px] uppercase font-bold text-aero-400">{getBlockTypeDisplayName(block.type)}</span>
        </div>
        <div className="font-bold text-sm text-aero-900 leading-tight">{block.name}</div>
        <div className="text-xs text-aero-500 mt-1 truncate">{block.description}</div>
        {block.type === 'api' && (
            <div className="mt-2 p-1.5 bg-cyan-50 rounded border border-cyan-200">
                <div className="text-[9px] font-bold text-cyan-700 uppercase mb-0.5">API Endpoint</div>
                <div className="text-[10px] text-cyan-600 font-mono break-all">
                    {block.apiEndpoint || `/api/pathway/block/${block.id}/complete`}
                </div>
            </div>
        )}
        {block.type === 'pathway' && (
            <div className="mt-2 p-1.5 bg-emerald-50 rounded border border-emerald-200 flex items-center gap-1.5 cursor-pointer">
                <Network className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                <span className="text-[9px] font-bold text-emerald-700 uppercase">Double-click to view</span>
            </div>
        )}
        {block.type === 'ojt' && (
            <div className="mt-2 p-1.5 bg-green-50 rounded border border-green-200">
                <div className="text-[9px] font-bold text-green-700 uppercase mb-0.5">OJT Block</div>
                <div className="text-[10px] text-green-600">
                    {block.ojtConfig ? `${block.ojtConfig.objectives.length} objectives · ${block.ojtConfig.expectedDurationHours}h` : 'Click to configure'}
                </div>
            </div>
        )}
        {block.type === 'theory' && block.courseTemplateName && (
            <div className="mt-2 p-1.5 bg-blue-50 rounded border border-blue-200">
                <div className="text-[9px] font-bold text-blue-700 uppercase mb-0.5">TR Course</div>
                <div className="text-[10px] text-blue-600 truncate">{block.courseTemplateName}</div>
            </div>
        )}
        <div className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 ${_dc?.dotColor || 'bg-aero-300'} rounded-full border-2 border-white z-10`}></div>
    </div>
    );
};

// Agent-led OJT (beta) — per-block toggle + dogfooding helper. See docs/OJT_AGENT.md.
const OjtAgentPilotPanel: React.FC<{
    config: OjtBlockConfig;
    setConfig: React.Dispatch<React.SetStateAction<OjtBlockConfig>>;
    /** When true, render the "issue test mini-page token" dogfood button. */
    showTestTokenHelper?: boolean;
}> = ({ config, setConfig, showTestTokenHelper }) => {
    const [issuing, setIssuing] = useState(false);
    const [issuedUrl, setIssuedUrl] = useState<string | null>(null);
    const [issueError, setIssueError] = useState('');

    const update = <K extends keyof OjtBlockConfig>(key: K, value: OjtBlockConfig[K]) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const toLocalDateTimeInput = (ms?: number) => {
        if (!ms) return '';
        const d = new Date(ms);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const handleIssueTestToken = async () => {
        setIssuing(true);
        setIssueError('');
        setIssuedUrl(null);
        try {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const { getApp } = await import('firebase/app');
            const fns = getFunctions(getApp(), 'europe-west2');
            const issue = httpsCallable(fns, 'issueTestMinipageToken');
            const result: any = await issue({
                kind: 'free_text',
                prompt: 'Test mini-page — type any reply to verify the flow.',
                expiryMinutes: config.miniPageExpiryMinutes || 30,
                origin: window.location.origin,
            });
            setIssuedUrl(result?.data?.url || null);
        } catch (e: any) {
            console.error('issueTestMinipageToken failed', e);
            setIssueError(e?.message || 'Failed to issue token.');
        } finally {
            setIssuing(false);
        }
    };

    return (
        <div className="border-t border-green-200 pt-3 mt-3 space-y-3">
            <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer font-bold">
                <input
                    type="checkbox"
                    checked={!!config.agentEnabled}
                    onChange={(e) => update('agentEnabled', e.target.checked)}
                    className="rounded border-green-300 text-green-600 focus:ring-green-500"
                />
                <Bot className="w-3 h-3" /> Use agent-led OJT (beta)
            </label>
            <p className="text-[10px] text-green-600 pl-6">
                When enabled, an SMS agent runs the OJT session with the candidate and lead hand. Existing candidate-led OJT is unaffected. Requires the trainingroom runtime to be deployed — see <code className="font-mono">docs/OJT_AGENT.md</code>.
            </p>
            {config.agentEnabled && (
                <div className="pl-6 space-y-3">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-green-600 w-32">Persona</span>
                        <select
                            value={config.agentPersona || 'coach'}
                            onChange={(e) => update('agentPersona', e.target.value as 'coach')}
                            className="border border-green-200 rounded px-2 py-1 text-xs"
                        >
                            <option value="coach">Coach</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-green-600 w-32">Scheduled start</span>
                        <input
                            type="datetime-local"
                            value={toLocalDateTimeInput(config.scheduledStartAt)}
                            onChange={(e) => {
                                const v = e.target.value;
                                update('scheduledStartAt', v ? new Date(v).getTime() : undefined);
                            }}
                            className="border border-green-200 rounded px-2 py-1 text-xs"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-green-600 w-32">Mini-page expiry</span>
                        <input
                            type="number"
                            min="1"
                            value={config.miniPageExpiryMinutes ?? 30}
                            onChange={(e) => update('miniPageExpiryMinutes', Math.max(1, parseInt(e.target.value, 10) || 30))}
                            className="w-20 border border-green-200 rounded px-2 py-1 text-xs"
                        />
                        <span className="text-[10px] text-green-600">minutes</span>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!config.requireCoPresenceSelfie}
                            onChange={(e) => update('requireCoPresenceSelfie', e.target.checked)}
                            className="rounded border-green-300 text-green-600 focus:ring-green-500"
                        />
                        Require co-presence selfie before SESSION_BODY
                    </label>

                    {showTestTokenHelper && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                            <p className="text-[10px] text-amber-700 font-bold uppercase">Dogfooding helper</p>
                            <p className="text-[10px] text-amber-700">
                                Issue a one-shot mini-page token (kind = free_text). For testing the flow before the trainingroom runtime exists.
                            </p>
                            <button
                                type="button"
                                onClick={handleIssueTestToken}
                                disabled={issuing}
                                className="text-xs bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded px-3 py-1.5 font-semibold"
                            >
                                {issuing ? 'Issuing…' : 'Issue test mini-page token'}
                            </button>
                            {issueError && <p className="text-[10px] text-red-600">{issueError}</p>}
                            {issuedUrl && (
                                <div className="flex items-start gap-2">
                                    <code className="flex-1 text-[10px] text-amber-900 break-all bg-white border border-amber-200 rounded p-2">{issuedUrl}</code>
                                    <button
                                        type="button"
                                        onClick={() => navigator.clipboard.writeText(issuedUrl)}
                                        className="text-amber-700 hover:text-amber-900"
                                        title="Copy"
                                    >
                                        <Copy className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface VisualFlowProps {
    pathway: Pathway;
    onUpdate?: (blocks: PathwayBlock[]) => void;
    activeOrgId?: string;
}

const VisualFlow = ({ pathway, onUpdate, activeOrgId }: VisualFlowProps) => {
    const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);
    const [dropTargetId, setDropTargetId] = useState<string | null>(null);
    const [isAddBlockOpen, setIsAddBlockOpen] = useState(false);
    const [addBlockBranch, setAddBlockBranch] = useState<string>('STANDARD');
    const [isBranchPreSelected, setIsBranchPreSelected] = useState(false);
    const [newBlockName, setNewBlockName] = useState('');
    const [newBlockDescription, setNewBlockDescription] = useState('');
    const [newBlockType, setNewBlockType] = useState<PathwayBlock['type']>('theory');
    const [addHorizontalToBlockId, setAddHorizontalToBlockId] = useState<string | null>(null);
    const [subPathwayModalBlock, setSubPathwayModalBlock] = useState<PathwayBlock | null>(null);
    const [subPathway, setSubPathway] = useState<Pathway | null>(null);
    const [isLoadingSubPathway, setIsLoadingSubPathway] = useState(false);
    const [subPathwayCandidateStats, setSubPathwayCandidateStats] = useState<{ total: number; completed: number } | null>(null);
    const [newBlockLinkedPathwayId, setNewBlockLinkedPathwayId] = useState('');
    const [newBlockCourseTemplateId, setNewBlockCourseTemplateId] = useState('');
    const [newBlockCourseTemplateName, setNewBlockCourseTemplateName] = useState('');
    const [availablePathways, setAvailablePathways] = useState<Pathway[]>([]);

    // Edit block modal state
    const [editingBlock, setEditingBlock] = useState<PathwayBlock | null>(null);
    const [isEditBlockOpen, setIsEditBlockOpen] = useState(false);
    const [editBlockName, setEditBlockName] = useState('');
    const [editBlockDescription, setEditBlockDescription] = useState('');
    const [editBlockType, setEditBlockType] = useState<PathwayBlock['type']>('theory');
    const [editBlockLinkedPathwayId, setEditBlockLinkedPathwayId] = useState('');
    const [editBlockPreviewPathway, setEditBlockPreviewPathway] = useState<Pathway | null>(null);
    const [isLoadingEditPreview, setIsLoadingEditPreview] = useState(false);
    const [editBlockApiEndpoint, setEditBlockApiEndpoint] = useState('');
    const [editBlockApiProvider, setEditBlockApiProvider] = useState('');
    const [editBlockCourseTemplateId, setEditBlockCourseTemplateId] = useState('');
    const [editBlockCourseTemplateName, setEditBlockCourseTemplateName] = useState('');
    const [editBlockOjtConfig, setEditBlockOjtConfig] = useState<OjtBlockConfig>({
        handshake: { pin: false, selfie: false, qrCode: false },
        objectives: [],
        preStartChecklist: [],
        locationTrackingEnabled: false,
        candidateCommentsEnabled: false,
        expectedDurationHours: 1
    });
    const [editBlockUploadInstructions, setEditBlockUploadInstructions] = useState('');
    const [editBlockPracticalConfig, setEditBlockPracticalConfig] = useState<PracticalBlockConfig>({
        rubric: [],
        authorizedRoles: []
    });
    const [editBlockAssessmentConfig, setEditBlockAssessmentConfig] = useState<AssessmentBlockConfig>({
        questions: [],
        passingThreshold: 80,
        maxAttempts: 3,
        requiresManualGrading: false
    });
    const [editBlockUploadConfig, setEditBlockUploadConfig] = useState<UploadBlockConfig>({
        instructions: '',
        acceptedFileTypes: [],
        requiresAdminVerification: true,
        aiVerificationEnabled: false
    });
    const [editBlockApiConfig, setEditBlockApiConfig] = useState<ApiBlockConfig>({
        provider: '',
        endpoint: '',
        successCriteria: ''
    });
    const [editBlockAuthorisationConfig, setEditBlockAuthorisationConfig] = useState<AuthorisationBlockConfig>({
        authorizedRoles: [],
        customRoleLabel: ''
    });
    const [newBlockUploadInstructions, setNewBlockUploadInstructions] = useState('');
    const [newBlockOjtConfig, setNewBlockOjtConfig] = useState<OjtBlockConfig>({
        handshake: { pin: false, selfie: false, qrCode: false },
        objectives: [],
        preStartChecklist: [],
        locationTrackingEnabled: false,
        candidateCommentsEnabled: false,
        expectedDurationHours: 1
    });
    const [newBlockPracticalConfig, setNewBlockPracticalConfig] = useState<PracticalBlockConfig>({
        rubric: [],
        authorizedRoles: []
    });
    const [newBlockAssessmentConfig, setNewBlockAssessmentConfig] = useState<AssessmentBlockConfig>({
        questions: [],
        passingThreshold: 80,
        maxAttempts: 3,
        requiresManualGrading: false
    });
    const [newBlockUploadConfig, setNewBlockUploadConfig] = useState<UploadBlockConfig>({
        instructions: '',
        acceptedFileTypes: [],
        requiresAdminVerification: true,
        aiVerificationEnabled: false
    });
    const [newBlockApiConfig, setNewBlockApiConfig] = useState<ApiBlockConfig>({
        provider: '',
        endpoint: '',
        successCriteria: ''
    });
    const [newBlockAuthorisationConfig, setNewBlockAuthorisationConfig] = useState<AuthorisationBlockConfig>({
        authorizedRoles: [],
        customRoleLabel: ''
    });
    const [courseTemplates, setCourseTemplates] = useState<CourseTemplate[]>([]);

    const handleOpenSubPathway = async (block: PathwayBlock) => {
        if (block.type !== 'pathway' || !block.linkedPathwayId) return;
        setSubPathwayModalBlock(block);
        setIsLoadingSubPathway(true);
        setSubPathwayCandidateStats(null);
        try {
            // Load the linked pathway versions — prefer PUBLISHED, fall back to latest
            const versions = await DataService.getPathwayVersions(block.linkedPathwayId);
            if (versions.length > 0) {
                const published = versions.filter(v => v.status === 'PUBLISHED').sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
                const target = published || versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
                setSubPathway({
                    id: target.id,
                    rootId: block.linkedPathwayId,
                    title: block.name,
                    role: '',
                    lastEdited: '',
                    status: target.status || 'DRAFT',
                    version: target.version ?? 1.0,
                    blocks: target.blocks || [],
                });
            } else {
                setSubPathway({
                    id: block.linkedPathwayId,
                    rootId: block.linkedPathwayId,
                    title: block.name,
                    role: '',
                    lastEdited: '',
                    status: 'DRAFT',
                    version: 1.0,
                    blocks: [],
                });
            }
        } catch (e) {
            console.error('Failed to load sub-pathway', e);
            // Show empty state
            setSubPathway({
                id: block.linkedPathwayId,
                rootId: block.linkedPathwayId,
                title: block.name,
                role: '',
                lastEdited: '',
                status: 'DRAFT',
                version: 1.0,
                blocks: [],
            });
        } finally {
            setIsLoadingSubPathway(false);
        }

        // Load candidate progress stats for the sub-pathway (non-blocking)
        // NOTE: Accurate per-block progress calculation requires a dedicated progress tracking collection
        // This currently counts assigned vs completed candidates as a high-level indicator
        if (activeOrgId && block.linkedPathwayId) {
            try {
                const candidates = await DataService.getCandidatesForPathway(activeOrgId, block.linkedPathwayId);
                const completed = candidates.filter((c: any) => c.status === 'COMPLETED').length;
                setSubPathwayCandidateStats({ total: candidates.length, completed });
            } catch (e) {
                console.error('Failed to load sub-pathway candidate stats', e);
            }
        }
    };

    const standard = pathway.blocks.filter(b => b.branchId === 'STANDARD');
    const accelerated = pathway.blocks.filter(b => b.branchId === 'ACCELERATED');
    const refresher = pathway.blocks.filter(b => b.branchId === 'REFRESHER');
    const merge = pathway.blocks.filter(b => b.branchId === 'MERGE');
    const remedial = pathway.blocks.filter(b => b.branchId === 'REMEDIAL');
    const getIndex = (id: string) => pathway.blocks.findIndex(b => b.id === id);

    const handleDelete = (blockId: string) => {
        if (!onUpdate) return;
        if (confirm('Delete this block?')) {
            onUpdate(pathway.blocks.filter(b => b.id !== blockId));
        }
    };

    const handleDragStart = (e: React.DragEvent, blockId: string) => {
        setDraggedBlockId(blockId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', blockId);
    };

    const handleDragOver = (e: React.DragEvent, blockId: string) => {
        e.preventDefault();
        if (draggedBlockId && blockId !== draggedBlockId) {
            setDropTargetId(blockId);
        }
    };

    const handleDragEnd = () => {
        setDraggedBlockId(null);
        setDropTargetId(null);
    };

    const handleDrop = (e: React.DragEvent, targetBlockId: string, branchId: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (!onUpdate || !draggedBlockId || draggedBlockId === targetBlockId) {
            setDraggedBlockId(null);
            setDropTargetId(null);
            return;
        }
        
        const sourceBlock = pathway.blocks.find(b => b.id === draggedBlockId);
        const targetBlock = pathway.blocks.find(b => b.id === targetBlockId);
        
        if (!sourceBlock || !targetBlock) {
            setDraggedBlockId(null);
            setDropTargetId(null);
            return;
        }
        
        // Only allow reordering within the same branch
        if (sourceBlock.branchId !== targetBlock.branchId) {
            setDraggedBlockId(null);
            setDropTargetId(null);
            return;
        }
        
        // Handle horizontal group reordering
        if (sourceBlock.horizontalGroup && targetBlock.horizontalGroup && 
            sourceBlock.horizontalGroup === targetBlock.horizontalGroup) {
            // Reorder within horizontal group
            const newBlocks = pathway.blocks.map(b => {
                if (b.id === draggedBlockId) {
                    return { ...b, horizontalPosition: targetBlock.horizontalPosition };
                }
                if (b.id === targetBlockId) {
                    return { ...b, horizontalPosition: sourceBlock.horizontalPosition || 0 };
                }
                return b;
            });
            onUpdate(newBlocks);
        } else {
            // Vertical reordering within branch
            const branchBlocks = pathway.blocks.filter(b => b.branchId === branchId);
            const otherBlocks = pathway.blocks.filter(b => b.branchId !== branchId);
            
            const sourceIdx = branchBlocks.findIndex(b => b.id === draggedBlockId);
            const targetIdx = branchBlocks.findIndex(b => b.id === targetBlockId);
            
            const [movedBlock] = branchBlocks.splice(sourceIdx, 1);
            branchBlocks.splice(targetIdx, 0, movedBlock);
            
            const newBlocks = [...otherBlocks, ...branchBlocks];
            onUpdate(newBlocks);
        }
        
        setDraggedBlockId(null);
        setDropTargetId(null);
    };

    const handleAddBlock = () => {
        if (!onUpdate || !newBlockName.trim()) return;
        
        let newBlock: PathwayBlock = {
            id: `block_${Date.now()}`,
            name: newBlockName.trim(),
            description: newBlockDescription.trim(),
            type: newBlockType,
            branchId: addBlockBranch,
            duration: '1 hour',
            evidence: 'Completion',
            ...(newBlockType === 'pathway' && newBlockLinkedPathwayId ? { linkedPathwayId: newBlockLinkedPathwayId } : {}),
            ...(newBlockType === 'theory' && newBlockCourseTemplateId ? { courseTemplateId: newBlockCourseTemplateId, courseTemplateName: newBlockCourseTemplateName } : {}),
            ...(newBlockType === 'upload' ? { uploadInstructions: newBlockUploadConfig.instructions, uploadConfig: newBlockUploadConfig } : {}),
            ...(newBlockType === 'ojt' ? { ojtConfig: newBlockOjtConfig } : {}),
            ...(newBlockType === 'practical' ? { practicalConfig: newBlockPracticalConfig } : {}),
            ...(newBlockType === 'assessment' ? { assessmentConfig: newBlockAssessmentConfig } : {}),
            ...(newBlockType === 'api' ? { apiEndpoint: newBlockApiConfig.endpoint, apiProvider: newBlockApiConfig.provider, apiConfig: newBlockApiConfig } : {}),
            ...(newBlockType === 'authorisation' ? { authorisationConfig: newBlockAuthorisationConfig } : {})
        };
        
        // If adding horizontally next to an existing block
        if (addHorizontalToBlockId) {
            const existingBlock = pathway.blocks.find(b => b.id === addHorizontalToBlockId);
            if (existingBlock) {
                if (existingBlock.horizontalGroup) {
                    // Add to existing group
                    const groupBlocks = pathway.blocks.filter(b => b.horizontalGroup === existingBlock.horizontalGroup);
                    const maxPosition = Math.max(...groupBlocks.map(b => b.horizontalPosition || 0));
                    newBlock.horizontalGroup = existingBlock.horizontalGroup;
                    newBlock.horizontalPosition = maxPosition + 1;
                } else {
                    // Create new group with both blocks
                    const groupId = `group_${Date.now()}`;
                    existingBlock.horizontalGroup = groupId;
                    existingBlock.horizontalPosition = 0;
                    newBlock.horizontalGroup = groupId;
                    newBlock.horizontalPosition = 1;
                }
            }
        }
        
        onUpdate([...pathway.blocks, newBlock]);
        setIsAddBlockOpen(false);
        setIsBranchPreSelected(false);
        setNewBlockName('');
        setNewBlockDescription('');
        setNewBlockType('theory');
        setAddHorizontalToBlockId(null);
        setNewBlockLinkedPathwayId('');
        setNewBlockCourseTemplateId('');
        setNewBlockCourseTemplateName('');
        setNewBlockUploadInstructions('');
        setNewBlockOjtConfig({
            handshake: { pin: false, selfie: false, qrCode: false },
            objectives: [],
            preStartChecklist: [],
            locationTrackingEnabled: false,
            candidateCommentsEnabled: false,
            expectedDurationHours: 1
        });
        setNewBlockPracticalConfig({ rubric: [], authorizedRoles: [] });
        setNewBlockAssessmentConfig({ questions: [], passingThreshold: 80, maxAttempts: 3, requiresManualGrading: false });
        setNewBlockUploadConfig({ instructions: '', acceptedFileTypes: [], requiresAdminVerification: true, aiVerificationEnabled: false });
        setNewBlockApiConfig({ provider: '', endpoint: '', successCriteria: '' });
        setNewBlockAuthorisationConfig({ authorizedRoles: [], customRoleLabel: '' });
    };

    const openAddBlockModal = (branchId: string, preSelected: boolean = false, horizontalToBlockId?: string) => {
        setAddBlockBranch(branchId);
        setIsBranchPreSelected(preSelected);
        setAddHorizontalToBlockId(horizontalToBlockId || null);
        setIsAddBlockOpen(true);
        // Eagerly load templates and pathways so dropdowns are ready for any type selection
        loadCourseTemplates();
        if (activeOrgId && availablePathways.length === 0) {
            DataService.getPathways(activeOrgId).then(setAvailablePathways).catch(console.error);
        }
    };

    // --- Edit Block Modal Handlers ---
    // Load the linked pathway preview when editBlockLinkedPathwayId changes
    useEffect(() => {
        if (!editBlockLinkedPathwayId) {
            setEditBlockPreviewPathway(null);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setIsLoadingEditPreview(true);
            try {
                // Load versions — prefer PUBLISHED, fall back to latest
                const versions = await DataService.getPathwayVersions(editBlockLinkedPathwayId);
                if (cancelled) return;
                const pw = availablePathways.find(p => p.rootId === editBlockLinkedPathwayId);
                if (versions.length > 0) {
                    const published = versions.filter(v => v.status === 'PUBLISHED').sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
                    const target = published || versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
                    setEditBlockPreviewPathway({
                        id: target.id,
                        rootId: editBlockLinkedPathwayId,
                        title: pw?.title || editBlockName || 'Sub-Pathway',
                        role: pw?.role || '',
                        lastEdited: pw?.lastEdited || '',
                        status: target.status || 'DRAFT',
                        version: target.version ?? 1.0,
                        blocks: target.blocks || [],
                    });
                } else {
                    setEditBlockPreviewPathway({
                        id: editBlockLinkedPathwayId,
                        rootId: editBlockLinkedPathwayId,
                        title: pw?.title || editBlockName || 'Sub-Pathway',
                        role: '',
                        lastEdited: '',
                        status: 'DRAFT',
                        version: 1.0,
                        blocks: [],
                    });
                }
            } catch (e) {
                console.error('Failed to load linked pathway preview', e);
                if (!cancelled) setEditBlockPreviewPathway(null);
            } finally {
                if (!cancelled) setIsLoadingEditPreview(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [editBlockLinkedPathwayId]);

    const openEditBlockModal = (block: PathwayBlock) => {
        setEditingBlock(block);
        setEditBlockName(block.name);
        setEditBlockDescription(block.description || '');
        setEditBlockType(block.type);
        setEditBlockLinkedPathwayId(block.linkedPathwayId || '');
        setEditBlockApiEndpoint(block.apiEndpoint || '');
        setEditBlockApiProvider(block.apiProvider || '');
        setEditBlockCourseTemplateId(block.courseTemplateId || '');
        setEditBlockCourseTemplateName(block.courseTemplateName || '');
        setEditBlockOjtConfig(block.ojtConfig || {
            handshake: { pin: false, selfie: false, qrCode: false },
            objectives: [],
            preStartChecklist: [],
            locationTrackingEnabled: false,
            candidateCommentsEnabled: false,
            expectedDurationHours: 1
        });
        setEditBlockUploadInstructions(block.uploadInstructions || '');
        setEditBlockPracticalConfig(block.practicalConfig || { rubric: [], authorizedRoles: [] });
        setEditBlockAssessmentConfig(block.assessmentConfig || { questions: [], passingThreshold: 80, maxAttempts: 3, requiresManualGrading: false });
        setEditBlockUploadConfig(block.uploadConfig || { instructions: block.uploadInstructions || '', acceptedFileTypes: [], requiresAdminVerification: true, aiVerificationEnabled: false });
        setEditBlockApiConfig(block.apiConfig || { provider: block.apiProvider || '', endpoint: block.apiEndpoint || '', successCriteria: '' });
        setEditBlockAuthorisationConfig(block.authorisationConfig || { authorizedRoles: [], customRoleLabel: '' });
        setIsEditBlockOpen(true);
        // Eagerly load both course templates and available pathways so dropdowns are populated
        // regardless of current type (user may switch type in the modal)
        loadCourseTemplates();
        if (activeOrgId) {
            DataService.getPathways(activeOrgId).then(setAvailablePathways).catch(console.error);
        }
    };

    const loadCourseTemplates = async () => {
        try {
            const templates = await DataService.getCourseTemplates();
            setCourseTemplates(templates);
        } catch (e) {
            console.error('Failed to load course templates:', e);
        }
    };

    const handleSaveEditBlock = () => {
        if (!onUpdate || !editingBlock || !editBlockName.trim()) return;
        const updatedBlocks = pathway.blocks.map(b => {
            if (b.id !== editingBlock.id) return b;
            return {
                ...b,
                name: editBlockName.trim(),
                description: editBlockDescription.trim(),
                type: editBlockType,
                ...(editBlockType === 'pathway' ? { linkedPathwayId: editBlockLinkedPathwayId } : {}),
                ...(editBlockType === 'api' ? { apiEndpoint: editBlockApiConfig.endpoint, apiProvider: editBlockApiConfig.provider, apiConfig: editBlockApiConfig } : {}),
                ...(editBlockType === 'theory' ? { courseTemplateId: editBlockCourseTemplateId, courseTemplateName: editBlockCourseTemplateName } : {}),
                ...(editBlockType === 'ojt' ? { ojtConfig: editBlockOjtConfig } : {}),
                ...(editBlockType === 'upload' ? { uploadInstructions: editBlockUploadConfig.instructions, uploadConfig: editBlockUploadConfig } : {}),
                ...(editBlockType === 'practical' ? { practicalConfig: editBlockPracticalConfig } : {}),
                ...(editBlockType === 'assessment' ? { assessmentConfig: editBlockAssessmentConfig } : {}),
                ...(editBlockType === 'authorisation' ? { authorisationConfig: editBlockAuthorisationConfig } : {}),
            };
        });
        onUpdate(updatedBlocks);
        setIsEditBlockOpen(false);
        setEditingBlock(null);
    };

    // OJT config helpers — accept setter so they work for both add and edit modals
    const ojtAddObjective = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>) => {
        setter(prev => ({
            ...prev,
            objectives: [...prev.objectives, { id: `obj_${Date.now()}`, title: '', description: '', evidence: { photo: false, signature: false, comment: false } }]
        }));
    };
    const ojtRemoveObjective = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>, id: string) => {
        setter(prev => ({ ...prev, objectives: prev.objectives.filter(o => o.id !== id) }));
    };
    const ojtUpdateObjective = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>, id: string, field: string, value: any) => {
        setter(prev => ({
            ...prev,
            objectives: prev.objectives.map(o => o.id === id ? { ...o, [field]: value } : o)
        }));
    };
    const ojtUpdateObjectiveEvidence = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>, id: string, evidenceField: string, value: boolean) => {
        setter(prev => ({
            ...prev,
            objectives: prev.objectives.map(o => o.id === id ? { ...o, evidence: { ...o.evidence, [evidenceField]: value } } : o)
        }));
    };
    const ojtAddChecklistItem = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>) => {
        setter(prev => ({
            ...prev,
            preStartChecklist: [...prev.preStartChecklist, { id: `psc_${Date.now()}`, label: '', required: true }]
        }));
    };
    const ojtRemoveChecklistItem = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>, id: string) => {
        setter(prev => ({ ...prev, preStartChecklist: prev.preStartChecklist.filter(i => i.id !== id) }));
    };
    const ojtUpdateChecklistItem = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>, id: string, field: string, value: any) => {
        setter(prev => ({
            ...prev,
            preStartChecklist: prev.preStartChecklist.map(i => i.id === id ? { ...i, [field]: value } : i)
        }));
    };

    // OJT Mode helpers
    const ojtEnableModes = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>) => {
        setter(prev => ({
            ...prev,
            modes: prev.modes && prev.modes.length > 0 ? prev.modes : [...BUILT_IN_OJT_MODES],
            modeSignOff: prev.modeSignOff || { required: false, method: 'pin' },
        }));
    };
    const ojtDisableModes = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>) => {
        setter(prev => ({ ...prev, modes: [], modeSignOff: undefined }));
    };
    const ojtAddCustomMode = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>) => {
        const palette = ['#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#ef4444', '#14b8a6', '#a855f7'];
        setter(prev => {
            const existing = prev.modes || [];
            const color = palette[existing.length % palette.length];
            return {
                ...prev,
                modes: [...existing, { id: `mode_${Date.now()}`, label: '', color, isBuiltIn: false }],
            };
        });
    };
    const ojtRemoveMode = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>, id: string) => {
        setter(prev => ({ ...prev, modes: (prev.modes || []).filter(m => m.id !== id || m.isBuiltIn) }));
    };
    const ojtUpdateMode = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>, id: string, field: keyof OjtMode, value: any) => {
        setter(prev => ({
            ...prev,
            modes: (prev.modes || []).map(m => m.id === id ? { ...m, [field]: value } : m),
        }));
    };
    const ojtUpdateModeSignOff = (setter: React.Dispatch<React.SetStateAction<OjtBlockConfig>>, field: 'required' | 'method', value: any) => {
        setter(prev => ({
            ...prev,
            modeSignOff: { required: false, method: 'pin', ...(prev.modeSignOff || {}), [field]: value },
        }));
    };

    // Convenience wrappers for edit modal (preserves existing call sites)
    const addOjtObjective = () => ojtAddObjective(setEditBlockOjtConfig);
    const removeOjtObjective = (id: string) => ojtRemoveObjective(setEditBlockOjtConfig, id);
    const updateOjtObjective = (id: string, field: string, value: any) => ojtUpdateObjective(setEditBlockOjtConfig, id, field, value);
    const updateOjtObjectiveEvidence = (id: string, evidenceField: string, value: boolean) => ojtUpdateObjectiveEvidence(setEditBlockOjtConfig, id, evidenceField, value);
    const addPreStartChecklistItem = () => ojtAddChecklistItem(setEditBlockOjtConfig);
    const removePreStartChecklistItem = (id: string) => ojtRemoveChecklistItem(setEditBlockOjtConfig, id);
    const updatePreStartChecklistItem = (id: string, field: string, value: any) => ojtUpdateChecklistItem(setEditBlockOjtConfig, id, field, value);

    // Practical config helpers — generic setter pattern
    const practicalAddRubricItem = (setter: React.Dispatch<React.SetStateAction<PracticalBlockConfig>>) => {
        setter(prev => ({
            ...prev,
            rubric: [...prev.rubric, { id: `rubric_${Date.now()}`, title: '', description: '' }]
        }));
    };
    const practicalRemoveRubricItem = (setter: React.Dispatch<React.SetStateAction<PracticalBlockConfig>>, id: string) => {
        setter(prev => ({ ...prev, rubric: prev.rubric.filter(r => r.id !== id) }));
    };
    const practicalUpdateRubricItem = (setter: React.Dispatch<React.SetStateAction<PracticalBlockConfig>>, id: string, field: string, value: string) => {
        setter(prev => ({
            ...prev,
            rubric: prev.rubric.map(r => r.id === id ? { ...r, [field]: value } : r)
        }));
    };
    const practicalToggleRole = (setter: React.Dispatch<React.SetStateAction<PracticalBlockConfig>>, role: PracticalBlockConfig['authorizedRoles'][number]) => {
        setter(prev => ({
            ...prev,
            authorizedRoles: prev.authorizedRoles.includes(role)
                ? prev.authorizedRoles.filter(r => r !== role)
                : [...prev.authorizedRoles, role]
        }));
    };

    // Assessment config helpers — generic setter pattern
    const assessmentAddQuestion = (setter: React.Dispatch<React.SetStateAction<AssessmentBlockConfig>>) => {
        setter(prev => ({
            ...prev,
            questions: [...prev.questions, { id: `q_${Date.now()}`, text: '', type: 'multiple_choice' as const, options: ['', ''], correctAnswer: '' }]
        }));
    };
    const assessmentRemoveQuestion = (setter: React.Dispatch<React.SetStateAction<AssessmentBlockConfig>>, id: string) => {
        setter(prev => ({ ...prev, questions: prev.questions.filter(q => q.id !== id) }));
    };
    const assessmentUpdateQuestion = (setter: React.Dispatch<React.SetStateAction<AssessmentBlockConfig>>, id: string, field: string, value: any) => {
        setter(prev => ({
            ...prev,
            questions: prev.questions.map(q => q.id === id ? { ...q, [field]: value } : q)
        }));
    };
    const assessmentAddOption = (setter: React.Dispatch<React.SetStateAction<AssessmentBlockConfig>>, questionId: string) => {
        setter(prev => ({
            ...prev,
            questions: prev.questions.map(q => q.id === questionId ? { ...q, options: [...(q.options || []), ''] } : q)
        }));
    };
    const assessmentRemoveOption = (setter: React.Dispatch<React.SetStateAction<AssessmentBlockConfig>>, questionId: string, idx: number) => {
        setter(prev => ({
            ...prev,
            questions: prev.questions.map(q => q.id === questionId ? { ...q, options: (q.options || []).filter((_, i) => i !== idx) } : q)
        }));
    };
    const assessmentUpdateOption = (setter: React.Dispatch<React.SetStateAction<AssessmentBlockConfig>>, questionId: string, idx: number, value: string) => {
        setter(prev => ({
            ...prev,
            questions: prev.questions.map(q => q.id === questionId ? { ...q, options: (q.options || []).map((o, i) => i === idx ? value : o) } : q)
        }));
    };

    // Authorisation config helpers
    const authorisationToggleRole = (setter: React.Dispatch<React.SetStateAction<AuthorisationBlockConfig>>, role: AuthorisationBlockConfig['authorizedRoles'][number]) => {
        setter(prev => ({
            ...prev,
            authorizedRoles: prev.authorizedRoles.includes(role)
                ? prev.authorizedRoles.filter(r => r !== role)
                : [...prev.authorizedRoles, role]
        }));
    };

    const blockTypes = getDropdownBlockTypes();

    const renderBranchBlocks = (blocks: PathwayBlock[], branchId: string) => {
        // Group blocks by horizontalGroup or treat as individual
        const groupedBlocks: Array<PathwayBlock[]> = [];
        const processedGroups = new Set<string>();
        
        blocks.forEach(block => {
            if (block.horizontalGroup && !processedGroups.has(block.horizontalGroup)) {
                // Get all blocks in this horizontal group
                const groupBlocks = blocks
                    .filter(b => b.horizontalGroup === block.horizontalGroup)
                    .sort((a, b) => (a.horizontalPosition || 0) - (b.horizontalPosition || 0));
                groupedBlocks.push(groupBlocks);
                processedGroups.add(block.horizontalGroup);
            } else if (!block.horizontalGroup) {
                // Single block (not part of horizontal group)
                groupedBlocks.push([block]);
            }
        });
        
        return (
            <>
                {groupedBlocks.map((group, groupIdx) => (
                    <div key={groupIdx} className="flex flex-col items-center">
                        {group.length > 1 ? (
                            // Horizontal group - display blocks side by side
                            <>
                                <div className="flex gap-4 items-center justify-center mb-2">
                                    {group.map((b, idx) => (
                                        <div key={b.id} className="flex flex-col items-center">
                                            {idx > 0 && <div className="text-[10px] text-aero-400 font-bold mb-1">OR</div>}
                                            <BlockNode 
                                                block={b} 
                                                index={getIndex(b.id)}
                                                onDelete={onUpdate ? () => handleDelete(b.id) : undefined}
                                                onDragStart={onUpdate ? (e) => handleDragStart(e, b.id) : undefined}
                                                onDragOver={onUpdate ? (e) => handleDragOver(e, b.id) : undefined}
                                                onDrop={onUpdate ? (e) => handleDrop(e, b.id, branchId) : undefined}
                                                onDragEnd={onUpdate ? handleDragEnd : undefined}
                                                isDragging={draggedBlockId === b.id}
                                                isDropTarget={dropTargetId === b.id}
                                                onAddHorizontal={onUpdate ? () => openAddBlockModal(branchId, true, b.id) : undefined}
                                                onDoubleClick={b.type === 'pathway' && b.linkedPathwayId ? () => handleOpenSubPathway(b) : undefined}
                                                onClick={onUpdate ? () => openEditBlockModal(b) : undefined}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="text-[9px] text-aero-500 italic mb-2 bg-aero-50 px-2 py-1 rounded">Complete any in this group</div>
                            </>
                        ) : (
                            // Single block
                            <BlockNode 
                                block={group[0]} 
                                index={getIndex(group[0].id)}
                                onDelete={onUpdate ? () => handleDelete(group[0].id) : undefined}
                                onDragStart={onUpdate ? (e) => handleDragStart(e, group[0].id) : undefined}
                                onDragOver={onUpdate ? (e) => handleDragOver(e, group[0].id) : undefined}
                                onDrop={onUpdate ? (e) => handleDrop(e, group[0].id, branchId) : undefined}
                                onDragEnd={onUpdate ? handleDragEnd : undefined}
                                isDragging={draggedBlockId === group[0].id}
                                isDropTarget={dropTargetId === group[0].id}
                                onAddHorizontal={onUpdate ? () => openAddBlockModal(branchId, true, group[0].id) : undefined}
                                onDoubleClick={group[0].type === 'pathway' && group[0].linkedPathwayId ? () => handleOpenSubPathway(group[0]) : undefined}
                                onClick={onUpdate ? () => openEditBlockModal(group[0]) : undefined}
                            />
                        )}
                        <ArrowDown className="w-4 h-4 text-aero-300 mt-2" />
                    </div>
                ))}
                {onUpdate && (
                    <button 
                        onClick={() => openAddBlockModal(branchId, true)}
                        className="mt-2 px-3 py-2 border-2 border-dashed border-aero-300 rounded-lg text-aero-400 hover:border-aero-500 hover:text-aero-600 transition-colors flex items-center gap-2 text-xs font-medium"
                    >
                        <Plus className="w-4 h-4" /> Add Block
                    </button>
                )}
            </>
        );
    };

    return (
        <div className="flex flex-col gap-4">
            <VisualFlowCanvas
                pathway={pathway}
                onUpdate={onUpdate}
                onEditBlock={openEditBlockModal}
                onDeleteBlock={handleDelete}
                onAddBlock={openAddBlockModal}
                onOpenSubPathway={handleOpenSubPathway}
            />
                {/* Add Block Modal */}
                <Modal isOpen={isAddBlockOpen} onClose={() => { setIsAddBlockOpen(false); setIsBranchPreSelected(false); }} title="Add New Block">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-aero-700 mb-1">Type</label>
                            <select
                                value={newBlockType}
                                onChange={(e) => {
                                    const val = e.target.value as PathwayBlock['type'];
                                    setNewBlockType(val);
                                    if (val === 'theory') loadCourseTemplates();
                                    if (val === 'pathway' && activeOrgId && availablePathways.length === 0) {
                                        DataService.getPathways(activeOrgId).then(setAvailablePathways).catch(console.error);
                                    }
                                }}
                                className="w-full border border-aero-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent"
                            >
                                {blockTypes.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        {newBlockType === 'pathway' && (
                            <div>
                                <label className="block text-sm font-medium text-aero-700 mb-1">Linked Pathway</label>
                                <select
                                    value={newBlockLinkedPathwayId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setNewBlockLinkedPathwayId(id);
                                        const pw = Object.values(availablePathways.reduce((acc, p) => {
                                            if (!acc[p.rootId] || acc[p.rootId].version < p.version) acc[p.rootId] = p;
                                            return acc;
                                        }, {} as Record<string, Pathway>)).find(p => p.rootId === id);
                                        if (pw) setNewBlockName(pw.title);
                                    }}
                                    className="w-full border border-aero-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                                >
                                    <option value="">Select a pathway...</option>
                                    {/* Group by rootId to show unique pathways — only those with a PUBLISHED version, excluding already linked */}
                                    {Object.values(availablePathways.filter(p => p.rootId !== pathway.rootId && p.status === 'PUBLISHED' && !pathway.blocks.some(b => b.type === 'pathway' && b.linkedPathwayId === p.rootId)).reduce((acc, p) => {
                                        if (!acc[p.rootId] || acc[p.rootId].version < p.version) acc[p.rootId] = p;
                                        return acc;
                                    }, {} as Record<string, Pathway>)).map(p => (
                                        <option key={p.rootId} value={p.rootId}>{p.title} (v{(p.version ?? 1.0).toFixed(1)})</option>
                                    ))}
                                </select>
                                {availablePathways.length === 0 && (
                                    <p className="text-xs text-aero-400 mt-1">No other pathways available to link</p>
                                )}
                            </div>
                        )}
                        {newBlockType === 'theory' && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                                <h4 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" /> TrainingRoom Course Template</h4>
                                <p className="text-xs text-blue-600">Select the course template this block must complete before progressing.</p>
                                <select
                                    value={newBlockCourseTemplateId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setNewBlockCourseTemplateId(id);
                                        const tmpl = courseTemplates.find(t => t.id === id);
                                        setNewBlockCourseTemplateName(tmpl?.name || '');
                                        if (tmpl?.name) setNewBlockName(tmpl.name);
                                    }}
                                    className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                >
                                    <option value="">Select a course template...</option>
                                    {courseTemplates.filter(t => !pathway.blocks.some(b => b.type === 'theory' && b.courseTemplateId === t.id)).map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                {courseTemplates.length === 0 && (
                                    <p className="text-xs text-blue-400">No course templates found. Templates are loaded from TrainingRoom.</p>
                                )}
                            </div>
                        )}
                        {newBlockType === 'ojt' && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
                                <h4 className="text-sm font-bold text-green-800 flex items-center gap-2"><Crosshair className="w-4 h-4" /> OJT Configuration</h4>

                                {/* Handshake */}
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><Fingerprint className="w-3 h-3" /> Handshake Verification</h5>
                                    <p className="text-[10px] text-green-600">How the lead hand verifies they are present with the candidate.</p>
                                    <div className="flex gap-4">
                                        {(['pin', 'selfie', 'qrCode'] as const).map(method => (
                                            <label key={method} className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={newBlockOjtConfig.handshake[method]}
                                                    onChange={(e) => setNewBlockOjtConfig(prev => ({
                                                        ...prev,
                                                        handshake: { ...prev.handshake, [method]: e.target.checked }
                                                    }))}
                                                    className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                                />
                                                {method === 'pin' ? 'PIN Code' : method === 'selfie' ? 'Selfie' : 'QR Code'}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Expected Duration */}
                                <div>
                                    <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><Clock className="w-3 h-3" /> Expected Duration</h5>
                                    <div className="flex items-center gap-2 mt-1">
                                        <input
                                            type="number"
                                            min="0.5"
                                            step="0.5"
                                            value={newBlockOjtConfig.expectedDurationHours}
                                            onChange={(e) => setNewBlockOjtConfig(prev => ({ ...prev, expectedDurationHours: parseFloat(e.target.value) || 1 }))}
                                            className="w-20 border border-green-200 rounded-lg px-3 py-1.5 text-sm"
                                        />
                                        <span className="text-xs text-green-600">hours</span>
                                    </div>
                                </div>

                                {/* Location Tracking */}
                                <div>
                                    <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newBlockOjtConfig.locationTrackingEnabled}
                                            onChange={(e) => setNewBlockOjtConfig(prev => ({ ...prev, locationTrackingEnabled: e.target.checked }))}
                                            className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                        />
                                        <MapPin className="w-3 h-3" /> Enable Location Tracking
                                    </label>
                                </div>

                                {/* Candidate Comments */}
                                <div>
                                    <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newBlockOjtConfig.candidateCommentsEnabled}
                                            onChange={(e) => setNewBlockOjtConfig(prev => ({ ...prev, candidateCommentsEnabled: e.target.checked }))}
                                            className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                        />
                                        <Edit2 className="w-3 h-3" /> Allow Candidate Comments
                                    </label>
                                </div>

                                {/* Pre-Start Checklist */}
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><ListChecks className="w-3 h-3" /> Pre-Start Checklist</h5>
                                    {newBlockOjtConfig.preStartChecklist.map((item, idx) => (
                                        <div key={item.id} className="flex items-center gap-2">
                                            <span className="text-[10px] text-green-500 w-4">{idx + 1}.</span>
                                            <input
                                                type="text"
                                                value={item.label}
                                                onChange={(e) => ojtUpdateChecklistItem(setNewBlockOjtConfig, item.id, 'label', e.target.value)}
                                                className="flex-1 border border-green-200 rounded px-2 py-1 text-xs"
                                                placeholder="Checklist item..."
                                            />
                                            <label className="flex items-center gap-1 text-[10px] text-green-600">
                                                <input
                                                    type="checkbox"
                                                    checked={item.required}
                                                    onChange={(e) => ojtUpdateChecklistItem(setNewBlockOjtConfig, item.id, 'required', e.target.checked)}
                                                    className="rounded border-green-300 text-green-600 w-3 h-3"
                                                />
                                                Req
                                            </label>
                                            <button onClick={() => ojtRemoveChecklistItem(setNewBlockOjtConfig, item.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                        </div>
                                    ))}
                                    <button onClick={() => ojtAddChecklistItem(setNewBlockOjtConfig)} className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Checklist Item</button>
                                </div>

                                {/* Objectives */}
                                <div className="space-y-3">
                                    <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><Target className="w-3 h-3" /> Objectives</h5>
                                    {newBlockOjtConfig.objectives.map((obj, idx) => (
                                        <div key={obj.id} className="bg-white border border-green-200 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-green-700">Objective {idx + 1}</span>
                                                <button onClick={() => ojtRemoveObjective(setNewBlockOjtConfig, obj.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                            </div>
                                            <input
                                                type="text"
                                                value={obj.title}
                                                onChange={(e) => ojtUpdateObjective(setNewBlockOjtConfig, obj.id, 'title', e.target.value)}
                                                className="w-full border border-green-200 rounded px-2 py-1 text-xs"
                                                placeholder="Objective title..."
                                            />
                                            <textarea
                                                value={obj.description || ''}
                                                onChange={(e) => ojtUpdateObjective(setNewBlockOjtConfig, obj.id, 'description', e.target.value)}
                                                className="w-full border border-green-200 rounded px-2 py-1 text-xs"
                                                rows={1}
                                                placeholder="Description (optional)..."
                                            />
                                            <div className="flex gap-3">
                                                <span className="text-[10px] text-green-600 font-medium">Evidence:</span>
                                                {(['photo', 'signature', 'comment'] as const).map(ev => (
                                                    <label key={ev} className="flex items-center gap-1 text-[10px] text-green-600 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={obj.evidence[ev]}
                                                            onChange={(e) => ojtUpdateObjectiveEvidence(setNewBlockOjtConfig, obj.id, ev, e.target.checked)}
                                                            className="rounded border-green-300 text-green-600 w-3 h-3"
                                                        />
                                                        {ev.charAt(0).toUpperCase() + ev.slice(1)}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    <button onClick={() => ojtAddObjective(setNewBlockOjtConfig)} className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Objective</button>
                                </div>

                                {/* OJT Modes */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><Layers className="w-3 h-3" /> Modes</h5>
                                        <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={!!(newBlockOjtConfig.modes && newBlockOjtConfig.modes.length > 0)}
                                                onChange={(e) => e.target.checked ? ojtEnableModes(setNewBlockOjtConfig) : ojtDisableModes(setNewBlockOjtConfig)}
                                                className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                            />
                                            Enable modes
                                        </label>
                                    </div>
                                    {newBlockOjtConfig.modes && newBlockOjtConfig.modes.length > 0 && (
                                        <>
                                            <p className="text-[10px] text-green-600">Candidates can switch between modes during a session. Each mode tracks time independently. Required minutes are informational only.</p>
                                            {newBlockOjtConfig.modes.map((mode, idx) => (
                                                <div key={mode.id} className="bg-white border border-green-200 rounded-lg p-3 space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="color"
                                                            value={mode.color}
                                                            onChange={(e) => ojtUpdateMode(setNewBlockOjtConfig, mode.id, 'color', e.target.value)}
                                                            className="w-8 h-8 rounded cursor-pointer border border-green-200 flex-shrink-0"
                                                            title="Mode colour"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={mode.label}
                                                            onChange={(e) => ojtUpdateMode(setNewBlockOjtConfig, mode.id, 'label', e.target.value)}
                                                            className="flex-1 border border-green-200 rounded px-2 py-1 text-xs"
                                                            placeholder="Mode name..."
                                                            disabled={mode.isBuiltIn}
                                                        />
                                                        {!mode.isBuiltIn && (
                                                            <button onClick={() => ojtRemoveMode(setNewBlockOjtConfig, mode.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                                        )}
                                                        {mode.isBuiltIn && <span className="text-[9px] text-green-500 uppercase font-bold">Default</span>}
                                                    </div>
                                                    <div className="flex items-center gap-2 pl-10">
                                                        <span className="text-[10px] text-green-600">Min minutes (optional):</span>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={mode.requiredMinutes ?? ''}
                                                            onChange={(e) => {
                                                                const v = e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value, 10) || 0);
                                                                ojtUpdateMode(setNewBlockOjtConfig, mode.id, 'requiredMinutes', v);
                                                            }}
                                                            className="w-20 border border-green-200 rounded px-2 py-0.5 text-xs"
                                                            placeholder="—"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            <button onClick={() => ojtAddCustomMode(setNewBlockOjtConfig)} className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Custom Mode</button>

                                            {/* Mode Change Sign-Off */}
                                            <div className="border-t border-green-200 pt-3 mt-3 space-y-2">
                                                <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!newBlockOjtConfig.modeSignOff?.required}
                                                        onChange={(e) => ojtUpdateModeSignOff(setNewBlockOjtConfig, 'required', e.target.checked)}
                                                        className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                                    />
                                                    Require lead-hand sign-off when switching modes
                                                </label>
                                                {newBlockOjtConfig.modeSignOff?.required && (
                                                    <div className="flex items-center gap-2 pl-6">
                                                        <span className="text-[10px] text-green-600">Method:</span>
                                                        <select
                                                            value={newBlockOjtConfig.modeSignOff?.method || 'pin'}
                                                            onChange={(e) => ojtUpdateModeSignOff(setNewBlockOjtConfig, 'method', e.target.value)}
                                                            className="border border-green-200 rounded px-2 py-1 text-xs"
                                                        >
                                                            <option value="pin">PIN</option>
                                                            <option value="signature">Signature</option>
                                                            <option value="selfie">Selfie</option>
                                                            <option value="qr">QR Code</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Agent-led OJT (beta) */}
                                <OjtAgentPilotPanel config={newBlockOjtConfig} setConfig={setNewBlockOjtConfig} />
                            </div>
                        )}
                        {newBlockType === 'upload' && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                                <h4 className="text-sm font-bold text-orange-800 flex items-center gap-2"><Upload className="w-4 h-4" /> Upload Task</h4>
                                <p className="text-xs text-orange-600">The trainee will see these instructions when completing this block. The title is taken from the block name above.</p>
                                <div>
                                    <label className="block text-xs font-medium text-orange-700 mb-1">Instructions to Trainee</label>
                                    <textarea
                                        value={newBlockUploadConfig.instructions}
                                        onChange={(e) => setNewBlockUploadConfig(prev => ({ ...prev, instructions: e.target.value }))}
                                        className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                                        placeholder="e.g., Please upload a clear photo of your completed sign-off sheet..."
                                        rows={3}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-orange-700 uppercase">Accepted File Types</h5>
                                    <div className="flex gap-4">
                                        {(['image', 'pdf', 'document', 'video'] as const).map(ft => (
                                            <label key={ft} className="flex items-center gap-2 text-xs text-orange-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={newBlockUploadConfig.acceptedFileTypes.includes(ft)}
                                                    onChange={(e) => setNewBlockUploadConfig(prev => ({
                                                        ...prev,
                                                        acceptedFileTypes: e.target.checked
                                                            ? [...prev.acceptedFileTypes, ft]
                                                            : prev.acceptedFileTypes.filter(t => t !== ft)
                                                    }))}
                                                    className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                                />
                                                {ft.charAt(0).toUpperCase() + ft.slice(1)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="flex items-center gap-2 text-xs text-orange-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newBlockUploadConfig.requiresAdminVerification}
                                            onChange={(e) => setNewBlockUploadConfig(prev => ({ ...prev, requiresAdminVerification: e.target.checked }))}
                                            className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                        />
                                        <Shield className="w-3 h-3" /> Requires Admin Verification
                                    </label>
                                </div>
                                <div>
                                    <label className="flex items-center gap-2 text-xs text-orange-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newBlockUploadConfig.aiVerificationEnabled}
                                            onChange={(e) => setNewBlockUploadConfig(prev => ({ ...prev, aiVerificationEnabled: e.target.checked }))}
                                            className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                        />
                                        <Eye className="w-3 h-3" /> AI Content Verification
                                    </label>
                                    <p className="text-xs text-orange-500 mt-1 ml-5">When enabled, uploaded files are checked by AI against the block description to verify relevance.</p>
                                </div>
                            </div>
                        )}
                        {newBlockType === 'practical' && (
                            <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 space-y-4">
                                <h4 className="text-sm font-bold text-orange-800 flex items-center gap-2"><Truck className="w-4 h-4" /> Practical Configuration</h4>

                                {/* Authorized Roles */}
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-orange-700 uppercase">Authorized Evaluator Roles</h5>
                                    <p className="text-[10px] text-orange-600">Who can evaluate the candidate for this practical.</p>
                                    <div className="flex gap-4">
                                        {([['assessor', 'Assessor'], ['lead_hand', 'Lead Hand'], ['site_manager', 'Site Manager']] as const).map(([role, label]) => (
                                            <label key={role} className="flex items-center gap-2 text-xs text-orange-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={newBlockPracticalConfig.authorizedRoles.includes(role)}
                                                    onChange={() => practicalToggleRole(setNewBlockPracticalConfig, role)}
                                                    className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Rubric / Competency Checklist */}
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-orange-700 uppercase flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Competency Rubric</h5>
                                    {newBlockPracticalConfig.rubric.map((item, idx) => (
                                        <div key={item.id} className="bg-white border border-orange-200 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-orange-700">Item {idx + 1}</span>
                                                <button onClick={() => practicalRemoveRubricItem(setNewBlockPracticalConfig, item.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                            </div>
                                            <input
                                                type="text"
                                                value={item.title}
                                                onChange={(e) => practicalUpdateRubricItem(setNewBlockPracticalConfig, item.id, 'title', e.target.value)}
                                                className="w-full border border-orange-200 rounded px-2 py-1 text-xs"
                                                placeholder="Competency title..."
                                            />
                                            <textarea
                                                value={item.description}
                                                onChange={(e) => practicalUpdateRubricItem(setNewBlockPracticalConfig, item.id, 'description', e.target.value)}
                                                className="w-full border border-orange-200 rounded px-2 py-1 text-xs"
                                                rows={1}
                                                placeholder="Description / criteria..."
                                            />
                                        </div>
                                    ))}
                                    <button onClick={() => practicalAddRubricItem(setNewBlockPracticalConfig)} className="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Rubric Item</button>
                                </div>
                            </div>
                        )}
                        {newBlockType === 'assessment' && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
                                <h4 className="text-sm font-bold text-red-800 flex items-center gap-2"><Shield className="w-4 h-4" /> Assessment Configuration</h4>

                                {/* Settings Row */}
                                <div className="flex gap-4 flex-wrap">
                                    <div>
                                        <label className="block text-xs font-medium text-red-700 mb-1">Passing Threshold (%)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={newBlockAssessmentConfig.passingThreshold}
                                            onChange={(e) => setNewBlockAssessmentConfig(prev => ({ ...prev, passingThreshold: parseInt(e.target.value) || 0 }))}
                                            className="w-20 border border-red-200 rounded-lg px-3 py-1.5 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-red-700 mb-1">Max Attempts</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={newBlockAssessmentConfig.maxAttempts}
                                            onChange={(e) => setNewBlockAssessmentConfig(prev => ({ ...prev, maxAttempts: parseInt(e.target.value) || 0 }))}
                                            className="w-20 border border-red-200 rounded-lg px-3 py-1.5 text-sm"
                                        />
                                        <p className="text-[10px] text-red-500 mt-0.5">0 = unlimited</p>
                                    </div>
                                    <div className="flex items-end pb-1">
                                        <label className="flex items-center gap-2 text-xs text-red-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={newBlockAssessmentConfig.requiresManualGrading}
                                                onChange={(e) => setNewBlockAssessmentConfig(prev => ({ ...prev, requiresManualGrading: e.target.checked }))}
                                                className="rounded border-red-300 text-red-600 focus:ring-red-500"
                                            />
                                            Requires Manual Grading
                                        </label>
                                    </div>
                                </div>

                                {/* Questions */}
                                <div className="space-y-3">
                                    <h5 className="text-xs font-bold text-red-700 uppercase flex items-center gap-1"><Hash className="w-3 h-3" /> Questions</h5>
                                    {newBlockAssessmentConfig.questions.map((q, idx) => (
                                        <div key={q.id} className="bg-white border border-red-200 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-red-700">Question {idx + 1}</span>
                                                <button onClick={() => assessmentRemoveQuestion(setNewBlockAssessmentConfig, q.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                            </div>
                                            <div className="flex gap-2">
                                                <select
                                                    value={q.type}
                                                    onChange={(e) => assessmentUpdateQuestion(setNewBlockAssessmentConfig, q.id, 'type', e.target.value)}
                                                    className="border border-red-200 rounded px-2 py-1 text-xs"
                                                >
                                                    <option value="multiple_choice">Multiple Choice</option>
                                                    <option value="short_answer">Short Answer</option>
                                                    <option value="true_false">True / False</option>
                                                </select>
                                            </div>
                                            <textarea
                                                value={q.text}
                                                onChange={(e) => assessmentUpdateQuestion(setNewBlockAssessmentConfig, q.id, 'text', e.target.value)}
                                                className="w-full border border-red-200 rounded px-2 py-1 text-xs"
                                                rows={2}
                                                placeholder="Question text..."
                                            />
                                            {q.type === 'multiple_choice' && (
                                                <div className="space-y-1 pl-2">
                                                    <span className="text-[10px] text-red-600 font-medium">Options:</span>
                                                    {(q.options || []).map((opt, oi) => (
                                                        <div key={oi} className="flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                name={`correct_${q.id}`}
                                                                checked={q.correctAnswer === opt && opt !== ''}
                                                                onChange={() => assessmentUpdateQuestion(setNewBlockAssessmentConfig, q.id, 'correctAnswer', opt)}
                                                                className="text-red-600 focus:ring-red-500"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={opt}
                                                                onChange={(e) => assessmentUpdateOption(setNewBlockAssessmentConfig, q.id, oi, e.target.value)}
                                                                className="flex-1 border border-red-200 rounded px-2 py-1 text-xs"
                                                                placeholder={`Option ${oi + 1}`}
                                                            />
                                                            <button onClick={() => assessmentRemoveOption(setNewBlockAssessmentConfig, q.id, oi)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                                        </div>
                                                    ))}
                                                    <button onClick={() => assessmentAddOption(setNewBlockAssessmentConfig, q.id)} className="text-[10px] text-red-600 hover:text-red-800 flex items-center gap-1"><Plus className="w-2 h-2" /> Add Option</button>
                                                </div>
                                            )}
                                            {q.type === 'true_false' && (
                                                <div className="flex gap-4 pl-2">
                                                    <span className="text-[10px] text-red-600 font-medium">Correct answer:</span>
                                                    {['True', 'False'].map(val => (
                                                        <label key={val} className="flex items-center gap-1 text-xs text-red-700 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name={`tf_${q.id}`}
                                                                checked={q.correctAnswer === val}
                                                                onChange={() => assessmentUpdateQuestion(setNewBlockAssessmentConfig, q.id, 'correctAnswer', val)}
                                                                className="text-red-600 focus:ring-red-500"
                                                            />
                                                            {val}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button onClick={() => assessmentAddQuestion(setNewBlockAssessmentConfig)} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Question</button>
                                </div>
                            </div>
                        )}
                        {newBlockType === 'api' && (
                            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 space-y-3">
                                <h4 className="text-sm font-bold text-cyan-800 flex items-center gap-2"><Zap className="w-4 h-4" /> 3rd Party API Configuration</h4>
                                <div>
                                    <label className="block text-xs font-medium text-cyan-700 mb-1">Provider Name</label>
                                    <input
                                        type="text"
                                        value={newBlockApiConfig.provider}
                                        onChange={(e) => setNewBlockApiConfig(prev => ({ ...prev, provider: e.target.value }))}
                                        className="w-full border border-cyan-200 rounded-lg px-3 py-2 text-sm"
                                        placeholder="e.g., Acme Corp"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-cyan-700 mb-1">Endpoint URL</label>
                                    <input
                                        type="text"
                                        value={newBlockApiConfig.endpoint}
                                        onChange={(e) => setNewBlockApiConfig(prev => ({ ...prev, endpoint: e.target.value }))}
                                        className="w-full border border-cyan-200 rounded-lg px-3 py-2 text-sm"
                                        placeholder="https://api.example.com/verify"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-cyan-700 mb-1">Success Criteria</label>
                                    <textarea
                                        value={newBlockApiConfig.successCriteria}
                                        onChange={(e) => setNewBlockApiConfig(prev => ({ ...prev, successCriteria: e.target.value }))}
                                        className="w-full border border-cyan-200 rounded-lg px-3 py-2 text-sm"
                                        rows={2}
                                        placeholder="e.g., API must return status 'valid' for the candidate's license number..."
                                    />
                                </div>
                            </div>
                        )}
                        {newBlockType === 'authorisation' && (
                            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-4">
                                <h4 className="text-sm font-bold text-amber-800 flex items-center gap-2"><Stamp className="w-4 h-4" /> Official Authorisation Configuration</h4>
                                <p className="text-[10px] text-amber-600">Specify which roles have the authority to review the entire pathway and grant final sign-off.</p>

                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-amber-700 uppercase">Authorized Sign-off Roles</h5>
                                    <div className="flex flex-col gap-2">
                                        {([['site_manager', 'Site Manager'], ['operations_director', 'Operations Director'], ['training_manager', 'Training Manager'], ['custom', 'Custom Role']] as const).map(([role, label]) => (
                                            <label key={role} className="flex items-center gap-2 text-xs text-amber-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={newBlockAuthorisationConfig.authorizedRoles.includes(role)}
                                                    onChange={() => authorisationToggleRole(setNewBlockAuthorisationConfig, role)}
                                                    className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                    {newBlockAuthorisationConfig.authorizedRoles.includes('custom') && (
                                        <div className="mt-2">
                                            <label className="block text-xs font-medium text-amber-700 mb-1">Custom Role Label</label>
                                            <input
                                                type="text"
                                                value={newBlockAuthorisationConfig.customRoleLabel || ''}
                                                onChange={(e) => setNewBlockAuthorisationConfig(prev => ({ ...prev, customRoleLabel: e.target.value }))}
                                                className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm"
                                                placeholder="e.g., Quality Assurance Lead"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {!isBranchPreSelected && (
                            <div>
                                <label className="block text-sm font-medium text-aero-700 mb-1">Branch</label>
                                <select
                                    value={addBlockBranch}
                                    onChange={(e) => setAddBlockBranch(e.target.value)}
                                    className="w-full border border-aero-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent"
                                >
                                    <option value="STANDARD">Standard (Novice)</option>
                                    <option value="ACCELERATED">Accelerated (RPL)</option>
                                    <option value="REFRESHER">Refresher (Renewal)</option>
                                    <option value="MERGE">Merge (Final Authorisation)</option>
                                    <option value="REMEDIAL">Remedial</option>
                                </select>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-aero-700 mb-1">Block Name</label>
                            <input
                                type="text"
                                value={newBlockName}
                                onChange={(e) => setNewBlockName(e.target.value)}
                                className="w-full border border-aero-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent"
                                placeholder="e.g., Theory Assessment"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-aero-700 mb-1">Description (optional)</label>
                            <textarea
                                value={newBlockDescription}
                                onChange={(e) => setNewBlockDescription(e.target.value)}
                                className="w-full border border-aero-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent"
                                placeholder="Brief description of this block..."
                                rows={2}
                            />
                        </div>
                        
                        {addHorizontalToBlockId && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-purple-700">
                                    <ArrowLeftRight className="w-4 h-4" />
                                    <span className="text-xs font-medium">Adding as parallel block</span>
                                </div>
                                <p className="text-xs text-purple-600 mt-1">This block can be completed in any order with its group</p>
                            </div>
                        )}
                        
                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => { setIsAddBlockOpen(false); setIsBranchPreSelected(false); setAddHorizontalToBlockId(null); }}
                                className="flex-1 px-4 py-2 border border-aero-200 text-aero-700 rounded-lg hover:bg-aero-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddBlock}
                                disabled={!newBlockName.trim()}
                                className="flex-1 px-4 py-2 bg-aero-800 text-white rounded-lg hover:bg-aero-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Add Block
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Sub-Pathway Viewer Modal */}
                <Modal isOpen={!!subPathwayModalBlock} onClose={() => { setSubPathwayModalBlock(null); setSubPathway(null); setSubPathwayCandidateStats(null); }} title={`Sub-Pathway: ${subPathwayModalBlock?.name || ''}`} maxWidth="max-w-6xl">
                    {isLoadingSubPathway ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="w-8 h-8 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin mb-4"></div>
                            <p className="text-sm text-aero-500">Loading sub-pathway...</p>
                        </div>
                    ) : subPathway && subPathway.blocks.length > 0 ? (
                        <div className="rounded-xl border border-aero-200 shadow-lg overflow-hidden bg-gray-50">
                            {/* Browser chrome - title bar */}
                            <div className="bg-gray-100 border-b border-aero-200 px-4 py-2.5 flex items-center gap-3">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-red-400" />
                                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                                    <div className="w-3 h-3 rounded-full bg-green-400" />
                                </div>
                                {/* Address bar */}
                                <div className="flex-1 flex items-center gap-2 bg-white rounded-md border border-aero-200 px-3 py-1.5 text-xs text-aero-500 font-mono">
                                    <Network className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                    <span className="truncate">{subPathway.title}</span>
                                    <span className="text-aero-300 mx-1">/</span>
                                    <span className="font-mono text-emerald-600">v{(subPathway.version ?? 1.0).toFixed(1)}</span>
                                    <span className={`ml-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${subPathway.status === 'PUBLISHED' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{subPathway.status}</span>
                                </div>
                                {/* Stats */}
                                <div className="flex items-center gap-3 text-xs text-aero-500 flex-shrink-0">
                                    <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{subPathway.blocks.length} blocks</span>
                                    {subPathwayCandidateStats && subPathwayCandidateStats.total > 0 && (
                                        <div className="flex items-center gap-1.5">
                                            <Users className="w-3.5 h-3.5" />
                                            <span className="font-bold text-aero-700">{subPathwayCandidateStats.completed}</span>
                                            <span className="text-aero-400">/ {subPathwayCandidateStats.total}</span>
                                            <div className="w-12 h-1.5 bg-aero-100 rounded-full overflow-hidden">
                                                <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${(subPathwayCandidateStats.completed / subPathwayCandidateStats.total) * 100}%` }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Browser viewport */}
                            <div className="max-h-[65vh] overflow-auto bg-white p-4">
                                <VisualFlow pathway={subPathway} />
                            </div>
                        </div>
                    ) : subPathway ? (
                        <div className="rounded-xl border border-aero-200 shadow-lg overflow-hidden bg-gray-50">
                            {/* Browser chrome - title bar */}
                            <div className="bg-gray-100 border-b border-aero-200 px-4 py-2.5 flex items-center gap-3">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded-full bg-red-400" />
                                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                                    <div className="w-3 h-3 rounded-full bg-green-400" />
                                </div>
                                <div className="flex-1 flex items-center gap-2 bg-white rounded-md border border-aero-200 px-3 py-1.5 text-xs text-aero-500 font-mono">
                                    <Network className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                    <span className="truncate">{subPathway.title}</span>
                                </div>
                            </div>
                            <div className="bg-white flex flex-col items-center justify-center py-16 text-center">
                                <div className="w-16 h-16 bg-aero-50 rounded-full flex items-center justify-center mb-4">
                                    <Network className="w-8 h-8 text-aero-300" />
                                </div>
                                <p className="text-aero-500 font-medium">No blocks in this sub-pathway yet</p>
                                <p className="text-xs text-aero-400 mt-1">Open the pathway directly to start building it</p>
                            </div>
                        </div>
                    ) : null}
                </Modal>

                {/* Edit Block Modal */}
                <Modal isOpen={isEditBlockOpen} onClose={() => { setIsEditBlockOpen(false); setEditingBlock(null); }} title={`Edit Block: ${editingBlock?.name || ''}`} maxWidth={editBlockType === 'pathway' ? 'max-w-6xl' : 'max-w-3xl'}>
                    <div className="flex flex-col" style={{ maxHeight: '75vh' }}>
                    <div className="space-y-4 overflow-y-auto pr-1 flex-1 min-h-0 pb-2">
                        {/* Basic Fields */}
                        <div>
                            <label className="block text-sm font-medium text-aero-700 mb-1">Type</label>
                            <select
                                value={editBlockType}
                                onChange={(e) => {
                                    const newType = e.target.value as PathwayBlock['type'];
                                    setEditBlockType(newType);
                                    if (newType === 'theory') loadCourseTemplates();
                                    if (newType === 'pathway' && activeOrgId && availablePathways.length === 0) {
                                        DataService.getPathways(activeOrgId).then(setAvailablePathways).catch(console.error);
                                    }
                                }}
                                className="w-full border border-aero-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent"
                            >
                                {blockTypes.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-aero-700 mb-1">Block Name</label>
                            <input
                                type="text"
                                value={editBlockName}
                                onChange={(e) => setEditBlockName(e.target.value)}
                                className="w-full border border-aero-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-aero-700 mb-1">Description</label>
                            <textarea
                                value={editBlockDescription}
                                onChange={(e) => setEditBlockDescription(e.target.value)}
                                className="w-full border border-aero-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent"
                                rows={2}
                            />
                        </div>

                        {/* Sub-Pathway Config */}
                        {editBlockType === 'pathway' && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                <h4 className="text-sm font-bold text-emerald-800 mb-2 flex items-center gap-2"><Network className="w-4 h-4" /> Sub-Pathway Link</h4>
                                <select
                                    value={editBlockLinkedPathwayId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setEditBlockLinkedPathwayId(id);
                                        const pw = Object.values(availablePathways.reduce((acc, p) => {
                                            if (!acc[p.rootId] || acc[p.rootId].version < p.version) acc[p.rootId] = p;
                                            return acc;
                                        }, {} as Record<string, Pathway>)).find(p => p.rootId === id);
                                        if (pw) setEditBlockName(pw.title);
                                    }}
                                    className="w-full border border-emerald-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                                >
                                    <option value="">Select a pathway...</option>
                                    {Object.values(availablePathways.filter(p => p.rootId !== pathway.rootId && p.status === 'PUBLISHED' && (p.rootId === editBlockLinkedPathwayId || !pathway.blocks.some(b => b.type === 'pathway' && b.linkedPathwayId === p.rootId))).reduce((acc, p) => {
                                        if (!acc[p.rootId] || acc[p.rootId].version < p.version) acc[p.rootId] = p;
                                        return acc;
                                    }, {} as Record<string, Pathway>)).map(p => (
                                        <option key={p.rootId} value={p.rootId}>{p.title} (v{p.version})</option>
                                    ))}
                                </select>
                                {/* Sub-Pathway Visual Flow Preview */}
                                {editBlockLinkedPathwayId && (
                                    <div className="mt-3 rounded-xl border border-aero-200 shadow-lg overflow-hidden bg-gray-50">
                                        <div className="bg-gray-100 border-b border-aero-200 px-3 py-2 flex items-center gap-2.5">
                                            <div className="flex items-center gap-1">
                                                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                                                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                                                <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                                            </div>
                                            <div className="flex-1 flex items-center gap-2 bg-white rounded-md border border-aero-200 px-2.5 py-1 text-xs text-aero-500 font-mono">
                                                <Network className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                                <span className="truncate">{editBlockPreviewPathway?.title || editBlockName || 'Loading...'}</span>
                                                {editBlockPreviewPathway && (
                                                    <>
                                                        <span className="text-aero-300">/</span>
                                                        <span className="text-emerald-600">v{(editBlockPreviewPathway.version ?? 1.0).toFixed(1)}</span>
                                                        <span className={`text-[8px] font-bold uppercase px-1 py-0.5 rounded ${editBlockPreviewPathway.status === 'PUBLISHED' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{editBlockPreviewPathway.status}</span>
                                                    </>
                                                )}
                                            </div>
                                            {editBlockPreviewPathway && (
                                                <span className="text-[10px] text-aero-400 flex items-center gap-1"><Layers className="w-3 h-3" />{editBlockPreviewPathway.blocks.length}</span>
                                            )}
                                        </div>
                                        {isLoadingEditPreview ? (
                                            <div className="bg-white flex flex-col items-center justify-center py-12">
                                                <div className="w-6 h-6 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin mb-3"></div>
                                                <p className="text-xs text-aero-400">Loading pathway...</p>
                                            </div>
                                        ) : editBlockPreviewPathway && editBlockPreviewPathway.blocks.length > 0 ? (
                                            <div className="max-h-[35vh] overflow-auto bg-white p-4">
                                                <VisualFlow pathway={editBlockPreviewPathway} />
                                            </div>
                                        ) : (
                                            <div className="bg-white flex flex-col items-center justify-center py-10 text-center">
                                                <Network className="w-8 h-8 text-aero-300 mb-2" />
                                                <p className="text-sm text-aero-500">No blocks in this pathway yet</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* API Config */}
                        {editBlockType === 'api' && (
                            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 space-y-3">
                                <h4 className="text-sm font-bold text-cyan-800 flex items-center gap-2"><Zap className="w-4 h-4" /> 3rd Party API Configuration</h4>
                                <div>
                                    <label className="block text-xs font-medium text-cyan-700 mb-1">Provider Name</label>
                                    <input type="text" value={editBlockApiConfig.provider} onChange={(e) => setEditBlockApiConfig(prev => ({ ...prev, provider: e.target.value }))} className="w-full border border-cyan-200 rounded-lg px-3 py-2 text-sm" placeholder="e.g., Acme Corp" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-cyan-700 mb-1">Endpoint URL</label>
                                    <input type="text" value={editBlockApiConfig.endpoint} onChange={(e) => setEditBlockApiConfig(prev => ({ ...prev, endpoint: e.target.value }))} className="w-full border border-cyan-200 rounded-lg px-3 py-2 text-sm" placeholder="https://api.example.com/verify" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-cyan-700 mb-1">Success Criteria</label>
                                    <textarea
                                        value={editBlockApiConfig.successCriteria}
                                        onChange={(e) => setEditBlockApiConfig(prev => ({ ...prev, successCriteria: e.target.value }))}
                                        className="w-full border border-cyan-200 rounded-lg px-3 py-2 text-sm"
                                        rows={2}
                                        placeholder="e.g., API must return status 'valid' for the candidate's license number..."
                                    />
                                </div>
                            </div>
                        )}

                        {/* Upload Config */}
                        {editBlockType === 'upload' && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                                <h4 className="text-sm font-bold text-orange-800 flex items-center gap-2"><Upload className="w-4 h-4" /> Upload Task</h4>
                                <p className="text-xs text-orange-600">The trainee will see these instructions when completing this block. The title is taken from the block name above.</p>
                                <div>
                                    <label className="block text-xs font-medium text-orange-700 mb-1">Instructions to Trainee</label>
                                    <textarea
                                        value={editBlockUploadConfig.instructions}
                                        onChange={(e) => setEditBlockUploadConfig(prev => ({ ...prev, instructions: e.target.value }))}
                                        className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                                        placeholder="e.g., Please upload a clear photo of your completed sign-off sheet..."
                                        rows={3}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-orange-700 uppercase">Accepted File Types</h5>
                                    <div className="flex gap-4">
                                        {(['image', 'pdf', 'document', 'video'] as const).map(ft => (
                                            <label key={ft} className="flex items-center gap-2 text-xs text-orange-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={editBlockUploadConfig.acceptedFileTypes.includes(ft)}
                                                    onChange={(e) => setEditBlockUploadConfig(prev => ({
                                                        ...prev,
                                                        acceptedFileTypes: e.target.checked
                                                            ? [...prev.acceptedFileTypes, ft]
                                                            : prev.acceptedFileTypes.filter(t => t !== ft)
                                                    }))}
                                                    className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                                />
                                                {ft.charAt(0).toUpperCase() + ft.slice(1)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="flex items-center gap-2 text-xs text-orange-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editBlockUploadConfig.requiresAdminVerification}
                                            onChange={(e) => setEditBlockUploadConfig(prev => ({ ...prev, requiresAdminVerification: e.target.checked }))}
                                            className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                        />
                                        <Shield className="w-3 h-3" /> Requires Admin Verification
                                    </label>
                                </div>
                                <div>
                                    <label className="flex items-center gap-2 text-xs text-orange-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editBlockUploadConfig.aiVerificationEnabled}
                                            onChange={(e) => setEditBlockUploadConfig(prev => ({ ...prev, aiVerificationEnabled: e.target.checked }))}
                                            className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                        />
                                        <Eye className="w-3 h-3" /> AI Content Verification
                                    </label>
                                    <p className="text-xs text-orange-500 mt-1 ml-5">When enabled, uploaded files are checked by AI against the block description to verify relevance.</p>
                                </div>
                            </div>
                        )}

                        {/* Practical Config */}
                        {editBlockType === 'practical' && (
                            <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 space-y-4">
                                <h4 className="text-sm font-bold text-orange-800 flex items-center gap-2"><Truck className="w-4 h-4" /> Practical Configuration</h4>
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-orange-700 uppercase">Authorized Evaluator Roles</h5>
                                    <div className="flex gap-4">
                                        {([['assessor', 'Assessor'], ['lead_hand', 'Lead Hand'], ['site_manager', 'Site Manager']] as const).map(([role, label]) => (
                                            <label key={role} className="flex items-center gap-2 text-xs text-orange-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={editBlockPracticalConfig.authorizedRoles.includes(role)}
                                                    onChange={() => practicalToggleRole(setEditBlockPracticalConfig, role)}
                                                    className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-orange-700 uppercase flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Competency Rubric</h5>
                                    {editBlockPracticalConfig.rubric.map((item, idx) => (
                                        <div key={item.id} className="bg-white border border-orange-200 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-orange-700">Item {idx + 1}</span>
                                                <button onClick={() => practicalRemoveRubricItem(setEditBlockPracticalConfig, item.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                            </div>
                                            <input type="text" value={item.title} onChange={(e) => practicalUpdateRubricItem(setEditBlockPracticalConfig, item.id, 'title', e.target.value)} className="w-full border border-orange-200 rounded px-2 py-1 text-xs" placeholder="Competency title..." />
                                            <textarea value={item.description} onChange={(e) => practicalUpdateRubricItem(setEditBlockPracticalConfig, item.id, 'description', e.target.value)} className="w-full border border-orange-200 rounded px-2 py-1 text-xs" rows={1} placeholder="Description / criteria..." />
                                        </div>
                                    ))}
                                    <button onClick={() => practicalAddRubricItem(setEditBlockPracticalConfig)} className="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Rubric Item</button>
                                </div>
                            </div>
                        )}

                        {/* Assessment Config */}
                        {editBlockType === 'assessment' && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
                                <h4 className="text-sm font-bold text-red-800 flex items-center gap-2"><Shield className="w-4 h-4" /> Assessment Configuration</h4>
                                <div className="flex gap-4 flex-wrap">
                                    <div>
                                        <label className="block text-xs font-medium text-red-700 mb-1">Passing Threshold (%)</label>
                                        <input type="number" min="0" max="100" value={editBlockAssessmentConfig.passingThreshold} onChange={(e) => setEditBlockAssessmentConfig(prev => ({ ...prev, passingThreshold: parseInt(e.target.value) || 0 }))} className="w-20 border border-red-200 rounded-lg px-3 py-1.5 text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-red-700 mb-1">Max Attempts</label>
                                        <input type="number" min="0" value={editBlockAssessmentConfig.maxAttempts} onChange={(e) => setEditBlockAssessmentConfig(prev => ({ ...prev, maxAttempts: parseInt(e.target.value) || 0 }))} className="w-20 border border-red-200 rounded-lg px-3 py-1.5 text-sm" />
                                        <p className="text-[10px] text-red-500 mt-0.5">0 = unlimited</p>
                                    </div>
                                    <div className="flex items-end pb-1">
                                        <label className="flex items-center gap-2 text-xs text-red-700 cursor-pointer">
                                            <input type="checkbox" checked={editBlockAssessmentConfig.requiresManualGrading} onChange={(e) => setEditBlockAssessmentConfig(prev => ({ ...prev, requiresManualGrading: e.target.checked }))} className="rounded border-red-300 text-red-600 focus:ring-red-500" />
                                            Requires Manual Grading
                                        </label>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <h5 className="text-xs font-bold text-red-700 uppercase flex items-center gap-1"><Hash className="w-3 h-3" /> Questions</h5>
                                    {editBlockAssessmentConfig.questions.map((q, idx) => (
                                        <div key={q.id} className="bg-white border border-red-200 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-red-700">Question {idx + 1}</span>
                                                <button onClick={() => assessmentRemoveQuestion(setEditBlockAssessmentConfig, q.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                            </div>
                                            <select value={q.type} onChange={(e) => assessmentUpdateQuestion(setEditBlockAssessmentConfig, q.id, 'type', e.target.value)} className="border border-red-200 rounded px-2 py-1 text-xs">
                                                <option value="multiple_choice">Multiple Choice</option>
                                                <option value="short_answer">Short Answer</option>
                                                <option value="true_false">True / False</option>
                                            </select>
                                            <textarea value={q.text} onChange={(e) => assessmentUpdateQuestion(setEditBlockAssessmentConfig, q.id, 'text', e.target.value)} className="w-full border border-red-200 rounded px-2 py-1 text-xs" rows={2} placeholder="Question text..." />
                                            {q.type === 'multiple_choice' && (
                                                <div className="space-y-1 pl-2">
                                                    <span className="text-[10px] text-red-600 font-medium">Options:</span>
                                                    {(q.options || []).map((opt, oi) => (
                                                        <div key={oi} className="flex items-center gap-2">
                                                            <input type="radio" name={`edit_correct_${q.id}`} checked={q.correctAnswer === opt && opt !== ''} onChange={() => assessmentUpdateQuestion(setEditBlockAssessmentConfig, q.id, 'correctAnswer', opt)} className="text-red-600 focus:ring-red-500" />
                                                            <input type="text" value={opt} onChange={(e) => assessmentUpdateOption(setEditBlockAssessmentConfig, q.id, oi, e.target.value)} className="flex-1 border border-red-200 rounded px-2 py-1 text-xs" placeholder={`Option ${oi + 1}`} />
                                                            <button onClick={() => assessmentRemoveOption(setEditBlockAssessmentConfig, q.id, oi)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                                        </div>
                                                    ))}
                                                    <button onClick={() => assessmentAddOption(setEditBlockAssessmentConfig, q.id)} className="text-[10px] text-red-600 hover:text-red-800 flex items-center gap-1"><Plus className="w-2 h-2" /> Add Option</button>
                                                </div>
                                            )}
                                            {q.type === 'true_false' && (
                                                <div className="flex gap-4 pl-2">
                                                    <span className="text-[10px] text-red-600 font-medium">Correct answer:</span>
                                                    {['True', 'False'].map(val => (
                                                        <label key={val} className="flex items-center gap-1 text-xs text-red-700 cursor-pointer">
                                                            <input type="radio" name={`edit_tf_${q.id}`} checked={q.correctAnswer === val} onChange={() => assessmentUpdateQuestion(setEditBlockAssessmentConfig, q.id, 'correctAnswer', val)} className="text-red-600 focus:ring-red-500" />
                                                            {val}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button onClick={() => assessmentAddQuestion(setEditBlockAssessmentConfig)} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Question</button>
                                </div>
                            </div>
                        )}

                        {/* Authorisation Config */}
                        {editBlockType === 'authorisation' && (
                            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-4">
                                <h4 className="text-sm font-bold text-amber-800 flex items-center gap-2"><Stamp className="w-4 h-4" /> Official Authorisation Configuration</h4>
                                <p className="text-[10px] text-amber-600">Specify which roles have the authority to review the entire pathway and grant final sign-off.</p>
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-amber-700 uppercase">Authorized Sign-off Roles</h5>
                                    <div className="flex flex-col gap-2">
                                        {([['site_manager', 'Site Manager'], ['operations_director', 'Operations Director'], ['training_manager', 'Training Manager'], ['custom', 'Custom Role']] as const).map(([role, label]) => (
                                            <label key={role} className="flex items-center gap-2 text-xs text-amber-700 cursor-pointer">
                                                <input type="checkbox" checked={editBlockAuthorisationConfig.authorizedRoles.includes(role)} onChange={() => authorisationToggleRole(setEditBlockAuthorisationConfig, role)} className="rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                    {editBlockAuthorisationConfig.authorizedRoles.includes('custom') && (
                                        <div className="mt-2">
                                            <label className="block text-xs font-medium text-amber-700 mb-1">Custom Role Label</label>
                                            <input type="text" value={editBlockAuthorisationConfig.customRoleLabel || ''} onChange={(e) => setEditBlockAuthorisationConfig(prev => ({ ...prev, customRoleLabel: e.target.value }))} className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm" placeholder="e.g., Quality Assurance Lead" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* TR Prerequisite Config */}
                        {editBlockType === 'theory' && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                                <h4 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" /> TrainingRoom Prerequisite</h4>
                                <p className="text-xs text-blue-600">Select the course template this block must complete before progressing.</p>
                                <select
                                    value={editBlockCourseTemplateId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setEditBlockCourseTemplateId(id);
                                        const tmpl = courseTemplates.find(t => t.id === id);
                                        setEditBlockCourseTemplateName(tmpl?.name || '');
                                        if (tmpl?.name) setEditBlockName(tmpl.name);
                                    }}
                                    className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                >
                                    <option value="">Select a course template...</option>
                                    {courseTemplates.filter(t => t.id === editBlockCourseTemplateId || !pathway.blocks.some(b => b.type === 'theory' && b.courseTemplateId === t.id)).map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                {editBlockCourseTemplateName && (
                                    <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-100 px-3 py-2 rounded-lg">
                                        <CheckCircle2 className="w-3 h-3" /> Linked to: {editBlockCourseTemplateName}
                                    </div>
                                )}
                                {courseTemplates.length === 0 && (
                                    <p className="text-xs text-blue-400 italic">No course templates found. Make sure they exist in TrainingRoom.</p>
                                )}
                            </div>
                        )}

                        {/* OJT Config */}
                        {editBlockType === 'ojt' && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
                                <h4 className="text-sm font-bold text-green-800 flex items-center gap-2"><Crosshair className="w-4 h-4" /> OJT Configuration</h4>

                                {/* Handshake */}
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><Fingerprint className="w-3 h-3" /> Handshake Verification</h5>
                                    <p className="text-[10px] text-green-600">How the lead hand verifies they are present with the candidate.</p>
                                    <div className="flex gap-4">
                                        {(['pin', 'selfie', 'qrCode'] as const).map(method => (
                                            <label key={method} className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={editBlockOjtConfig.handshake[method]}
                                                    onChange={(e) => setEditBlockOjtConfig(prev => ({
                                                        ...prev,
                                                        handshake: { ...prev.handshake, [method]: e.target.checked }
                                                    }))}
                                                    className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                                />
                                                {method === 'pin' ? 'PIN Code' : method === 'selfie' ? 'Selfie' : 'QR Code'}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Expected Duration */}
                                <div>
                                    <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><Clock className="w-3 h-3" /> Expected Duration</h5>
                                    <div className="flex items-center gap-2 mt-1">
                                        <input
                                            type="number"
                                            min="0.5"
                                            step="0.5"
                                            value={editBlockOjtConfig.expectedDurationHours}
                                            onChange={(e) => setEditBlockOjtConfig(prev => ({ ...prev, expectedDurationHours: parseFloat(e.target.value) || 1 }))}
                                            className="w-20 border border-green-200 rounded-lg px-3 py-1.5 text-sm"
                                        />
                                        <span className="text-xs text-green-600">hours</span>
                                    </div>
                                </div>

                                {/* Location Tracking */}
                                <div>
                                    <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editBlockOjtConfig.locationTrackingEnabled}
                                            onChange={(e) => setEditBlockOjtConfig(prev => ({ ...prev, locationTrackingEnabled: e.target.checked }))}
                                            className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                        />
                                        <MapPin className="w-3 h-3" /> Enable Location Tracking
                                    </label>
                                </div>

                                {/* Candidate Comments */}
                                <div>
                                    <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editBlockOjtConfig.candidateCommentsEnabled}
                                            onChange={(e) => setEditBlockOjtConfig(prev => ({ ...prev, candidateCommentsEnabled: e.target.checked }))}
                                            className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                        />
                                        <Edit2 className="w-3 h-3" /> Allow Candidate Comments
                                    </label>
                                </div>

                                {/* Pre-Start Checklist */}
                                <div className="space-y-2">
                                    <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><ListChecks className="w-3 h-3" /> Pre-Start Checklist</h5>
                                    {editBlockOjtConfig.preStartChecklist.map((item, idx) => (
                                        <div key={item.id} className="flex items-center gap-2">
                                            <span className="text-[10px] text-green-500 w-4">{idx + 1}.</span>
                                            <input
                                                type="text"
                                                value={item.label}
                                                onChange={(e) => updatePreStartChecklistItem(item.id, 'label', e.target.value)}
                                                className="flex-1 border border-green-200 rounded px-2 py-1 text-xs"
                                                placeholder="Checklist item..."
                                            />
                                            <label className="flex items-center gap-1 text-[10px] text-green-600">
                                                <input
                                                    type="checkbox"
                                                    checked={item.required}
                                                    onChange={(e) => updatePreStartChecklistItem(item.id, 'required', e.target.checked)}
                                                    className="rounded border-green-300 text-green-600 w-3 h-3"
                                                />
                                                Req
                                            </label>
                                            <button onClick={() => removePreStartChecklistItem(item.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                        </div>
                                    ))}
                                    <button onClick={addPreStartChecklistItem} className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Checklist Item</button>
                                </div>

                                {/* Objectives */}
                                <div className="space-y-3">
                                    <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><Target className="w-3 h-3" /> Objectives</h5>
                                    {editBlockOjtConfig.objectives.map((obj, idx) => (
                                        <div key={obj.id} className="bg-white border border-green-200 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-green-700">Objective {idx + 1}</span>
                                                <button onClick={() => removeOjtObjective(obj.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                            </div>
                                            <input
                                                type="text"
                                                value={obj.title}
                                                onChange={(e) => updateOjtObjective(obj.id, 'title', e.target.value)}
                                                className="w-full border border-green-200 rounded px-2 py-1 text-xs"
                                                placeholder="Objective title..."
                                            />
                                            <textarea
                                                value={obj.description || ''}
                                                onChange={(e) => updateOjtObjective(obj.id, 'description', e.target.value)}
                                                className="w-full border border-green-200 rounded px-2 py-1 text-xs"
                                                rows={1}
                                                placeholder="Description (optional)..."
                                            />
                                            <div className="flex gap-3">
                                                <span className="text-[10px] text-green-600 font-medium">Evidence:</span>
                                                {(['photo', 'signature', 'comment'] as const).map(ev => (
                                                    <label key={ev} className="flex items-center gap-1 text-[10px] text-green-600 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={obj.evidence[ev]}
                                                            onChange={(e) => updateOjtObjectiveEvidence(obj.id, ev, e.target.checked)}
                                                            className="rounded border-green-300 text-green-600 w-3 h-3"
                                                        />
                                                        {ev.charAt(0).toUpperCase() + ev.slice(1)}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    <button onClick={addOjtObjective} className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Objective</button>
                                </div>

                                {/* OJT Modes */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h5 className="text-xs font-bold text-green-700 uppercase flex items-center gap-1"><Layers className="w-3 h-3" /> Modes</h5>
                                        <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={!!(editBlockOjtConfig.modes && editBlockOjtConfig.modes.length > 0)}
                                                onChange={(e) => e.target.checked ? ojtEnableModes(setEditBlockOjtConfig) : ojtDisableModes(setEditBlockOjtConfig)}
                                                className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                            />
                                            Enable modes
                                        </label>
                                    </div>
                                    {editBlockOjtConfig.modes && editBlockOjtConfig.modes.length > 0 && (
                                        <>
                                            <p className="text-[10px] text-green-600">Candidates can switch between modes during a session. Each mode tracks time independently. Required minutes are informational only.</p>
                                            {editBlockOjtConfig.modes.map((mode, idx) => (
                                                <div key={mode.id} className="bg-white border border-green-200 rounded-lg p-3 space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="color"
                                                            value={mode.color}
                                                            onChange={(e) => ojtUpdateMode(setEditBlockOjtConfig, mode.id, 'color', e.target.value)}
                                                            className="w-8 h-8 rounded cursor-pointer border border-green-200 flex-shrink-0"
                                                            title="Mode colour"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={mode.label}
                                                            onChange={(e) => ojtUpdateMode(setEditBlockOjtConfig, mode.id, 'label', e.target.value)}
                                                            className="flex-1 border border-green-200 rounded px-2 py-1 text-xs"
                                                            placeholder="Mode name..."
                                                            disabled={mode.isBuiltIn}
                                                        />
                                                        {!mode.isBuiltIn && (
                                                            <button onClick={() => ojtRemoveMode(setEditBlockOjtConfig, mode.id)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                                        )}
                                                        {mode.isBuiltIn && <span className="text-[9px] text-green-500 uppercase font-bold">Default</span>}
                                                    </div>
                                                    <div className="flex items-center gap-2 pl-10">
                                                        <span className="text-[10px] text-green-600">Min minutes (optional):</span>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={mode.requiredMinutes ?? ''}
                                                            onChange={(e) => {
                                                                const v = e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value, 10) || 0);
                                                                ojtUpdateMode(setEditBlockOjtConfig, mode.id, 'requiredMinutes', v);
                                                            }}
                                                            className="w-20 border border-green-200 rounded px-2 py-0.5 text-xs"
                                                            placeholder="—"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            <button onClick={() => ojtAddCustomMode(setEditBlockOjtConfig)} className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"><Plus className="w-3 h-3" /> Add Custom Mode</button>

                                            {/* Mode Change Sign-Off */}
                                            <div className="border-t border-green-200 pt-3 mt-3 space-y-2">
                                                <label className="flex items-center gap-2 text-xs text-green-700 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!editBlockOjtConfig.modeSignOff?.required}
                                                        onChange={(e) => ojtUpdateModeSignOff(setEditBlockOjtConfig, 'required', e.target.checked)}
                                                        className="rounded border-green-300 text-green-600 focus:ring-green-500"
                                                    />
                                                    Require lead-hand sign-off when switching modes
                                                </label>
                                                {editBlockOjtConfig.modeSignOff?.required && (
                                                    <div className="flex items-center gap-2 pl-6">
                                                        <span className="text-[10px] text-green-600">Method:</span>
                                                        <select
                                                            value={editBlockOjtConfig.modeSignOff?.method || 'pin'}
                                                            onChange={(e) => ojtUpdateModeSignOff(setEditBlockOjtConfig, 'method', e.target.value)}
                                                            className="border border-green-200 rounded px-2 py-1 text-xs"
                                                        >
                                                            <option value="pin">PIN</option>
                                                            <option value="signature">Signature</option>
                                                            <option value="selfie">Selfie</option>
                                                            <option value="qr">QR Code</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Agent-led OJT (beta) */}
                                <OjtAgentPilotPanel config={editBlockOjtConfig} setConfig={setEditBlockOjtConfig} showTestTokenHelper />
                            </div>
                        )}

                        {/* Save / Cancel */}
                        </div>
                        <div className="flex gap-3 pt-3 border-t border-aero-100 bg-white flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => { setIsEditBlockOpen(false); setEditingBlock(null); }}
                                className="flex-1 px-4 py-2 border border-aero-200 text-aero-700 rounded-lg hover:bg-aero-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveEditBlock}
                                disabled={!editBlockName.trim()}
                                className="flex-1 px-4 py-2 bg-aero-800 text-white rounded-lg hover:bg-aero-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <Save className="w-4 h-4" /> Save Changes
                            </button>
                        </div>
                    </div>
                </Modal>
        </div>
    );
};

const TRACK_LABELS: Record<string, { label: string; badge: string }> = {
    STANDARD:    { label: 'Standard (Novice)',   badge: 'bg-blue-100 text-blue-700' },
    ACCELERATED: { label: 'Accelerated (RPL)',   badge: 'bg-purple-100 text-purple-700' },
    REFRESHER:   { label: 'Refresher (Renewal)', badge: 'bg-orange-100 text-orange-700' },
    REMEDIAL:    { label: 'Remedial',            badge: 'bg-red-100 text-red-700' },
};

// Helper: group blocks into sequential levels (parallel blocks share the same horizontalGroup)
function groupBlocksForReview(blocks: PathwayBlock[]): PathwayBlock[][] {
    const groups: PathwayBlock[][] = [];
    const seen = new Set<string>();
    for (const block of blocks) {
        if (seen.has(block.id)) continue;
        if (block.horizontalGroup) {
            const group = blocks
                .filter(b => b.horizontalGroup === block.horizontalGroup)
                .sort((a, b) => (a.horizontalPosition || 0) - (b.horizontalPosition || 0));
            group.forEach(b => seen.add(b.id));
            groups.push(group);
        } else {
            seen.add(block.id);
            groups.push([block]);
        }
    }
    return groups;
}

const AdminBlockReviewPanel: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    pathway: Pathway;
    candidate: PathwayProgress | null;
    blockProgressMap: Map<string, string>;
    isLoadingProgress: boolean;
}> = ({ isOpen, onClose, pathway, candidate, blockProgressMap, isLoadingProgress }) => {
    const [evidence, setEvidence] = useState<Map<string, AssessmentAttempt[] | BlockUpload | PracticalEvaluation | PathwayAuthorisation | OjtSession | null>>(new Map());
    const [isLoadingEvidence, setIsLoadingEvidence] = useState(false);
    const [localProgressMap, setLocalProgressMap] = useState<Map<string, string>>(new Map());
    const [signOffConfirm, setSignOffConfirm] = useState<{ levelIdx: number; blocks: PathwayBlock[] } | null>(null);
    const [isSigningOff, setIsSigningOff] = useState(false);
    const [localLevelReviews, setLocalLevelReviews] = useState<Record<string, LevelReviewEntry>>({});
    const [levelRejectTarget, setLevelRejectTarget] = useState<{ groupKey: string; levelIdx: number } | null>(null);
    const [levelRejectReason, setLevelRejectReason] = useState('');
    const [isRejectingLevel, setIsRejectingLevel] = useState(false);
    const [uploadActionBlockId, setUploadActionBlockId] = useState<string | null>(null);
    const [uploadRejectTarget, setUploadRejectTarget] = useState<{ blockId: string; upload: BlockUpload } | null>(null);
    const [uploadRejectReason, setUploadRejectReason] = useState('');

    useEffect(() => {
        setLocalProgressMap(new Map(blockProgressMap));
    }, [blockProgressMap]);

    useEffect(() => {
        setLocalLevelReviews(candidate?.levelReviews || {});
    }, [candidate?.candidateId, candidate?.levelReviews]);

    useEffect(() => {
        if (!isOpen || !candidate) return;
        let cancelled = false;
        const pathwayId = pathway.rootId || pathway.id;
        const load = async () => {
            setIsLoadingEvidence(true);
            const map = new Map<string, any>();
            const blocksToLoad = pathway.blocks.filter(b => {
                const s = blockProgressMap.get(b.id);
                return s === 'in_progress' || s === 'complete';
            });
            await Promise.all(blocksToLoad.map(async block => {
                try {
                    if (block.type === 'ojt') {
                        map.set(block.id, await DataService.getOjtSessionForBlock(candidate.candidateId, pathwayId, block.id));
                    } else if (block.type === 'upload' || block.type === 'affidavit') {
                        map.set(block.id, await DataService.getBlockUpload(candidate.candidateId, pathwayId, block.id));
                    } else if (block.type === 'assessment') {
                        map.set(block.id, await DataService.getAssessmentAttempts(candidate.candidateId, pathwayId, block.id));
                    } else if (block.type === 'practical') {
                        map.set(block.id, await DataService.getPracticalEvaluation(candidate.candidateId, pathwayId, block.id));
                    } else if (block.type === 'authorisation') {
                        map.set(block.id, await DataService.getPathwayAuthorisation(candidate.candidateId, pathwayId, block.id));
                    }
                } catch { /* ignore per-block evidence errors */ }
            }));
            if (!cancelled) { setEvidence(map); setIsLoadingEvidence(false); }
        };
        load();
        return () => { cancelled = true; };
    }, [isOpen, candidate?.candidateId]);

    if (!candidate) return null;

    const pathwayId = pathway.rootId || pathway.id;
    const trackBlocks = pathway.blocks.filter(b =>
        !b.branchId || b.branchId === candidate.selectedTrack || b.branchId === 'MERGE'
    );
    const groups = groupBlocksForReview(trackBlocks);

    const handleSignOff = async () => {
        if (!signOffConfirm) return;
        // Guard: only sign off when every block in the level is already complete and the level is 'submitted'.
        const signOffGroupKey = `level_${signOffConfirm.levelIdx}`;
        const signOffReview = localLevelReviews[signOffGroupKey];
        const signOffAllComplete = signOffConfirm.blocks.every(
            b => (localProgressMap.get(b.id) || 'not_started') === 'complete'
        );
        if (signOffReview?.status !== 'submitted' || !signOffAllComplete) {
            alert('Cannot approve: level must be submitted and all blocks complete.');
            return;
        }
        setIsSigningOff(true);
        try {
            const completedAt = Date.now();
            await Promise.all(signOffConfirm.blocks.map(block =>
                DataService.setBlockProgress({
                    candidateId: candidate.candidateId,
                    pathwayId,
                    blockId: block.id,
                    status: 'complete',
                    completedAt,
                })
            ));
            const updated = new Map(localProgressMap);
            signOffConfirm.blocks.forEach(b => updated.set(b.id, 'complete'));
            setLocalProgressMap(updated);
            const relevantBlockIds = new Set(trackBlocks.map(b => b.id));
            const totalBlocks = relevantBlockIds.size;
            const completedCount = [...updated.entries()].filter(
                ([blockId, status]) => relevantBlockIds.has(blockId) && status === 'complete'
            ).length;
            const newProgress = totalBlocks > 0 ? Math.round((completedCount / totalBlocks) * 100) : 0;
            const groupKey = `level_${signOffConfirm.levelIdx}`;
            const currentUser = auth.currentUser;
            const reviewEntry: LevelReviewEntry = {
                status: 'approved',
                reviewedAt: Date.now(),
                reviewedBy: currentUser?.uid || 'unknown',
                reviewedByName: currentUser?.displayName || currentUser?.email || 'Admin',
            };
            const nextReviews = { ...localLevelReviews, [groupKey]: reviewEntry };
            await DataService.updatePathwayProgress(candidate.candidateId, pathwayId, {
                progress: newProgress,
                status: newProgress === 100 ? 'COMPLETED' : 'IN_PROGRESS',
                lastActivity: Date.now(),
                levelReviews: nextReviews,
            });
            setLocalLevelReviews(nextReviews);
            setSignOffConfirm(null);
        } catch (e) {
            console.error('Sign off failed', e);
            alert('Sign off failed. Please try again.');
        } finally {
            setIsSigningOff(false);
        }
    };

    const handleRejectLevel = async () => {
        if (!levelRejectTarget) return;
        const reason = levelRejectReason.trim();
        if (!reason) return;
        setIsRejectingLevel(true);
        try {
            const currentUser = auth.currentUser;
            const reviewEntry: LevelReviewEntry = {
                status: 'rejected',
                reviewedAt: Date.now(),
                reviewedBy: currentUser?.uid || 'unknown',
                reviewedByName: currentUser?.displayName || currentUser?.email || 'Admin',
                rejectionReason: reason,
            };
            const nextReviews = { ...localLevelReviews, [levelRejectTarget.groupKey]: reviewEntry };
            await DataService.updatePathwayProgress(candidate.candidateId, pathwayId, {
                lastActivity: Date.now(),
                levelReviews: nextReviews,
            });
            setLocalLevelReviews(nextReviews);
            setLevelRejectTarget(null);
            setLevelRejectReason('');
        } catch (e) {
            console.error('Reject level failed', e);
            alert('Reject failed. Please try again.');
        } finally {
            setIsRejectingLevel(false);
        }
    };

    const handleApproveUpload = async (block: PathwayBlock, upload: BlockUpload) => {
        if (!upload.id) return;
        setUploadActionBlockId(block.id);
        try {
            const currentUser = auth.currentUser;
            const now = Date.now();
            const updates: Partial<BlockUpload> = {
                status: 'approved',
                reviewedBy: currentUser?.uid || 'unknown',
                reviewedByName: currentUser?.displayName || currentUser?.email || 'Admin',
                reviewedAt: now,
            };
            await DataService.updateBlockUpload(upload.id, updates);
            await DataService.setBlockProgress({
                candidateId: candidate.candidateId,
                pathwayId,
                blockId: block.id,
                status: 'complete',
                completedAt: now,
            });
            const nextProgressMap = new Map(localProgressMap);
            nextProgressMap.set(block.id, 'complete');
            const approveRelevantIds = new Set(trackBlocks.map(b => b.id));
            const approveCompleted = [...nextProgressMap.entries()].filter(
                ([id, status]) => approveRelevantIds.has(id) && status === 'complete'
            ).length;
            const approveProgress = approveRelevantIds.size > 0
                ? Math.round((approveCompleted / approveRelevantIds.size) * 100)
                : 0;
            await DataService.updatePathwayProgress(candidate.candidateId, pathwayId, {
                progress: approveProgress,
                status: approveProgress === 100 ? 'COMPLETED' : 'IN_PROGRESS',
                lastActivity: now,
            });
            setEvidence(prev => {
                const next = new Map(prev);
                next.set(block.id, { ...upload, ...updates });
                return next;
            });
            setLocalProgressMap(prev => {
                const next = new Map(prev);
                next.set(block.id, 'complete');
                return next;
            });
        } catch (e) {
            console.error('Approve upload failed', e);
            alert('Approve failed. Please try again.');
        } finally {
            setUploadActionBlockId(null);
        }
    };

    const handleRejectUpload = async () => {
        if (!uploadRejectTarget) return;
        const reason = uploadRejectReason.trim();
        if (!reason) return;
        const { blockId, upload } = uploadRejectTarget;
        if (!upload.id) return;
        setUploadActionBlockId(blockId);
        try {
            const currentUser = auth.currentUser;
            const updates: Partial<BlockUpload> = {
                status: 'rejected',
                rejectionReason: reason,
                reviewedBy: currentUser?.uid || 'unknown',
                reviewedByName: currentUser?.displayName || currentUser?.email || 'Admin',
                reviewedAt: Date.now(),
            };
            await DataService.updateBlockUpload(upload.id, updates);
            await DataService.setBlockProgress({
                candidateId: candidate.candidateId,
                pathwayId,
                blockId,
                status: 'in_progress',
            });
            setEvidence(prev => {
                const next = new Map(prev);
                next.set(blockId, { ...upload, ...updates });
                return next;
            });
            setLocalProgressMap(prev => {
                const next = new Map(prev);
                next.set(blockId, 'in_progress');
                return next;
            });
            setUploadRejectTarget(null);
            setUploadRejectReason('');
        } catch (e) {
            console.error('Reject upload failed', e);
            alert('Reject failed. Please try again.');
        } finally {
            setUploadActionBlockId(null);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`${candidate.candidateName} — Block Review`} maxWidth="max-w-3xl">
            {/* Candidate sub-header */}
            <div className="flex items-center gap-3 mb-4">
                {candidate.selectedTrack && TRACK_LABELS[candidate.selectedTrack] && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${TRACK_LABELS[candidate.selectedTrack].badge}`}>
                        {TRACK_LABELS[candidate.selectedTrack].label}
                    </span>
                )}
                <div className="flex-1 bg-aero-100 rounded-full h-2">
                    <div
                        className={`h-2 rounded-full transition-all duration-500 ${candidate.progress === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${candidate.progress}%` }}
                    />
                </div>
                <span className="text-xs font-bold text-aero-600 w-10 text-right">{candidate.progress}%</span>
            </div>

            {(isLoadingProgress || isLoadingEvidence) ? (
                <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600" />
                </div>
            ) : (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                    {groups.map((group, gIdx) => {
                        const groupKey = `level_${gIdx}`;
                        const levelReview = localLevelReviews[groupKey];
                        const allComplete = group.every(b => (localProgressMap.get(b.id) || 'not_started') === 'complete');
                        const anyStarted = group.some(b => {
                            const s = localProgressMap.get(b.id) || 'not_started';
                            return s !== 'not_started';
                        });
                        const isApproved = levelReview?.status === 'approved';
                        const isSubmitted = levelReview?.status === 'submitted';
                        const isRejected = levelReview?.status === 'rejected';
                        const canReview = isSubmitted && allComplete;
                        return (
                            <div key={gIdx} className="border border-aero-200 rounded-lg overflow-hidden">
                                {/* Awaiting review ribbon */}
                                {isSubmitted && (
                                    <div className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-50 border-b border-amber-200 text-[11px] font-bold text-amber-800">
                                        <Clock className="w-3.5 h-3.5" /> Awaiting your review
                                    </div>
                                )}
                                {/* Level header */}
                                <div className="flex items-center justify-between px-4 py-2 bg-aero-50 border-b border-aero-200">
                                    <span className="text-xs font-bold text-aero-600 uppercase tracking-wide">
                                        Level {gIdx + 1}{group.length > 1 ? ` (${group.length} parallel blocks)` : ''}
                                    </span>
                                    {isApproved ? (
                                        <span className="flex items-center gap-1 text-[10px] font-bold text-green-600">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Approved
                                        </span>
                                    ) : isRejected ? (
                                        <div className="flex items-center gap-2">
                                            <span className="flex items-center gap-1 text-[10px] font-bold text-red-600">
                                                <XCircle className="w-3.5 h-3.5" /> Rejected
                                            </span>
                                            {canReview && (
                                                <button
                                                    onClick={() => setSignOffConfirm({ levelIdx: gIdx, blocks: group })}
                                                    className="flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 border border-green-300 rounded text-[10px] font-bold hover:bg-green-200 transition-colors"
                                                >
                                                    Approve Level {gIdx + 1}
                                                </button>
                                            )}
                                        </div>
                                    ) : canReview ? (
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setSignOffConfirm({ levelIdx: gIdx, blocks: group })}
                                                className="flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 border border-green-300 rounded text-[10px] font-bold hover:bg-green-200 transition-colors"
                                            >
                                                Approve Level {gIdx + 1}
                                            </button>
                                            <button
                                                onClick={() => { setLevelRejectTarget({ groupKey, levelIdx: gIdx }); setLevelRejectReason(''); }}
                                                className="flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-800 border border-red-300 rounded text-[10px] font-bold hover:bg-red-200 transition-colors"
                                            >
                                                Reject Level {gIdx + 1}
                                            </button>
                                        </div>
                                    ) : anyStarted ? (
                                        <span className="text-[10px] text-aero-400">In progress</span>
                                    ) : (
                                        <span className="text-[10px] text-aero-400">Not started</span>
                                    )}
                                </div>

                                {/* Blocks in level */}
                                <div className="divide-y divide-aero-100">
                                    {group.map(block => {
                                        const status = localProgressMap.get(block.id) || 'not_started';
                                        const ev = evidence.get(block.id);
                                        return (
                                            <div key={block.id} className="px-4 py-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    {renderBlockIcon(block.type)}
                                                    <span className="text-xs font-semibold text-aero-900">{block.name}</span>
                                                    <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                                        status === 'complete'    ? 'bg-green-100 text-green-700' :
                                                        status === 'in_progress' ? 'bg-blue-50 text-blue-700' :
                                                                                   'bg-gray-100 text-gray-400'
                                                    }`}>{status.replace('_', ' ')}</span>
                                                </div>
                                                {/* Evidence details */}
                                                {ev && (
                                                    <div className="mt-1.5 text-[11px] text-aero-600 space-y-0.5 pl-6">
                                                        {block.type === 'ojt' && (() => {
                                                            const s = ev as OjtSession;
                                                            const totalObj = s.objectives?.length ?? 0;
                                                            const doneObj = s.objectives?.filter(o => o.isCompleted).length ?? 0;
                                                            return (
                                                                <>
                                                                    <div>Session: <span className="font-medium">{s.status}</span> · Lead hand: <span className="font-medium">{s.leadHandName}</span></div>
                                                                    {totalObj > 0 && <div>Objectives: <span className="font-medium">{doneObj}/{totalObj}</span></div>}
                                                                    {s.handshakeVerified && <div className="text-green-600 font-medium">✓ Handshake verified</div>}
                                                                </>
                                                            );
                                                        })()}
                                                        {(block.type === 'upload' || block.type === 'affidavit') && (() => {
                                                            const u = ev as BlockUpload;
                                                            const isBusy = uploadActionBlockId === block.id;
                                                            return (
                                                                <>
                                                                    <div><a href={u.fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">{u.fileName}</a></div>
                                                                    <div>Uploaded: {new Date(u.uploadedAt).toLocaleDateString()} · Status: <span className={`font-medium ${u.status === 'approved' ? 'text-green-600' : u.status === 'rejected' ? 'text-red-600' : 'text-amber-600'}`}>{u.status.replace('_', ' ')}</span></div>
                                                                    {u.status === 'rejected' && u.rejectionReason && (
                                                                        <div className="italic text-red-500">"{u.rejectionReason}"</div>
                                                                    )}
                                                                    {u.status === 'pending_review' && (
                                                                        <div className="flex items-center gap-2 mt-1">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleApproveUpload(block, u)}
                                                                                disabled={isBusy}
                                                                                className="px-2 py-0.5 bg-green-100 text-green-800 border border-green-300 rounded text-[10px] font-bold hover:bg-green-200 disabled:opacity-50 transition-colors"
                                                                            >
                                                                                Approve
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => { setUploadRejectTarget({ blockId: block.id, upload: u }); setUploadRejectReason(''); }}
                                                                                disabled={isBusy}
                                                                                className="px-2 py-0.5 bg-red-100 text-red-800 border border-red-300 rounded text-[10px] font-bold hover:bg-red-200 disabled:opacity-50 transition-colors"
                                                                            >
                                                                                Reject
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                        {block.type === 'assessment' && Array.isArray(ev) && (ev as AssessmentAttempt[]).length > 0 && (() => {
                                                            const latest = (ev as AssessmentAttempt[]).sort((a, b) => b.attemptNumber - a.attemptNumber)[0];
                                                            return (
                                                                <div>Attempt {latest.attemptNumber}: <span className={`font-medium ${latest.passed ? 'text-green-600' : 'text-red-600'}`}>{latest.passed ? 'Passed' : 'Failed'}</span>{latest.score !== undefined ? ` · ${latest.score}%` : ''}</div>
                                                            );
                                                        })()}
                                                        {block.type === 'practical' && (() => {
                                                            const p = ev as PracticalEvaluation;
                                                            const passedRubric = p.rubricResults.filter(r => r.passed).length;
                                                            return (
                                                                <div>Evaluator: <span className="font-medium">{p.evaluatorName}</span> · Result: <span className={`font-medium ${p.overallResult === 'pass' ? 'text-green-600' : 'text-red-600'}`}>{p.overallResult}</span> · Rubric: {passedRubric}/{p.rubricResults.length}</div>
                                                            );
                                                        })()}
                                                        {block.type === 'authorisation' && (() => {
                                                            const a = ev as PathwayAuthorisation;
                                                            return (
                                                                <div>Authorised by <span className="font-medium">{a.authoriserName}</span> ({a.authoriserRole}) · {new Date(a.authorisedAt).toLocaleDateString()}</div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                                {!ev && (status === 'in_progress' || status === 'complete') && block.type !== 'theory' && block.type !== 'pathway' && (
                                                    <div className="mt-1 pl-6 text-[11px] text-aero-400 italic">No evidence recorded yet</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Sign-off confirmation overlay */}
            {signOffConfirm && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-aero-900/30 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
                        <h4 className="font-bold text-aero-900 mb-2">Approve Level {signOffConfirm.levelIdx + 1}?</h4>
                        <p className="text-sm text-aero-600 mb-4">
                            Approve <strong>Level {signOffConfirm.levelIdx + 1}</strong> for <strong>{candidate.candidateName}</strong>?{' '}
                            This will mark the block{signOffConfirm.blocks.length > 1 ? 's' : ''} complete and unlock the next level.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setSignOffConfirm(null)}
                                disabled={isSigningOff}
                                className="px-4 py-2 border border-aero-200 text-aero-600 rounded-lg text-sm hover:bg-aero-50 disabled:opacity-50"
                            >Cancel</button>
                            <button
                                onClick={handleSignOff}
                                disabled={isSigningOff}
                                className="flex items-center gap-2 px-4 py-2 bg-aero-800 text-white rounded-lg text-sm hover:bg-aero-700 disabled:opacity-50"
                            >
                                {isSigningOff && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                Confirm Approve
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Level rejection reason modal */}
            {levelRejectTarget && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-aero-900/30 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
                        <h4 className="font-bold text-aero-900 mb-2">Reject Level {levelRejectTarget.levelIdx + 1}?</h4>
                        <p className="text-sm text-aero-600 mb-3">
                            Provide a reason for rejecting this level for <strong>{candidate.candidateName}</strong>.
                        </p>
                        <textarea
                            value={levelRejectReason}
                            onChange={e => setLevelRejectReason(e.target.value)}
                            placeholder="Reason for rejection..."
                            className="w-full px-3 py-2 border border-aero-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400 mb-3"
                            rows={3}
                            autoFocus
                        />
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => { setLevelRejectTarget(null); setLevelRejectReason(''); }}
                                disabled={isRejectingLevel}
                                className="px-4 py-2 border border-aero-200 text-aero-600 rounded-lg text-sm hover:bg-aero-50 disabled:opacity-50"
                            >Cancel</button>
                            <button
                                onClick={handleRejectLevel}
                                disabled={isRejectingLevel || !levelRejectReason.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                            >
                                {isRejectingLevel && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                Confirm Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload rejection reason modal */}
            {uploadRejectTarget && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-aero-900/30 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
                        <h4 className="font-bold text-aero-900 mb-2">Reject Upload?</h4>
                        <p className="text-sm text-aero-600 mb-3">
                            Provide a reason for rejecting <strong>{uploadRejectTarget.upload.fileName}</strong>.
                        </p>
                        <textarea
                            value={uploadRejectReason}
                            onChange={e => setUploadRejectReason(e.target.value)}
                            placeholder="Reason for rejection..."
                            className="w-full px-3 py-2 border border-aero-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400 mb-3"
                            rows={3}
                            autoFocus
                        />
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => { setUploadRejectTarget(null); setUploadRejectReason(''); }}
                                disabled={uploadActionBlockId === uploadRejectTarget.blockId}
                                className="px-4 py-2 border border-aero-200 text-aero-600 rounded-lg text-sm hover:bg-aero-50 disabled:opacity-50"
                            >Cancel</button>
                            <button
                                onClick={handleRejectUpload}
                                disabled={uploadActionBlockId === uploadRejectTarget.blockId || !uploadRejectReason.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                            >
                                {uploadActionBlockId === uploadRejectTarget.blockId && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                Confirm Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

const CandidateListForPathway = ({ pathway, activeOrgId, orgCandidates }: { pathway: Pathway; activeOrgId?: string; orgCandidates: TeamTrainee[] }) => {
    const [candidates, setCandidates] = useState<PathwayProgress[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const [availableCandidates, setAvailableCandidates] = useState<TeamTrainee[]>([]);
    const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([]);
    const [isAssigning, setIsAssigning] = useState(false);
    const [assignSearch, setAssignSearch] = useState('');
    const [assignError, setAssignError] = useState('');
    const [assignSuccess, setAssignSuccess] = useState('');
    const [deletingCandidateId, setDeletingCandidateId] = useState<string | null>(null);
    const [deleteConfirmCandidate, setDeleteConfirmCandidate] = useState<{ id: string; name: string } | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteIncludeSubPathways, setDeleteIncludeSubPathways] = useState(false);
    const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(null);
    const [candidateTrLinks, setCandidateTrLinks] = useState<Record<string, Record<string, BlockTrainingRoomLink | null>>>({});
    const [selectedTrack, setSelectedTrack] = useState<'STANDARD' | 'ACCELERATED' | 'REFRESHER' | 'REMEDIAL'>('STANDARD');
    const [inlineViewerId, setInlineViewerId] = useState<string | null>(null);
    const [inlineBlockProgressMap, setInlineBlockProgressMap] = useState<Map<string, string>>(new Map());
    const [inlineIsLoading, setInlineIsLoading] = useState(false);
    const [pendingSubPathways, setPendingSubPathways] = useState<{ linkedPathwayId: string; blockName: string; tracks: readonly ('STANDARD' | 'ACCELERATED' | 'REFRESHER' | 'REMEDIAL')[]; users: { id: string; name: string }[] }[]>([]);
    const [subPathwayTrackSelections, setSubPathwayTrackSelections] = useState<Record<string, 'STANDARD' | 'ACCELERATED' | 'REFRESHER' | 'REMEDIAL'>>({});
    const [isSubPathwayModalOpen, setIsSubPathwayModalOpen] = useState(false);
    const [isAssigningSubPathways, setIsAssigningSubPathways] = useState(false);
    const [selectedStartingModes, setSelectedStartingModes] = useState<Record<string, string>>({});

    // Derive available tracks from pathway blocks
    const availableTracks = (['STANDARD', 'ACCELERATED', 'REFRESHER', 'REMEDIAL'] as const).filter(t =>
        pathway.blocks.some(b => b.branchId === t)
    );
    const hasTracks = availableTracks.length > 0;

    // OJT blocks in the current selected track that have modes enabled
    const ojtBlocksWithModes = pathway.blocks.filter(b =>
        b.type === 'ojt' &&
        b.ojtConfig?.modes && b.ojtConfig.modes.length > 0 &&
        (!hasTracks || !b.branchId || b.branchId === selectedTrack || b.branchId === 'MERGE')
    );

    // Fingerprint includes block IDs + mode IDs/order so effect reruns when modes change (not just block IDs)
    const ojtModeFingerprint = ojtBlocksWithModes
        .map(block => `${block.id}:${(block.ojtConfig?.modes || []).map(mode => mode.id).join(',')}`)
        .join('|');

    // Default starting mode for each OJT-with-modes block to its first mode whenever the set changes
    useEffect(() => {
        setSelectedStartingModes(prev => {
            const next: Record<string, string> = {};
            for (const block of ojtBlocksWithModes) {
                const firstId = block.ojtConfig?.modes?.[0]?.id;
                if (!firstId) continue;
                next[block.id] = prev[block.id] && block.ojtConfig?.modes?.some(m => m.id === prev[block.id])
                    ? prev[block.id]
                    : firstId;
            }
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ojtModeFingerprint, selectedTrack]);

    const theoryBlocks = pathway.blocks.filter(b => b.type === 'theory' && b.courseTemplateId);

    const toggleExpand = async (candidateId: string) => {
        if (expandedCandidateId === candidateId) {
            setExpandedCandidateId(null);
            return;
        }
        setExpandedCandidateId(candidateId);
        const links: Record<string, BlockTrainingRoomLink | null> = {};
        await Promise.all(theoryBlocks.map(async (block) => {
            const linkData = await DataService.getBlockTrainingRoomLink(candidateId, block.id, pathway.rootId || pathway.id);
            // Use block.courseTemplateId directly — don't rely on linkData having templateId/courseId
            // (old link documents may be missing these fields)
            const templateId = block.courseTemplateId || linkData?.templateId;
            if (linkData && templateId) {
                // Re-validate against live Trainingroom course data on every expand
                const courses = await DataService.getTrainingRoomCourses(candidateId, templateId);
                // Try exact courseId match first, then fall back to any valid complete course
                let matchingCourse = linkData.courseId
                    ? courses.find(c => c.id === linkData.courseId)
                    : undefined;
                if (!matchingCourse) {
                    matchingCourse = courses.find(c => DataService.checkCourseValidity(c).isValid) || undefined;
                }
                if (matchingCourse) {
                    const validity = DataService.checkCourseValidity(matchingCourse);
                    const newStatus = validity.isComplete
                        ? (validity.isValid ? 'complete' : 'expired')
                        : 'in_progress';
                    const courseIdChanged = linkData.courseId && matchingCourse.id !== linkData.courseId;
                    if (linkData.status !== newStatus || courseIdChanged) {
                        const completionDate = validity.isComplete && matchingCourse.signeddate
                            ? (matchingCourse.signeddate.toDate
                                ? matchingCourse.signeddate.toDate().getTime()
                                : new Date(matchingCourse.signeddate).getTime())
                            : undefined;
                        const updates: Partial<BlockTrainingRoomLink> = { status: newStatus };
                        if (completionDate) updates.completionDate = completionDate;
                        if (courseIdChanged) updates.courseId = matchingCourse.id;
                        await DataService.updateBlockTrainingRoomLink(linkData.id, updates);
                        links[block.id] = { ...linkData, ...updates };
                        return;
                    }
                }
            }
            links[block.id] = linkData;
        }));
        setCandidateTrLinks(prev => ({ ...prev, [candidateId]: links }));

        // Write block_progress for complete theory blocks
        const pathwayId = pathway.rootId || pathway.id;
        await Promise.all(theoryBlocks.map(async (block) => {
            const link = links[block.id];
            if (link?.status === 'complete') {
                await DataService.setBlockProgress({
                    candidateId,
                    pathwayId,
                    blockId: block.id,
                    status: 'complete',
                    completedAt: link.completionDate,
                });
            }
        }));

        // Compute pathway progress from all block_progress entries and update pathway_assignments
        const allProgress = await DataService.getBlockProgressForPathway(candidateId, pathwayId);
        const completeBlockIds = new Set(allProgress.filter((p: any) => p.status === 'complete').map((p: any) => p.blockId));
        const totalBlocks = pathway.blocks.length;
        const completeCount = pathway.blocks.filter(b => completeBlockIds.has(b.id)).length;
        const newProgress = totalBlocks > 0 ? Math.round((completeCount / totalBlocks) * 100) : 0;
        const newPathwayStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' =
            completeCount === 0 ? 'NOT_STARTED'
            : completeCount === totalBlocks ? 'COMPLETED'
            : 'IN_PROGRESS';
        await DataService.updatePathwayProgress(candidateId, pathwayId, {
            progress: newProgress,
            status: newPathwayStatus,
            lastActivity: Date.now(),
        });
        setCandidates(prev => prev.map(c =>
            c.candidateId === candidateId
                ? { ...c, progress: newProgress, status: newPathwayStatus, lastActivity: Date.now() }
                : c
        ));
    };

    const loadCandidates = async () => {
        setIsLoading(true);
        try {
            const data = await DataService.getCandidatesForPathway(pathway.rootId || pathway.id, pathway.title);
            const pathwayId = pathway.rootId || pathway.id;
            const totalBlocks = pathway.blocks.length;

            // For each candidate, sync block_progress from stored link statuses and update pathway_assignments
            const updatedCandidates = await Promise.all(data.map(async (candidate) => {
                try {
                    // Write block_progress for any theory block already marked complete in block_trainingroom_links
                    await Promise.all(theoryBlocks.map(async (block) => {
                        const linkData = await DataService.getBlockTrainingRoomLink(candidate.candidateId, block.id, pathwayId);
                        if (linkData?.status === 'complete') {
                            await DataService.setBlockProgress({
                                candidateId: candidate.candidateId,
                                pathwayId,
                                blockId: block.id,
                                status: 'complete',
                                completedAt: linkData.completionDate,
                            });
                        }
                    }));

                    // Compute real progress from all block_progress entries for this candidate
                    const allProgress = await DataService.getBlockProgressForPathway(candidate.candidateId, pathwayId);
                    const completeBlockIds = new Set(allProgress.filter((p: any) => p.status === 'complete').map((p: any) => p.blockId));
                    const completeCount = pathway.blocks.filter(b => completeBlockIds.has(b.id)).length;
                    const newProgress = totalBlocks > 0 ? Math.round((completeCount / totalBlocks) * 100) : 0;
                    const newStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' =
                        completeCount === 0 ? 'NOT_STARTED'
                        : completeCount === totalBlocks ? 'COMPLETED'
                        : 'IN_PROGRESS';

                    if (candidate.progress !== newProgress || candidate.status !== newStatus) {
                        await DataService.updatePathwayProgress(candidate.candidateId, pathwayId, {
                            progress: newProgress,
                            status: newStatus,
                            lastActivity: Date.now(),
                        });
                        return { ...candidate, progress: newProgress, status: newStatus };
                    }
                    return candidate;
                } catch {
                    return candidate;
                }
            }));

            setCandidates(updatedCandidates);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadCandidates();
    }, [pathway]);

    useEffect(() => {
        if (!inlineViewerId) return;
        let cancelled = false;
        const loadProgress = async () => {
            setInlineIsLoading(true);
            try {
                const progress = await DataService.getBlockProgressForPathway(inlineViewerId, pathway.rootId || pathway.id);
                if (cancelled) return;
                const map = new Map<string, string>();
                (progress as any[]).forEach((p: any) => map.set(p.blockId, p.status));
                setInlineBlockProgressMap(map);
            } catch (e) {
                console.error('Failed to load inline progress', e);
            } finally {
                if (!cancelled) setInlineIsLoading(false);
            }
        };
        loadProgress();
        return () => { cancelled = true; };
    }, [inlineViewerId, pathway]);

    const openAssignModal = () => {
        setAssignError('');
        setAssignSuccess('');
        setSelectedCandidateIds([]);
        setAssignSearch('');
        setSelectedTrack(availableTracks[0] || 'STANDARD');
        const assignedIds = new Set(candidates.map(c => c.candidateId));
        setAvailableCandidates(orgCandidates.filter(u => !assignedIds.has(u.id)));
        setIsAssignOpen(true);
    };

    const toggleCandidateSelection = (id: string) => {
        setSelectedCandidateIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleAssign = async () => {
        if (selectedCandidateIds.length === 0) return;
        setIsAssigning(true);
        setAssignError('');
        setAssignSuccess('');
        try {
            const selectedUsers = availableCandidates.filter(u => selectedCandidateIds.includes(u.id));
            // Get theory blocks that need TR course provisioning — filtered to selected track + MERGE
            const theoryBlocks = pathway.blocks.filter(b =>
                b.type === 'theory' && b.courseTemplateId &&
                (!hasTracks || !b.branchId || b.branchId === selectedTrack || b.branchId === 'MERGE')
            );

            const startingModeByBlockId = Object.keys(selectedStartingModes).length > 0 ? selectedStartingModes : undefined;
            for (const user of selectedUsers) {
                // 1. Create pathway assignment
                await DataService.assignPathwayToCandidate({
                    pathwayId: pathway.rootId || pathway.id,
                    pathwayTitle: pathway.title,
                    candidateId: user.id,
                    candidateName: user.name,
                    progress: 0,
                    status: 'NOT_STARTED',
                    lastActivity: Date.now(),
                    assignedDate: Date.now(),
                    ...(hasTracks ? { selectedTrack } : {}),
                    ...(startingModeByBlockId ? { startingModeByBlockId } : {}),
                });

                // 2. Provision TR prerequisite courses (skip if valid course already exists)
                for (const block of theoryBlocks) {
                    if (!block.courseTemplateId || !activeOrgId) continue;
                    try {
                        const existingCourses = await DataService.getTrainingRoomCourses(user.id, block.courseTemplateId);
                        const validCourse = existingCourses.find(c => DataService.checkCourseValidity(c).isValid);

                        if (validCourse) {
                            // Valid course exists — store on block as complete
                            const toTs = (ts: any) => ts?.toDate ? ts.toDate().getTime() : ts ? new Date(ts).getTime() : undefined;
                            await DataService.createBlockTrainingRoomLink({
                                candidateId: user.id,
                                blockId: block.id,
                                pathwayId: pathway.rootId || pathway.id,
                                templateId: block.courseTemplateId,
                                courseId: validCourse.courseid || validCourse.id,
                                accessCode: validCourse.accesscode,
                                status: 'complete',
                                assignedDate: Date.now(),
                                completionDate: toTs(validCourse.signeddate),
                                renewalDate: toTs(validCourse.renewalDate),
                            });
                        } else {
                            // No valid course — provision a new one
                            const { courseId, accessCode } = await DataService.createTrainingRoomCourseRequest(
                                block.courseTemplateId, user.id, activeOrgId
                            );
                            await DataService.createBlockTrainingRoomLink({
                                candidateId: user.id,
                                blockId: block.id,
                                pathwayId: pathway.rootId || pathway.id,
                                templateId: block.courseTemplateId,
                                courseId,
                                accessCode,
                                status: 'not_started',
                                assignedDate: Date.now(),
                            });
                        }
                    } catch (e) {
                        console.error(`Failed to provision TR course for ${user.name} / block ${block.name}:`, e);
                    }
                }
            }

            setAssignSuccess(`Successfully assigned ${selectedUsers.length} candidate${selectedUsers.length !== 1 ? 's' : ''}.${theoryBlocks.length > 0 ? ` TR prerequisite courses linked per candidate.` : ''}`);
            setSelectedCandidateIds([]);
            // Refresh the list
            await loadCandidates();

            // 3. Check for sub-pathway blocks that need assignment
            const subPathwayBlocks = pathway.blocks.filter(b =>
                b.type === 'pathway' && b.linkedPathwayId &&
                (!hasTracks || !b.branchId || b.branchId === selectedTrack || b.branchId === 'MERGE')
            );

            if (subPathwayBlocks.length > 0) {
                const pending: typeof pendingSubPathways = [];
                for (const block of subPathwayBlocks) {
                    if (!block.linkedPathwayId) continue;
                    // Check which users don't already have this sub-pathway assigned
                    const unassignedUsers: { id: string; name: string }[] = [];
                    for (const user of selectedUsers) {
                        const existing = await DataService.getPathwaysForCandidate(user.id);
                        if (!existing.some(p => p.pathwayId === block.linkedPathwayId)) {
                            unassignedUsers.push({ id: user.id, name: user.name });
                        }
                    }
                    if (unassignedUsers.length > 0) {
                        // Load sub-pathway to check for tracks
                        const versions = await DataService.getPathwayVersions(block.linkedPathwayId);
                        const published = versions.filter(v => v.status === 'PUBLISHED').sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
                        const target = published || versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];
                        const subBlocks = target?.blocks || [];
                        const subTracks = (['STANDARD', 'ACCELERATED', 'REFRESHER', 'REMEDIAL'] as const).filter(t =>
                            subBlocks.some(b => b.branchId === t)
                        );

                        if (subTracks.length > 0) {
                            // Has tracks — needs modal for track selection
                            pending.push({ linkedPathwayId: block.linkedPathwayId, blockName: block.name, tracks: subTracks, users: unassignedUsers });
                        } else {
                            // No tracks — assign directly
                            for (const user of unassignedUsers) {
                                await DataService.assignPathwayToCandidate({
                                    pathwayId: block.linkedPathwayId,
                                    pathwayTitle: block.name,
                                    candidateId: user.id,
                                    candidateName: user.name,
                                    progress: 0,
                                    status: 'NOT_STARTED',
                                    lastActivity: Date.now(),
                                    assignedDate: Date.now(),
                                });
                            }
                        }
                    }
                }

                if (pending.length > 0) {
                    setPendingSubPathways(pending);
                    const defaults: Record<string, 'STANDARD' | 'ACCELERATED' | 'REFRESHER' | 'REMEDIAL'> = {};
                    pending.forEach(p => { defaults[p.linkedPathwayId] = p.tracks[0]; });
                    setSubPathwayTrackSelections(defaults);
                    setIsSubPathwayModalOpen(true);
                    setIsAssigning(false);
                    return;
                }
            }

            // Close after short delay so user sees the success message
            setTimeout(() => { setIsAssignOpen(false); setAssignSuccess(''); }, 2000);
        } catch (e) {
            console.error('Failed to assign candidates:', e);
            setAssignError('Failed to assign one or more candidates. Please try again.');
        } finally {
            setIsAssigning(false);
        }
    };

    const handleRemove = async (candidateId: string, includeSubPathways: boolean) => {
        setDeleteConfirmCandidate(null);
        setDeleteConfirmText('');
        setDeleteIncludeSubPathways(false);
        setDeletingCandidateId(candidateId);
        try {
            await DataService.removePathwayAssignment(candidateId, pathway.rootId || pathway.id, includeSubPathways);
            await loadCandidates();
        } catch (e) {
            console.error('Failed to remove pathway assignment:', e);
        } finally {
            setDeletingCandidateId(null);
        }
    };

    const handleAssignSubPathways = async () => {
        setIsAssigningSubPathways(true);
        try {
            for (const sp of pendingSubPathways) {
                const track = subPathwayTrackSelections[sp.linkedPathwayId];
                for (const user of sp.users) {
                    await DataService.assignPathwayToCandidate({
                        pathwayId: sp.linkedPathwayId,
                        pathwayTitle: sp.blockName,
                        candidateId: user.id,
                        candidateName: user.name,
                        progress: 0,
                        status: 'NOT_STARTED',
                        lastActivity: Date.now(),
                        assignedDate: Date.now(),
                        selectedTrack: track,
                    });
                }
            }
            setIsSubPathwayModalOpen(false);
            setPendingSubPathways([]);
            setIsAssignOpen(false);
            setAssignSuccess('');
        } catch (e) {
            console.error('Failed to assign sub-pathways:', e);
        } finally {
            setIsAssigningSubPathways(false);
        }
    };

    const hasSubPathways = pathway.blocks.some(b => b.type === 'pathway' && b.linkedPathwayId);

    const filteredAvailable = availableCandidates.filter(u =>
        u.name.toLowerCase().includes(assignSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(assignSearch.toLowerCase())
    );

    if (isLoading) return <div className="p-8 text-center text-aero-400">Loading candidates...</div>;

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-aero-800">Assigned Candidates ({candidates.length})</h4>
                {activeOrgId && (
                    <button
                        onClick={openAssignModal}
                        className="flex items-center gap-2 px-3 py-1.5 bg-aero-800 text-white rounded-lg hover:bg-aero-700 transition-colors text-xs font-medium"
                    >
                        <UserPlus className="w-3.5 h-3.5" /> Assign Candidate
                    </button>
                )}
            </div>

            {candidates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-aero-200 rounded-xl">
                    <div className="w-16 h-16 bg-aero-50 rounded-full flex items-center justify-center mb-4">
                        <Users className="w-8 h-8 text-aero-300" />
                    </div>
                    <p className="text-aero-500 font-medium mb-1">No candidates assigned yet</p>
                    <p className="text-xs text-aero-400 mb-4">Assign candidates to start tracking their pathway progress</p>
                    {activeOrgId && (
                        <button
                            onClick={openAssignModal}
                            className="flex items-center gap-2 px-4 py-2 bg-aero-800 text-white rounded-lg hover:bg-aero-700 transition-colors text-sm font-medium"
                        >
                            <UserPlus className="w-4 h-4" /> Assign First Candidate
                        </button>
                    )}
                </div>
            ) : (
                <div className="border border-aero-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-aero-50 text-aero-600 font-semibold border-b border-aero-200">
                            <tr>
                                <th className="px-6 py-3">Candidate</th>
                                <th className="px-6 py-3">Assigned</th>
                                <th className="px-6 py-3">Last Active</th>
                                <th className="px-6 py-3">Progress</th>
                                <th className="px-6 py-3 text-right">Status</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-aero-100">
                            {candidates.map((c, idx) => (
                                <React.Fragment key={idx}>
                                <tr
                                    className="transition-colors hover:bg-aero-50"
                                >
                                    <td className="px-6 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-aero-100 rounded-full flex items-center justify-center">
                                                <span className="text-xs font-bold text-aero-600">{c.candidateName.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
                                            </div>
                                            <span className="font-medium text-aero-900">{c.candidateName}</span>
                                            {c.selectedTrack && TRACK_LABELS[c.selectedTrack] && (
                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${TRACK_LABELS[c.selectedTrack].badge}`}>
                                                    {TRACK_LABELS[c.selectedTrack].label}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 text-aero-500 text-xs">
                                        {new Date(c.assignedDate).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-3 text-aero-500 text-xs">
                                        {new Date(c.lastActivity).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-3 w-1/4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 bg-aero-100 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full transition-all duration-500 ${c.progress === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                                                    style={{ width: `${c.progress}%` }}
                                                ></div>
                                            </div>
                                            <span className="text-xs font-bold text-aero-600 w-8">{c.progress}%</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                            c.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                            c.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700' :
                                            'bg-gray-100 text-gray-500'
                                        }`}>
                                            {c.status.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 text-right" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() => setInlineViewerId(c.candidateId)}
                                                className="p-1.5 text-aero-400 hover:text-aero-600 hover:bg-aero-100 rounded transition-colors"
                                                title="View pathway progress"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteConfirmCandidate({ id: c.candidateId, name: c.candidateName })}
                                                disabled={deletingCandidateId === c.candidateId}
                                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Remove assignment"
                                            >
                                                {deletingCandidateId === c.candidateId ? (
                                                    <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <Trash2 className="w-4 h-4" />
                                                )}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {/* Assign Candidate Modal */}
            <Modal isOpen={isAssignOpen} onClose={() => { setIsAssignOpen(false); setAssignError(''); setAssignSuccess(''); }} title="Assign Candidates to Pathway">
                <div className="space-y-4">
                    <p className="text-sm text-aero-500">Select candidates to assign to <strong>{pathway.title}</strong>.</p>

                    {/* Track Selector */}
                    {hasTracks && (
                        <div>
                            <p className="text-xs font-semibold text-aero-700 mb-2">Select Track</p>
                            <div className="flex flex-wrap gap-2">
                                {availableTracks.map(track => (
                                    <button
                                        key={track}
                                        type="button"
                                        onClick={() => setSelectedTrack(track)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                                            selectedTrack === track
                                                ? 'border-aero-700 bg-aero-800 text-white'
                                                : 'border-aero-200 text-aero-600 hover:bg-aero-50'
                                        }`}
                                    >
                                        {TRACK_LABELS[track]?.label || track}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* OJT Starting Mode Selectors */}
                    {ojtBlocksWithModes.length > 0 && (
                        <div className="space-y-2 bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-green-700 flex items-center gap-1">
                                <Layers className="w-3 h-3" /> Starting OJT mode
                            </p>
                            <p className="text-[10px] text-green-600">Pick the mode each candidate will start in for these OJT blocks. They can switch later during the session.</p>
                            {ojtBlocksWithModes.map(block => {
                                const modes = block.ojtConfig?.modes || [];
                                const current = selectedStartingModes[block.id] || modes[0]?.id || '';
                                const currentMode = modes.find(m => m.id === current);
                                return (
                                    <div key={block.id} className="flex items-center gap-2">
                                        <span className="text-xs text-green-800 flex-1 truncate" title={block.name}>{block.name}</span>
                                        <div className="flex items-center gap-1.5">
                                            {currentMode && <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: currentMode.color }} />}
                                            <select
                                                value={current}
                                                onChange={(e) => setSelectedStartingModes(prev => ({ ...prev, [block.id]: e.target.value }))}
                                                className="border border-green-200 rounded px-2 py-1 text-xs bg-white"
                                            >
                                                {modes.map(m => (
                                                    <option key={m.id} value={m.id}>{m.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aero-400" />
                        <input
                            type="text"
                            value={assignSearch}
                            onChange={(e) => setAssignSearch(e.target.value)}
                            className="w-full border border-aero-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-aero-400 focus:border-transparent"
                            placeholder="Search candidates..."
                        />
                    </div>

                    {/* Candidate List */}
                    <div className="max-h-64 overflow-y-auto border border-aero-200 rounded-lg divide-y divide-aero-100">
                        {filteredAvailable.length === 0 ? (
                            <div className="p-6 text-center text-aero-400 text-sm">
                                {availableCandidates.length === 0 ? 'No unassigned candidates found in this organization.' : 'No candidates match your search.'}
                            </div>
                        ) : (
                            filteredAvailable.map(u => (
                                <label
                                    key={u.id}
                                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-aero-50 transition-colors ${
                                        selectedCandidateIds.includes(u.id) ? 'bg-blue-50' : ''
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedCandidateIds.includes(u.id)}
                                        onChange={() => toggleCandidateSelection(u.id)}
                                        className="rounded border-aero-300 text-aero-600 focus:ring-aero-500"
                                    />
                                    <div className="w-8 h-8 bg-aero-100 rounded-full flex items-center justify-center">
                                        <span className="text-xs font-bold text-aero-600">{u.name.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-aero-900 truncate">{u.name}</div>
                                        <div className="text-xs text-aero-400 truncate">{u.email}</div>
                                    </div>
                                </label>
                            ))
                        )}
                    </div>

                    {/* Selection count */}
                    {selectedCandidateIds.length > 0 && (
                        <div className="text-xs text-aero-600 bg-aero-50 px-3 py-2 rounded-lg">
                            {selectedCandidateIds.length} candidate{selectedCandidateIds.length !== 1 ? 's' : ''} selected
                            {pathway.blocks.some(b => b.type === 'theory' && b.courseTemplateId) && (
                                <span className="text-blue-600 ml-1">— TR prerequisite courses will be auto-provisioned</span>
                            )}
                        </div>
                    )}

                    {/* Error / Success */}
                    {assignError && <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg flex items-center gap-2"><AlertCircle className="w-3 h-3" /> {assignError}</div>}
                    {assignSuccess && <div className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg flex items-center gap-2"><CheckCircle2 className="w-3 h-3" /> {assignSuccess}</div>}

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={() => { setIsAssignOpen(false); setAssignError(''); setAssignSuccess(''); }}
                            className="flex-1 px-4 py-2 border border-aero-200 text-aero-700 rounded-lg hover:bg-aero-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleAssign}
                            disabled={selectedCandidateIds.length === 0 || isAssigning}
                            className="flex-1 px-4 py-2 bg-aero-800 text-white rounded-lg hover:bg-aero-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isAssigning ? (
                                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Assigning...</>
                            ) : (
                                <><UserPlus className="w-4 h-4" /> Assign ({selectedCandidateIds.length})</>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Sub-pathway track selection modal */}
            {isSubPathwayModalOpen && pendingSubPathways.length > 0 && (
                <Modal isOpen={true} onClose={() => { setIsSubPathwayModalOpen(false); setPendingSubPathways([]); setIsAssignOpen(false); setAssignSuccess(''); }} title="Assign Sub-Pathways">
                    <div className="space-y-4">
                        <p className="text-sm text-aero-700">
                            The following sub-pathways need to be assigned to the selected candidates. Please choose a track for each.
                        </p>

                        <div className="space-y-3">
                            {pendingSubPathways.map(sp => (
                                <div key={sp.linkedPathwayId} className="p-3 border border-aero-200 rounded-lg bg-aero-50/30">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-semibold text-aero-900">{sp.blockName}</span>
                                        <span className="text-xs text-aero-500">{sp.users.length} candidate{sp.users.length !== 1 ? 's' : ''}</span>
                                    </div>
                                    <select
                                        value={subPathwayTrackSelections[sp.linkedPathwayId] || sp.tracks[0]}
                                        onChange={e => setSubPathwayTrackSelections(prev => ({ ...prev, [sp.linkedPathwayId]: e.target.value as any }))}
                                        className="w-full px-3 py-2 border border-aero-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-aero-500"
                                    >
                                        {sp.tracks.map(t => (
                                            <option key={t} value={t}>{TRACK_LABELS[t]?.label || t}</option>
                                        ))}
                                    </select>
                                    <div className="mt-2 text-xs text-aero-400">
                                        {sp.users.map(u => u.name).join(', ')}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => { setIsSubPathwayModalOpen(false); setPendingSubPathways([]); setIsAssignOpen(false); setAssignSuccess(''); }}
                                className="flex-1 px-4 py-2 border border-aero-200 text-aero-700 rounded-lg hover:bg-aero-50 transition-colors text-sm"
                            >
                                Skip
                            </button>
                            <button
                                onClick={handleAssignSubPathways}
                                disabled={isAssigningSubPathways}
                                className="flex-1 px-4 py-2 bg-aero-800 text-white rounded-lg hover:bg-aero-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
                            >
                                {isAssigningSubPathways ? (
                                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Assigning...</>
                                ) : (
                                    <>Assign Sub-Pathways</>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Delete confirmation dialog */}
            {deleteConfirmCandidate && (
                <Modal isOpen={true} onClose={() => { setDeleteConfirmCandidate(null); setDeleteConfirmText(''); setDeleteIncludeSubPathways(false); }} title="Delete Pathway Assignment">
                    <div className="space-y-4">
                        <p className="text-sm text-aero-700">
                            You are about to permanently delete the pathway assignment for <span className="font-bold">{deleteConfirmCandidate.name}</span> on <span className="font-bold">{pathway.title}</span>.
                        </p>
                        <p className="text-sm text-red-600 font-medium">
                            This will remove the assignment, all block progress, and all training room links for this candidate on this pathway. This action cannot be undone.
                        </p>

                        {hasSubPathways && (
                            <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={deleteIncludeSubPathways}
                                    onChange={e => setDeleteIncludeSubPathways(e.target.checked)}
                                    className="mt-0.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-amber-800">Also delete sub-pathway assignments</span>
                                    <p className="text-xs text-amber-600 mt-0.5">This will remove assignments, progress, and links for all linked sub-pathways as well.</p>
                                </div>
                            </label>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-aero-700 mb-1">
                                Type <span className="font-bold text-red-600">Delete</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder="Delete"
                                className="w-full px-3 py-2 border border-aero-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-2 pt-2">
                            <button
                                onClick={() => { setDeleteConfirmCandidate(null); setDeleteConfirmText(''); setDeleteIncludeSubPathways(false); }}
                                className="flex-1 px-4 py-2 border border-aero-200 text-aero-600 rounded-lg hover:bg-aero-50 transition-colors text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleRemove(deleteConfirmCandidate.id, deleteIncludeSubPathways)}
                                disabled={deleteConfirmText !== 'Delete'}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                                Delete Assignment
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Admin Block Review Panel */}
            <AdminBlockReviewPanel
                isOpen={!!inlineViewerId}
                onClose={() => { setInlineViewerId(null); setInlineBlockProgressMap(new Map()); }}
                pathway={pathway}
                candidate={candidates.find(c => c.candidateId === inlineViewerId) ?? null}
                blockProgressMap={inlineBlockProgressMap}
                isLoadingProgress={inlineIsLoading}
            />

        </div>
    );
};

const BlockList = ({ pathway, onUpdate }: { pathway: Pathway, onUpdate: (blocks: PathwayBlock[]) => void }) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [expandedBranches, setExpandedBranches] = useState<Record<string, boolean>>({
        'STANDARD': true, 'ACCELERATED': false, 'REFRESHER': false, 'MERGE': true, 'REMEDIAL': false
    });
    const toggleBranch = (branch: string) => setExpandedBranches(prev => ({...prev, [branch]: !prev[branch]}));

    const onDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
    };
    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();
    const onDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
        e.preventDefault();
        const sourceIndexStr = e.dataTransfer.getData('text/plain');
        if (!sourceIndexStr) return;
        const sourceIndex = parseInt(sourceIndexStr, 10);
        if (isNaN(sourceIndex) || sourceIndex === targetIndex) { setDraggedIndex(null); return; }
        const newBlocks = [...pathway.blocks];
        const [movedBlock] = newBlocks.splice(sourceIndex, 1);
        newBlocks.splice(targetIndex, 0, movedBlock);
        onUpdate(newBlocks);
        setDraggedIndex(null);
    };

    const branchConfig = [
        { id: 'STANDARD', label: 'Standard Track (Novice)', color: 'bg-blue-50 border-blue-100 text-blue-700' },
        { id: 'ACCELERATED', label: 'Accelerated Track (RPL)', color: 'bg-purple-50 border-purple-100 text-purple-700' },
        { id: 'REFRESHER', label: 'Refresher Track', color: 'bg-orange-50 border-orange-100 text-orange-700' },
        { id: 'MERGE', label: 'Final Assessment (Common)', color: 'bg-aero-100 border-aero-200 text-aero-800' },
        { id: 'REMEDIAL', label: 'Remedial Loop', color: 'bg-red-50 border-red-100 text-red-700' },
    ];

    return (
        <div className="space-y-6 pb-8">
            <div className="flex justify-between items-center pb-4 border-b border-aero-100">
                <h4 className="font-bold text-aero-800">Learning Blocks Configuration</h4>
                <Button variant="secondary" className="h-8 text-xs"><Plus className="w-3 h-3 mr-1" /> Add Block</Button>
            </div>
            <div className="space-y-4">
                {branchConfig.map(branch => {
                    const branchBlocks = pathway.blocks.map((block, idx) => ({ block, originalIndex: idx })).filter(item => item.block.branchId === branch.id);
                    return (
                        <div key={branch.id} className="border border-aero-200 rounded-xl overflow-hidden shadow-sm">
                            <button onClick={() => toggleBranch(branch.id)} className={`w-full flex items-center justify-between p-3 ${branch.color} transition-colors`}>
                                <span className="font-bold text-sm flex items-center gap-2">
                                    {expandedBranches[branch.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    {branch.label}
                                </span>
                                <span className="text-xs font-medium bg-white/50 px-2 py-0.5 rounded-full">{branchBlocks.length} Blocks</span>
                            </button>
                            {expandedBranches[branch.id] && (
                                <div className="p-3 bg-aero-50/30 space-y-2">
                                    {branchBlocks.length === 0 ? <div className="text-center py-4 text-xs text-aero-400 italic">No blocks configured for this track.</div> : 
                                        branchBlocks.map(({ block, originalIndex }) => (
                                            <div key={block.id} draggable onDragStart={(e) => onDragStart(e, originalIndex)} onDragOver={onDragOver} onDrop={(e) => onDrop(e, originalIndex)}
                                                className={`flex items-start gap-4 p-4 bg-white border rounded-lg transition-all group cursor-move ${draggedIndex === originalIndex ? 'opacity-50 border-dashed border-aero-400' : 'border-aero-200 hover:border-aero-400'}`}>
                                                <div className="mt-2 text-aero-300 group-hover:text-aero-500 cursor-grab active:cursor-grabbing"><GripVertical className="w-4 h-4" /></div>
                                                <div className="mt-1">{renderBlockIcon(block.type)}</div>
                                                <div className="flex-1">
                                                    <div className="flex justify-between">
                                                        <span className="font-bold text-sm text-aero-900">{block.name}</span>
                                                        <div className="flex items-center gap-2"><span className="text-[10px] text-aero-400 font-mono">#{originalIndex + 1}</span></div>
                                                    </div>
                                                    <p className="text-xs text-aero-500 mt-1">{block.description}</p>
                                                    <div className="flex gap-4 mt-2 text-[10px] text-aero-400 font-mono">
                                                        {block.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {block.duration}</span>}
                                                        {block.evidence && <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {block.evidence}</span>}
                                                    </div>
                                                </div>
                                                <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                                    <button className="p-1 hover:bg-aero-100 rounded text-aero-400"><Edit2 className="w-3 h-3" /></button>
                                                    <button className="p-1 hover:bg-red-50 rounded text-red-400"><Trash2 className="w-3 h-3" /></button>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const PathwaysManager = ({ activeOrgId, orgCandidates, initialPathwayId, onPathwaySelected }: { activeOrgId?: string; orgCandidates: TeamTrainee[]; initialPathwayId?: string | null; onPathwaySelected?: () => void }) => {
    const [pathways, setPathways] = useState<Pathway[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPathway, setSelectedPathway] = useState<Pathway | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isVersionDropdownOpen, setIsVersionDropdownOpen] = useState(false);
    const [isPathwayEdited, setIsPathwayEdited] = useState(false);
    const [isRenamingPathway, setIsRenamingPathway] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [pathwayFilter, setPathwayFilter] = useState('');
    const [candidateFilter, setCandidateFilter] = useState('');
    const [pathwayCandidates, setPathwayCandidates] = useState<Record<string, PathwayProgress[]>>({});
    const [isCandidatesLoading, setIsCandidatesLoading] = useState(false);
    const [pmProgressViewer, setPmProgressViewer] = useState<{ isOpen: boolean; candidateId: string; candidateName: string; pathwayId: string; selectedTrack?: string }>({ isOpen: false, candidateId: '', candidateName: '', pathwayId: '' });
    const [pmDeleteConfirm, setPmDeleteConfirm] = useState<{ candidateId: string; candidateName: string; pathwayId: string; pathwayTitle: string } | null>(null);
    const [pmDeleteConfirmText, setPmDeleteConfirmText] = useState('');
    const [pmDeleteIncludeSubPathways, setPmDeleteIncludeSubPathways] = useState(false);
    const [pmDeletingCandidateId, setPmDeletingCandidateId] = useState<string | null>(null);
    const [candidatesRefreshKey, setCandidatesRefreshKey] = useState(0);
    const versionDropdownRef = React.useRef<HTMLDivElement>(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (versionDropdownRef.current && !versionDropdownRef.current.contains(e.target as Node)) {
                setIsVersionDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load candidates for all pathways
    useEffect(() => {
        const loadAllCandidates = async () => {
            if (pathways.length === 0) return;
            setIsCandidatesLoading(true);
            try {
                // Get unique latest pathways by rootId
                const latestByRoot = Object.values(pathways.reduce((acc, curr) => {
                    if (!acc[curr.rootId] || acc[curr.rootId].version < curr.version) { acc[curr.rootId] = curr; }
                    return acc;
                }, {} as Record<string, Pathway>)) as Pathway[];

                const results: Record<string, PathwayProgress[]> = {};
                await Promise.all(latestByRoot.map(async (p) => {
                    try {
                        const candidates = await DataService.getCandidatesForPathway(p.rootId, p.title);
                        results[p.rootId] = candidates;
                    } catch (e) {
                        console.error(`Failed to load candidates for pathway ${p.title}`, e);
                        results[p.rootId] = [];
                    }
                }));
                setPathwayCandidates(results);
            } catch (e) {
                console.error('Failed to load pathway candidates', e);
            } finally {
                setIsCandidatesLoading(false);
            }
        };
        loadAllCandidates();
    }, [pathways, candidatesRefreshKey]);

    const handlePmRemove = async (candidateId: string, pathwayId: string, includeSubPathways: boolean) => {
        setPmDeleteConfirm(null);
        setPmDeleteConfirmText('');
        setPmDeleteIncludeSubPathways(false);
        setPmDeletingCandidateId(candidateId);
        try {
            await DataService.removePathwayAssignment(candidateId, pathwayId, includeSubPathways);
            setCandidatesRefreshKey(k => k + 1);
        } catch (e) {
            console.error('Failed to remove pathway assignment:', e);
        } finally {
            setPmDeletingCandidateId(null);
        }
    };

    useEffect(() => {
        const loadPathways = async () => {
            if (!activeOrgId) {
                setPathways([]);
                setIsLoading(false);
                return;
            }
            try {
                const data = await DataService.getPathways(activeOrgId);
                setPathways(data);
                if (initialPathwayId) {
                    const match = data.find(p => (p.rootId || p.id) === initialPathwayId);
                    if (match) {
                        const siblings = data.filter(v => v.rootId === match.rootId).sort((a, b) => (b.version ?? 1.0) - (a.version ?? 1.0));
                        const published = siblings.filter(v => v.status === 'PUBLISHED');
                        setSelectedPathway(published[0] ?? siblings[0] ?? match);
                        setIsEditing(false);
                        onPathwaySelected?.();
                    }
                }
            } catch (e) {
                console.error('Failed to load pathways', e);
            } finally {
                setIsLoading(false);
            }
        };
        loadPathways();
    }, [activeOrgId]);

    const handleCreate = async () => {
        if (!activeOrgId) return;
        const parentData = { 
            title: newTitle, 
            role: 'Unassigned', 
            lastEdited: new Date().toISOString(), 
            status: 'DRAFT' as const, 
            orgId: activeOrgId,
            dateCreated: new Date().toISOString(),
        };
        const initialVersion = {
            version: 1.0,
            blocks: [],
            status: 'DRAFT' as const,
            createdAt: new Date().toISOString(),
        };
        try {
            const { pathway, version } = await DataService.createPathway(parentData, initialVersion);
            // Merge into flat Pathway for the UI
            const created: Pathway = {
                id: version.id,
                rootId: pathway.id,
                title: pathway.title,
                role: parentData.role,
                lastEdited: parentData.lastEdited,
                status: version.status,
                version: version.version,
                blocks: version.blocks || [],
            };
            setPathways([...pathways, created]);
            setIsCreateOpen(false); 
            setNewTitle('');
        } catch (e) {
            console.error('Failed to create pathway', e);
        }
    };

    const handleDelete = async (id: string) => {
        const pathway = pathways.find(p => p.id === id);
        if (!pathway) return;
        if(confirm('Delete this pathway?')) {
            try {
                // Delete the parent doc (and all versions) using rootId
                await DataService.deletePathway(pathway.rootId);
                // Remove all versions of this pathway from local state
                setPathways(pathways.filter(p => p.rootId !== pathway.rootId));
                if(selectedPathway?.rootId === pathway.rootId) setSelectedPathway(null);
            } catch (e) {
                console.error('Failed to delete pathway', e);
            }
        }
    };

    const [isSaving, setIsSaving] = useState(false);

    const handleCreateVersion = async (type: 'major' | 'minor') => {
        if (!selectedPathway || isSaving) return;
        const versions = pathways.filter(p => p.rootId === selectedPathway.rootId);
        const maxVersion = Math.max(...versions.map(p => p.version ?? 1.0));
        const increment = type === 'major' ? 1.0 : 0.1;
        const nextVersion = type === 'major'
            ? Math.floor(maxVersion) + 1.0
            : parseFloat((maxVersion + increment).toFixed(1));
        setIsSaving(true);
        try {
            // Persist the new version to Firestore first
            const created = await DataService.createPathwayVersion(
                selectedPathway.rootId,
                { version: nextVersion, blocks: selectedPathway.blocks, status: 'DRAFT' as const },
                { title: selectedPathway.title, lastEdited: new Date().toISOString(), dateLastUpdated: new Date().toISOString(), description: selectedPathway.description, soloreadyOutline: selectedPathway.soloreadyOutline }
            );
            const newVersion: Pathway = {
                ...selectedPathway,
                id: created.id, // Use the Firestore-generated ID
                version: nextVersion,
                status: 'DRAFT',
                lastEdited: 'Just now',
                title: selectedPathway.title,
            };
            setPathways([...pathways, newVersion]);
            setSelectedPathway(newVersion);
            setIsPathwayEdited(false);
            setIsSaveModalOpen(false);
            setIsEditing(false);
        } catch (e) {
            console.error('Failed to create new version', e);
            alert('Failed to create new version. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSwitchVersion = (versionId: string) => {
        const target = pathways.find(p => p.id === versionId);
        if (target) {
            setSelectedPathway(target);
            setIsPathwayEdited(false);
            setIsEditing(false);
        }
    };

    const handlePublish = async () => {
        if (!selectedPathway) return;
        if (!confirm(`Publish v${selectedPathway.version.toFixed(1)} of "${selectedPathway.title}"? This will make it available to candidates.`)) return;
        setIsSaving(true);
        try {
            // Save blocks first if edited
            const updates: Partial<import('../types').PathwayVersion> = { status: 'PUBLISHED' };
            if (isPathwayEdited) {
                updates.blocks = selectedPathway.blocks;
            }
            await DataService.updatePathwayVersion(selectedPathway.rootId, selectedPathway.id, updates);
            await DataService.updatePathway(selectedPathway.rootId, {
                title: selectedPathway.title,
                status: 'PUBLISHED',
                lastEdited: new Date().toISOString(),
                dateLastUpdated: new Date().toISOString(),
                description: selectedPathway.description,
                soloreadyOutline: selectedPathway.soloreadyOutline,
            });
            // Update local state
            const updated = { ...selectedPathway, status: 'PUBLISHED' as const };
            setPathways(pathways.map(p => p.id === updated.id ? updated : p));
            setSelectedPathway(updated);
            setIsPathwayEdited(false);
        } catch (e) {
            console.error('Failed to publish pathway', e);
            alert('Failed to publish. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!selectedPathway) {
        const latestPathways = Object.values(pathways.reduce((acc, curr) => {
            if (!acc[curr.rootId] || acc[curr.rootId].version < curr.version) { acc[curr.rootId] = curr; }
            return acc;
        }, {} as Record<string, Pathway>)) as Pathway[];

        // Flatten all candidates across all pathways for the top panel table
        const allCandidateRows = latestPathways.flatMap(p =>
            (pathwayCandidates[p.rootId] || []).map(c => ({
                ...c,
                pathwayRootId: p.rootId,
            }))
        );

        // Dual filtering: pathway title + candidate name (AND logic)
        const filteredPathways = latestPathways.filter(p => {
            const matchesPathway = !pathwayFilter.trim() || p.title.toLowerCase().includes(pathwayFilter.toLowerCase());
            const matchesCandidate = !candidateFilter.trim() || (pathwayCandidates[p.rootId] || []).some(c => c.candidateName.toLowerCase().includes(candidateFilter.toLowerCase()));
            return matchesPathway && matchesCandidate;
        });

        // Filter the candidate rows for the table based on both filters
        const filteredCandidateRows = allCandidateRows.filter(c => {
            const matchesCandidate = !candidateFilter.trim() || c.candidateName.toLowerCase().includes(candidateFilter.toLowerCase());
            const matchesPathway = !pathwayFilter.trim() || c.pathwayTitle.toLowerCase().includes(pathwayFilter.toLowerCase());
            return matchesCandidate && matchesPathway;
        });

        return (
            <div className="space-y-6 animate-in fade-in duration-300">
                <div className="flex justify-between items-center">
                    <div><h2 className="text-2xl font-bold text-aero-900">Pathways</h2><p className="text-aero-500 text-sm">Design and manage competency tracks.</p></div>
                    <Button onClick={() => setIsCreateOpen(true)}><Plus className="w-4 h-4 mr-2" /> Create Pathway</Button>
                </div>

                {/* Active Candidates Panel */}
                <div className="space-y-4">
                    <div className="flex justify-between items-end">
                        <div>
                            <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-aero-600" />
                                Active Candidates
                            </h3>
                            <p className="text-sm text-aero-500 mt-1">Candidates currently in a pathway but not yet SoloReady.</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl border border-aero-200 shadow-sm overflow-hidden">
                        {/* Filter inputs inside the panel */}
                        <div className="flex items-center gap-4 p-4 border-b border-aero-100 bg-aero-50/30">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aero-400" />
                                <input
                                    type="text"
                                    value={candidateFilter}
                                    onChange={(e) => setCandidateFilter(e.target.value)}
                                    placeholder="Filter by candidate name..."
                                    className="w-full pl-9 pr-8 py-2 text-sm border border-aero-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-aero-500"
                                />
                                {candidateFilter && (
                                    <button onClick={() => setCandidateFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-aero-100 rounded">
                                        <X className="w-3 h-3 text-aero-400" />
                                    </button>
                                )}
                            </div>
                            <div className="relative flex-1">
                                <Network className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aero-400" />
                                <input
                                    type="text"
                                    value={pathwayFilter}
                                    onChange={(e) => setPathwayFilter(e.target.value)}
                                    placeholder="Filter by pathway title..."
                                    className="w-full pl-9 pr-8 py-2 text-sm border border-aero-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-aero-500"
                                />
                                {pathwayFilter && (
                                    <button onClick={() => setPathwayFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-aero-100 rounded">
                                        <X className="w-3 h-3 text-aero-400" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-aero-50 text-aero-600 font-semibold border-b border-aero-100">
                                    <tr>
                                        <th className="px-6 py-3">Candidate</th>
                                        <th className="px-6 py-3">Current Pathway</th>
                                        <th className="px-6 py-3">Progress</th>
                                        <th className="px-6 py-3 text-right">Status</th>
                                        <th className="px-6 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-aero-50">
                                    {isCandidatesLoading ? (
                                        <tr><td colSpan={5} className="px-6 py-8 text-center text-aero-400 italic">Loading candidates...</td></tr>
                                    ) : filteredCandidateRows.length === 0 ? (
                                        <tr><td colSpan={5} className="px-6 py-8 text-center text-aero-400 italic">
                                            {(candidateFilter || pathwayFilter) ? 'No candidates match the current filters.' : 'No candidates assigned to any pathway yet.'}
                                        </td></tr>
                                    ) : (
                                        filteredCandidateRows.map((c, idx) => (
                                            <tr key={`${c.candidateId}-${c.pathwayId}-${idx}`} className="hover:bg-aero-50 transition-colors cursor-pointer" onClick={() => setPmProgressViewer({ isOpen: true, candidateId: c.candidateId, candidateName: c.candidateName, pathwayId: c.pathwayRootId, selectedTrack: c.selectedTrack })}>
                                                <td className="px-6 py-3 font-medium text-aero-900">{c.candidateName}</td>
                                                <td className="px-6 py-3 text-aero-600">{c.pathwayTitle}</td>
                                                <td className="px-6 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-24 bg-aero-100 rounded-full h-2">
                                                            <div 
                                                                className={`h-2 rounded-full ${c.progress > 80 ? 'bg-green-500' : 'bg-blue-500'}`} 
                                                                style={{ width: `${c.progress}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="text-xs font-bold text-aero-500">{c.progress}%</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 text-right">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                                        c.status === 'COMPLETED' 
                                                            ? 'bg-green-50 text-green-700 border-green-100'
                                                            : c.status === 'IN_PROGRESS' 
                                                                ? 'bg-blue-50 text-blue-700 border-blue-100' 
                                                                : 'bg-orange-50 text-orange-700 border-orange-100'
                                                    }`}>
                                                        {c.status.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3 text-right" onClick={e => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => setPmDeleteConfirm({ candidateId: c.candidateId, candidateName: c.candidateName, pathwayId: c.pathwayRootId, pathwayTitle: c.pathwayTitle })}
                                                        disabled={pmDeletingCandidateId === c.candidateId}
                                                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Remove assignment"
                                                    >
                                                        {pmDeletingCandidateId === c.candidateId ? (
                                                            <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {(candidateFilter || pathwayFilter) && filteredCandidateRows.length > 0 && (
                            <div className="px-4 py-2 border-t border-aero-100 bg-aero-50/30">
                                <p className="text-xs text-aero-400">Showing {filteredCandidateRows.length} of {allCandidateRows.length} candidates</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Pathway Cards Grid */}
                <div className="space-y-4">
                    <div className="flex justify-between items-end">
                        <div>
                            <h3 className="text-lg font-bold text-aero-900 flex items-center gap-2">
                                <Network className="w-5 h-5 text-aero-600" />
                                Pathway Cards
                            </h3>
                            <p className="text-sm text-aero-500 mt-1">
                                {(pathwayFilter || candidateFilter) 
                                    ? `Showing ${filteredPathways.length} of ${latestPathways.length} pathways`
                                    : `${latestPathways.length} pathway${latestPathways.length !== 1 ? 's' : ''}`
                                }
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredPathways.map(p => {
                            const candidates = pathwayCandidates[p.rootId] || [];
                            const siblings = pathways.filter(v => v.rootId === p.rootId).sort((a, b) => (b.version ?? 1.0) - (a.version ?? 1.0));
                            const publishedSiblings = siblings.filter(v => v.status === 'PUBLISHED');
                            const resolved = publishedSiblings[0] ?? siblings[0] ?? p;
                            return (
                            <div key={p.id} onClick={() => { setSelectedPathway(resolved); setIsEditing(false); setIsPathwayEdited(false); setIsRenamingPathway(false); }} className="group bg-white rounded-xl border border-aero-200 p-6 shadow-sm hover:shadow-md hover:border-aero-300 transition-all cursor-pointer relative overflow-hidden">
                                 <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity z-10"><button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="p-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100"><Trash2 className="w-4 h-4" /></button></div>
                                 <div className="w-12 h-12 bg-aero-50 rounded-lg flex items-center justify-center text-aero-500 mb-4 group-hover:bg-aero-600 group-hover:text-white transition-colors"><Network className="w-6 h-6" /></div>
                                 <h3 className="font-bold text-lg text-aero-900 mb-1">{resolved.title}</h3><p className="text-sm text-aero-500 mb-4">{resolved.role}</p>
                                 <div className="flex items-center justify-between text-xs text-aero-400 border-t border-aero-50 pt-4"><div className="flex items-center gap-2"><span className="font-mono bg-aero-100 text-aero-700 px-1.5 rounded">v{(resolved.version ?? 1.0).toFixed(1)}</span><span>{(resolved.blocks || []).length} Blocks</span></div><span className={`px-2 py-0.5 rounded-full ${resolved.status === 'PUBLISHED' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{resolved.status}</span></div>
                                 {/* Candidates section */}
                                 <div className="mt-4 pt-4 border-t border-aero-50">
                                     <div className="flex items-center gap-2 mb-3">
                                         <Users className="w-3.5 h-3.5 text-aero-400" />
                                         <span className="text-xs font-semibold text-aero-600">{candidates.length} Candidate{candidates.length !== 1 ? 's' : ''}</span>
                                     </div>
                                     {candidates.length === 0 ? (
                                         <p className="text-xs text-aero-300 italic">No candidates assigned</p>
                                     ) : (
                                         <div className="space-y-2">
                                             {candidates.map((c, idx) => (
                                                 <div key={idx} className="flex items-center gap-2">
                                                     <span className="text-xs font-medium text-aero-700 w-24 truncate" title={c.candidateName}>{c.candidateName}</span>
                                                     <div className="flex-1 bg-aero-100 rounded-full h-1.5">
                                                         <div
                                                             className={`h-1.5 rounded-full transition-all duration-500 ${c.progress > 80 ? 'bg-green-500' : c.progress > 0 ? 'bg-blue-500' : 'bg-aero-200'}`}
                                                             style={{ width: `${c.progress}%` }}
                                                         ></div>
                                                     </div>
                                                     <span className="text-[10px] font-bold text-aero-500 w-8 text-right">{c.progress}%</span>
                                                     <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                                         c.status === 'COMPLETED' ? 'bg-green-50 text-green-600' :
                                                         c.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-600' :
                                                         'bg-gray-50 text-gray-400'
                                                     }`}>{c.status.replace('_', ' ')}</span>
                                                 </div>
                                             ))}
                                         </div>
                                     )}
                                 </div>
                            </div>
                            );
                        })}
                    </div>
                </div>

                <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Create New Pathway">
                    <div className="space-y-4">
                        <Input label="Pathway Title" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Heavy Machinery Level 2" />
                        <div className="flex justify-end gap-2 mt-4"><Button variant="ghost" onClick={() => setIsCreateOpen(false)}>Cancel</Button><Button onClick={handleCreate} disabled={!newTitle}>Create</Button></div>
                    </div>
                </Modal>
                <PathwayProgressViewerModal
                    isOpen={pmProgressViewer.isOpen}
                    onClose={() => setPmProgressViewer(v => ({ ...v, isOpen: false }))}
                    candidateName={pmProgressViewer.candidateName}
                    candidateId={pmProgressViewer.candidateId}
                    pathwayId={pmProgressViewer.pathwayId}
                    selectedTrack={pmProgressViewer.selectedTrack}
                />

                {/* Delete confirmation dialog for Active Candidates */}
                {pmDeleteConfirm && (
                    <Modal isOpen={true} onClose={() => { setPmDeleteConfirm(null); setPmDeleteConfirmText(''); setPmDeleteIncludeSubPathways(false); }} title="Delete Pathway Assignment">
                        <div className="space-y-4">
                            <p className="text-sm text-aero-700">
                                You are about to permanently delete the pathway assignment for <span className="font-bold">{pmDeleteConfirm.candidateName}</span> on <span className="font-bold">{pmDeleteConfirm.pathwayTitle}</span>.
                            </p>
                            <p className="text-sm text-red-600 font-medium">
                                This will remove the assignment, all block progress, and all training room links for this candidate on this pathway. This action cannot be undone.
                            </p>

                            <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={pmDeleteIncludeSubPathways}
                                    onChange={e => setPmDeleteIncludeSubPathways(e.target.checked)}
                                    className="mt-0.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-amber-800">Also delete sub-pathway assignments</span>
                                    <p className="text-xs text-amber-600 mt-0.5">This will remove assignments, progress, and links for all linked sub-pathways as well.</p>
                                </div>
                            </label>

                            <div>
                                <label className="block text-sm font-medium text-aero-700 mb-1">
                                    Type <span className="font-bold text-red-600">Delete</span> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={pmDeleteConfirmText}
                                    onChange={e => setPmDeleteConfirmText(e.target.value)}
                                    placeholder="Delete"
                                    className="w-full px-3 py-2 border border-aero-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button
                                    onClick={() => { setPmDeleteConfirm(null); setPmDeleteConfirmText(''); setPmDeleteIncludeSubPathways(false); }}
                                    className="flex-1 px-4 py-2 border border-aero-200 text-aero-600 rounded-lg hover:bg-aero-50 transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handlePmRemove(pmDeleteConfirm.candidateId, pmDeleteConfirm.pathwayId, pmDeleteIncludeSubPathways)}
                                    disabled={pmDeleteConfirmText !== 'Delete'}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                >
                                    Delete Assignment
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </div>
        );
    }

    const availableVersions = pathways.filter(p => p.rootId === selectedPathway.rootId).sort((a, b) => b.version - a.version);

    return (
        <div className="h-[calc(100vh-140px)] flex flex-col animate-in slide-in-from-right-4 duration-300">
             <div className="flex items-center justify-between mb-6">
                 <div className="flex items-center gap-4">
                     <button onClick={() => { setSelectedPathway(null); setIsEditing(false); setIsPathwayEdited(false); setIsRenamingPathway(false); }} className="p-2 hover:bg-aero-100 rounded-lg text-aero-500 transition-colors"><ChevronRight className="w-5 h-5 rotate-180" /></button>
                     <div>
                         <div className="flex items-center gap-3">
                             {isRenamingPathway ? (
                                 <div className="flex items-center gap-2">
                                     <input
                                         autoFocus
                                         className="text-2xl font-bold text-aero-900 bg-aero-50 border border-aero-300 rounded-lg px-2 py-0.5 outline-none focus:ring-2 focus:ring-aero-400 transition-all"
                                         value={selectedPathway.title}
                                         onChange={(e) => {
                                             const updated = { ...selectedPathway, title: e.target.value };
                                             setPathways(pathways.map(p => p.id === updated.id ? updated : p));
                                             setSelectedPathway(updated);
                                             setIsPathwayEdited(true);
                                         }}
                                         onKeyDown={(e) => { if (e.key === 'Enter') setIsRenamingPathway(false); if (e.key === 'Escape') setIsRenamingPathway(false); }}
                                         onBlur={() => setIsRenamingPathway(false)}
                                     />
                                 </div>
                             ) : (
                                 <div className={`flex items-center gap-2 group ${isEditing ? 'cursor-pointer' : ''}`} onClick={isEditing ? () => setIsRenamingPathway(true) : undefined}>
                                     <h2 className="text-2xl font-bold text-aero-900">{selectedPathway.title}</h2>
                                     {isEditing && <Pencil className="w-4 h-4 text-aero-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                                 </div>
                             )}
                             {isEditing ? (
                                <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-sm font-bold ${isPathwayEdited ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' : 'bg-aero-100 text-aero-700'}`}><GitBranch className="w-3 h-3" /> {isPathwayEdited ? 'edited' : `v${(selectedPathway.version ?? 1.0).toFixed(1)}`}</span>
                             ) : (
                             <div className="relative" ref={versionDropdownRef}>
                                <button onClick={() => setIsVersionDropdownOpen(!isVersionDropdownOpen)} className={`flex items-center gap-1 px-2 py-0.5 rounded text-sm font-bold transition-colors ${isPathwayEdited ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 ring-1 ring-amber-300' : 'bg-aero-100 text-aero-700 hover:bg-aero-200'}`}><GitBranch className="w-3 h-3" /> {isPathwayEdited ? 'edited' : `v${(selectedPathway.version ?? 1.0).toFixed(1)}`} <ChevronDown className="w-3 h-3" /></button>
                                {isVersionDropdownOpen && (
                                <div className="absolute top-full left-0 mt-1 w-32 bg-white border border-aero-200 rounded-lg shadow-lg overflow-hidden z-50">
                                    {availableVersions.map(v => (<button key={v.id} onClick={() => { handleSwitchVersion(v.id); setIsVersionDropdownOpen(false); }} className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-aero-50 ${v.id === selectedPathway.id ? 'bg-aero-50 text-aero-800' : 'text-aero-500'}`}>v{(v.version ?? 1.0).toFixed(1)} - {v.status}</button>))}
                                </div>
                                )}
                             </div>
                             )}
                             <span className={`uppercase text-[10px] font-bold px-1.5 py-0.5 rounded ${selectedPathway.status === 'PUBLISHED' ? 'bg-green-100 text-green-700' : selectedPathway.status === 'ARCHIVED' ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-600'}`}>{selectedPathway.status}</span>
                             {!isEditing && (
                                <button onClick={() => setIsEditing(true)} title="Edit pathway" className="p-1.5 hover:bg-aero-100 rounded-lg text-aero-500 hover:text-aero-700 transition-colors"><Pencil className="w-4 h-4" /></button>
                             )}
                         </div>
                         <div className="flex items-center text-sm text-aero-500 mt-1"><span>{selectedPathway.role}</span></div>
                         <div className="flex gap-4 mt-2">
                             <div className="flex flex-col gap-0.5">
                                 <label className="text-[10px] font-semibold text-aero-400 uppercase tracking-wide">Pathway Description</label>
                                 <textarea
                                     value={selectedPathway.description || ''}
                                     onChange={(e) => {
                                         const updated = { ...selectedPathway, description: e.target.value };
                                         setPathways(pathways.map(p => p.id === updated.id ? updated : p));
                                         setSelectedPathway(updated);
                                         setIsPathwayEdited(true);
                                     }}
                                     readOnly={!isEditing}
                                     placeholder="What is this pathway for?"
                                     rows={2}
                                     className={`w-72 text-sm text-aero-700 bg-aero-50 border border-aero-200 rounded-lg px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-aero-400 placeholder:text-aero-300 ${!isEditing ? 'cursor-default' : ''}`}
                                 />
                             </div>
                             <div className="flex flex-col gap-0.5">
                                 <label className="text-[10px] font-semibold text-aero-400 uppercase tracking-wide">SoloReady Outline</label>
                                 <textarea
                                     value={selectedPathway.soloreadyOutline || ''}
                                     onChange={(e) => {
                                         const updated = { ...selectedPathway, soloreadyOutline: e.target.value };
                                         setPathways(pathways.map(p => p.id === updated.id ? updated : p));
                                         setSelectedPathway(updated);
                                         setIsPathwayEdited(true);
                                     }}
                                     readOnly={!isEditing}
                                     placeholder="What is this person SoloReady to carry out on completion?"
                                     rows={2}
                                     className={`w-72 text-sm text-aero-700 bg-aero-50 border border-aero-200 rounded-lg px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-aero-400 placeholder:text-aero-300 ${!isEditing ? 'cursor-default' : ''}`}
                                 />
                             </div>
                         </div>
                     </div>
                 </div>
                 <div className="flex gap-2">
                     {isEditing && (
                         <>
                             <Button variant="secondary" onClick={() => setIsSaveModalOpen(true)} disabled={!isPathwayEdited || isSaving}>{isSaving ? 'Saving...' : 'Save'}</Button>
                             <Button
                                onClick={handlePublish}
                                disabled={isSaving || (selectedPathway.status === 'PUBLISHED' && isPathwayEdited)}
                             >
                                {isSaving ? 'Publishing...' : `Publish v${selectedPathway.version.toFixed(1)}`}
                             </Button>
                         </>
                     )}
                 </div>
             </div>
             <div className="flex-1 bg-white rounded-xl border border-aero-200 shadow-sm overflow-y-auto flex flex-col">
                 <div className="p-4 border-b border-aero-100 bg-white">
                     <Tabs items={[
                         { id: 'visual', label: 'Visual Flow', content: <VisualFlow pathway={selectedPathway} activeOrgId={activeOrgId} onUpdate={isEditing ? (newBlocks) => { const updated = { ...selectedPathway, blocks: newBlocks }; setPathways(pathways.map(p => p.id === updated.id ? updated : p)); setSelectedPathway(updated); setIsPathwayEdited(true); } : undefined} /> },
                         { id: 'candidates', label: 'Candidates', content: <CandidateListForPathway pathway={selectedPathway} activeOrgId={activeOrgId} orgCandidates={orgCandidates} /> },
                     ]} />
                 </div>
             </div>
             <PathwayProgressViewerModal
                 isOpen={pmProgressViewer.isOpen}
                 onClose={() => setPmProgressViewer(v => ({ ...v, isOpen: false }))}
                 candidateName={pmProgressViewer.candidateName}
                 candidateId={pmProgressViewer.candidateId}
                 pathwayId={pmProgressViewer.pathwayId}
                 selectedTrack={pmProgressViewer.selectedTrack}
             />

            {isSaveModalOpen && selectedPathway && (() => {
                const siblings = pathways.filter(p => p.rootId === selectedPathway.rootId);
                const maxVersion = Math.max(...siblings.map(p => p.version ?? 1.0));
                const nextMinor = parseFloat((maxVersion + 0.1).toFixed(1));
                const nextMajor = Math.floor(maxVersion) + 1.0;
                const sortedSiblings = [...siblings].sort((a, b) => (b.version ?? 1.0) - (a.version ?? 1.0));
                return (
                    <Modal isOpen={true} onClose={() => setIsSaveModalOpen(false)} title="Save as new version">
                        <div className="space-y-4">
                            <div>
                                <label className="text-[10px] font-semibold text-aero-400 uppercase tracking-wide">Existing versions</label>
                                <div className="mt-1 max-h-32 overflow-y-auto border border-aero-200 rounded-lg divide-y divide-aero-100">
                                    {sortedSiblings.map(v => (
                                        <div key={v.id} className={`flex items-center justify-between px-3 py-1.5 text-xs ${v.id === selectedPathway.id ? 'bg-aero-50' : ''}`}>
                                            <span className="font-mono font-semibold text-aero-800">v{(v.version ?? 1.0).toFixed(1)}</span>
                                            <span className={`uppercase text-[9px] font-bold px-1.5 py-0.5 rounded ${v.status === 'PUBLISHED' ? 'bg-green-100 text-green-700' : v.status === 'ARCHIVED' ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-600'}`}>{v.status}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-aero-400 uppercase tracking-wide">Save as</label>
                                <div className="mt-1 grid grid-cols-1 gap-2">
                                    <button onClick={() => handleCreateVersion('minor')} disabled={isSaving} className="text-left px-4 py-3 border border-aero-200 rounded-lg hover:border-aero-400 hover:bg-aero-50 transition-colors disabled:opacity-50">
                                        <span className="font-semibold text-aero-800">Minor Version</span>
                                        <span className="block text-[11px] text-aero-500 mt-0.5">Increment by 0.1 → v{nextMinor.toFixed(1)}</span>
                                    </button>
                                    <button onClick={() => handleCreateVersion('major')} disabled={isSaving} className="text-left px-4 py-3 border border-aero-200 rounded-lg hover:border-aero-400 hover:bg-aero-50 transition-colors disabled:opacity-50">
                                        <span className="font-semibold text-aero-800">Major Version</span>
                                        <span className="block text-[11px] text-aero-500 mt-0.5">Increment by 1.0 → v{nextMajor.toFixed(1)}</span>
                                    </button>
                                </div>
                            </div>
                            <div className="flex justify-end pt-2">
                                <Button variant="secondary" onClick={() => setIsSaveModalOpen(false)} disabled={isSaving}>Cancel</Button>
                            </div>
                        </div>
                    </Modal>
                );
            })()}

            {/* Delete confirmation dialog for Active Candidates */}
            {pmDeleteConfirm && (
                <Modal isOpen={true} onClose={() => { setPmDeleteConfirm(null); setPmDeleteConfirmText(''); setPmDeleteIncludeSubPathways(false); }} title="Delete Pathway Assignment">
                    <div className="space-y-4">
                        <p className="text-sm text-aero-700">
                            You are about to permanently delete the pathway assignment for <span className="font-bold">{pmDeleteConfirm.candidateName}</span> on <span className="font-bold">{pmDeleteConfirm.pathwayTitle}</span>.
                        </p>
                        <p className="text-sm text-red-600 font-medium">
                            This will remove the assignment, all block progress, and all training room links for this candidate on this pathway. This action cannot be undone.
                        </p>

                        <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                            <input
                                type="checkbox"
                                checked={pmDeleteIncludeSubPathways}
                                onChange={e => setPmDeleteIncludeSubPathways(e.target.checked)}
                                className="mt-0.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                            />
                            <div>
                                <span className="text-sm font-medium text-amber-800">Also delete sub-pathway assignments</span>
                                <p className="text-xs text-amber-600 mt-0.5">This will remove assignments, progress, and links for all linked sub-pathways as well.</p>
                            </div>
                        </label>

                        <div>
                            <label className="block text-sm font-medium text-aero-700 mb-1">
                                Type <span className="font-bold text-red-600">Delete</span> to confirm
                            </label>
                            <input
                                type="text"
                                value={pmDeleteConfirmText}
                                onChange={e => setPmDeleteConfirmText(e.target.value)}
                                placeholder="Delete"
                                className="w-full px-3 py-2 border border-aero-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-2 pt-2">
                            <button
                                onClick={() => { setPmDeleteConfirm(null); setPmDeleteConfirmText(''); setPmDeleteIncludeSubPathways(false); }}
                                className="flex-1 px-4 py-2 border border-aero-200 text-aero-600 rounded-lg hover:bg-aero-50 transition-colors text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handlePmRemove(pmDeleteConfirm.candidateId, pmDeleteConfirm.pathwayId, pmDeleteIncludeSubPathways)}
                                disabled={pmDeleteConfirmText !== 'Delete'}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                                Delete Assignment
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// --- Org Tree Types and Components ---
interface OrgTreeItemType {
    id: number;
    name: string;
    teamid: string;
    active: boolean;
    children?: OrgTreeItemType[];
}

interface OrgTreeItemProps {
    node: OrgTreeItemType;
    depth: number;
    activeOrgId?: string;
    onSwitchOrg: (id: string, sub: boolean) => void;
    expandedTeams: Set<string>;
    onToggleExpand: (teamid: string) => void;
    onEditTeam: (node: OrgTreeItemType) => void;
    onAddSubTeam: (parentNode: OrgTreeItemType) => void;
    onToggleActive: (node: OrgTreeItemType) => void;
}

const OrgTreeItem: React.FC<OrgTreeItemProps> = ({ node, depth, activeOrgId, onSwitchOrg, expandedTeams, onToggleExpand, onEditTeam, onAddSubTeam, onToggleActive }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isActive = activeOrgId === node.teamid;
    const isExpanded = expandedTeams.has(node.teamid);
    
    return (
        <li className="select-none">
            <div 
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                    isActive ? 'bg-aero-600 text-white' : 'hover:bg-aero-100 text-aero-800'
                }`}
                style={{ paddingLeft: `${depth * 20 + 12}px` }}
                onClick={() => onSwitchOrg(node.teamid, false)}
            >
                {hasChildren && (
                    <button 
                        onClick={(e) => { e.stopPropagation(); onToggleExpand(node.teamid); }}
                        className="p-0.5 hover:bg-aero-200 rounded"
                    >
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                )}
                {!hasChildren && <div className="w-5" />}
                <Building2 className="w-4 h-4" />
                <span className={`flex-1 font-medium ${!node.active ? 'line-through opacity-50' : ''}`}>
                    {node.name}
                </span>
                
                {/* Action buttons - show on hover */}
                <div className={`flex items-center gap-1 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                    <button
                        onClick={(e) => { e.stopPropagation(); onAddSubTeam(node); }}
                        className={`p-1 rounded hover:bg-green-100 ${isActive ? 'text-white hover:text-green-600' : 'text-aero-400 hover:text-green-600'}`}
                        title="Add Sub Team"
                    >
                        <Plus className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onEditTeam(node); }}
                        className={`p-1 rounded hover:bg-aero-100 ${isActive ? 'text-white hover:text-aero-700' : 'text-aero-400 hover:text-aero-700'}`}
                        title="Edit Team"
                    >
                        <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleActive(node); }}
                        className={`p-1 rounded ${node.active ? 'hover:bg-orange-100' : 'hover:bg-green-100'} ${isActive ? 'text-white' : 'text-aero-400'} ${node.active ? 'hover:text-orange-600' : 'hover:text-green-600'}`}
                        title={node.active ? 'Set Inactive' : 'Set Active'}
                    >
                        {node.active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                </div>
            </div>
            {hasChildren && isExpanded && (
                <ul className="space-y-1 mt-1">
                    {node.children!.map(child => (
                        <OrgTreeItem 
                            key={child.id} 
                            node={child} 
                            depth={depth + 1} 
                            activeOrgId={activeOrgId}
                            onSwitchOrg={onSwitchOrg}
                            expandedTeams={expandedTeams}
                            onToggleExpand={onToggleExpand}
                            onEditTeam={onEditTeam}
                            onAddSubTeam={onAddSubTeam}
                            onToggleActive={onToggleActive}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ user, activeOrgId, onUpdateUser, onSwitchOrg, currentView, onSwitchView }) => {
    // Org Tree from teamsadminnest
    interface TreeItem {
        id: number;
        name: string;
        teamid: string;
        active: boolean;
        children?: TreeItem[];
    }
    const [orgTree, setOrgTree] = useState<TreeItem[]>([]);
    
    // Get team data from context
    const teamData = useTeamData();
    
    // User Management State
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [userModalMode, setUserModalMode] = useState<'CREATE' | 'EDIT'>('CREATE');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isExistingUser, setIsExistingUser] = useState(false);
    const [userFormName, setUserFormName] = useState('');
    const [userFormEmail, setUserFormEmail] = useState('');
    const [userFormEmailConfirm, setUserFormEmailConfirm] = useState('');
    const [userFormPhone, setUserFormPhone] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(countries[0]);
    const [detectedCountry, setDetectedCountry] = useState(countries[0]);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const [selectedExistingUsers, setSelectedExistingUsers] = useState<string[]>([]);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showEmailFields, setShowEmailFields] = useState(false);
    const [showPhoneFields, setShowPhoneFields] = useState(false);

    // Pathway Progress Modal
    const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
    const [viewingUser, setViewingUser] = useState<User | null>(null);
    const [userPathways, setUserPathways] = useState<PathwayProgress[]>([]);
    const [pendingPathwayId, setPendingPathwayId] = useState<string | null>(null);
    const [pathwayProgressViewer, setPathwayProgressViewer] = useState<{ isOpen: boolean; candidateId: string; candidateName: string; pathwayId: string; selectedTrack?: string }>({ isOpen: false, candidateId: '', candidateName: '', pathwayId: '' });

    // Trainee Team Memberships (for candidate edit modal)
    const [traineeTeamMemberships, setTraineeTeamMemberships] = useState<Array<{ teamId: string; teamName: string; role: string }>>([]);
    const [isLoadingMemberships, setIsLoadingMemberships] = useState(false);

    // Organization Modal State
    const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
    const [editingOrg, setEditingOrg] = useState<any>(null);
    const [orgFormName, setOrgFormName] = useState('');
    const [orgFormDesc, setOrgFormDesc] = useState('');
    const [orgFormParentId, setOrgFormParentId] = useState('ROOT');
    const [isAddingSubTeam, setIsAddingSubTeam] = useState(false);

    // Move/Remove Candidates State
    const [selectedCandidates, setSelectedCandidates] = useState<Array<{ id: string; name: string }>>([]);
    const [isMoveRemoveModalOpen, setIsMoveRemoveModalOpen] = useState(false);
    const [moveRemoveAction, setMoveRemoveAction] = useState<'Move' | 'Remove'>('Move');
    const [moveTargetTeamId, setMoveTargetTeamId] = useState<string>('');

    // Activity Feed Refresh Key - increment to trigger refresh
    const [activityRefreshKey, setActivityRefreshKey] = useState(0);

    // Team Edit/Add Modal State
    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
    const [teamModalMode, setTeamModalMode] = useState<'ADD' | 'EDIT'>('EDIT');
    const [selectedTeamNode, setSelectedTeamNode] = useState<{ teamid: string; name: string; active: boolean; parentTeamId?: string } | null>(null);
    const [teamFormName, setTeamFormName] = useState('');
    const [isTeamSaving, setIsTeamSaving] = useState(false);

    // Detect user's country from IP on mount
    useEffect(() => {
        const detectCountry = async () => {
            try {
                const response = await fetch('https://extreme-ip-lookup.com/json/?key=Oc3p0jeNTpi1inmlfqVW');
                const data = await response.json();
                const detected = countries.find(c => c.iso2.toUpperCase() === data.countryCode);
                if (detected) {
                    setDetectedCountry(detected);
                }
            } catch (err) {
                console.log('Error detecting country:', err);
            }
        };
        detectCountry();
    }, []);

    // Get flat list of orgs from orgTree
    const orgs = React.useMemo(() => {
        const flatten = (items: TreeItem[]): TreeItem[] => {
            const result: TreeItem[] = [];
            items.forEach(item => {
                result.push(item);
                if (item.children && item.children.length > 0) {
                    result.push(...flatten(item.children));
                }
            });
            return result;
        };
        return flatten(orgTree);
    }, [orgTree]);

    // Build tree from teamsadminnest
    useEffect(() => {
        if (user.teamsadminnest) {
            const tree = buildTreeview(user.teamsadminnest, 1);
            setOrgTree(tree.dest);
        }
    }, [user.teamsadminnest]);

    // Reload available users when switching between admins/trainees views
    useEffect(() => {
        const loadAvailableUsersForView = async () => {
            if (!user || !user.activeOrgId || !user.teamsadminnest) return;
            
            const excludeRole = currentView === 'admins' ? 'admins' : 'trainees';
            
            try {
                const availableUsers = await DataService.loadAvailableUsers(
                    user.teamsadminnest,
                    user.activeOrgId,
                    excludeRole
                );
                teamData.setAvailableUsers(availableUsers);
            } catch (error) {
                console.error('Failed to load available users:', error);
            }
        };
        
        if (currentView === 'admins' || currentView === 'candidates') {
            loadAvailableUsersForView();
        }
    }, [currentView, user?.activeOrgId]);

    // Data is now loaded from context via App.tsx, no need to load here

    // Convert teamsadminnest to tree structure (like tr-admin)
    const buildTreeview = (data: any, id: number): { dest: TreeItem[], id: number } => {
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            return { dest: [], id };
        }
        
        const dest: TreeItem[] = [];
        let tempId = id;
        
        // Convert object to sorted array
        const items = Object.keys(data).map(key => ({ ...data[key], id: key }))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        
        items.forEach(item => {
            const key = item.id;
            const treeItem = data[key];
            if (!treeItem || !treeItem.name) return;
            
            const tempObj: TreeItem = {
                id: tempId++,
                name: treeItem.name,
                teamid: key,
                active: treeItem.active !== false
            };
            
            // Recursively build children
            if (treeItem.children && typeof treeItem.children === 'object' && Object.keys(treeItem.children).length > 0) {
                const childResult = buildTreeview(treeItem.children, tempId);
                tempObj.children = childResult.dest;
                tempId = childResult.id;
            }
            
            dest.push(tempObj);
        });
        
        return { dest, id: tempId };
    };

    const handleViewPathways = async (u: User) => {
        setViewingUser(u);
        setIsProgressModalOpen(true);
        try {
            const pathways = await DataService.getPathwaysForCandidate(u.id);
            setUserPathways(pathways);
        } catch (e) {
            console.error("Failed to fetch pathways", e);
        }
    };

    // Helper to find org name from tree
    const findOrgName = (teamid: string, tree: TreeItem[] = orgTree): string | null => {
        for (const node of tree) {
            if (node.teamid === teamid) return node.name;
            if (node.children) {
                const found = findOrgName(teamid, node.children);
                if (found) return found;
            }
        }
        return null;
    };
    
    const ActiveOrgName = activeOrgId ? findOrgName(activeOrgId) : null;

    // Helper to reload team data after adding users
    const reloadTeamData = async () => {
        if (!user || !user.activeOrgId) return;
        
        try {
            teamData.setIsLoading(true);
            
            // Load team document
            const team = await DataService.loadTeamDocument(user.activeOrgId);
            if (!team) {
                teamData.clearData();
                return;
            }

            // Load admins
            const admins = await DataService.loadAdmins(team.adminslist || []);
            teamData.setAdmins(admins);
            
            // Load trainees (candidates)
            const { candidates } = await DataService.loadTrainees(
                team.trainees || {},
                user.activeOrgIncludesSubTeams || false
            );
            teamData.setCandidates(candidates);
            
            // Reload available users for current view
            const excludeRole = currentView === 'admins' ? 'admins' : 'trainees';
            const availableUsers = await DataService.loadAvailableUsers(
                user.teamsadminnest || {},
                user.activeOrgId,
                excludeRole
            );
            teamData.setAvailableUsers(availableUsers);
            
            console.log('✅ Team data reloaded after adding user');
        } catch (error) {
            console.error('❌ Failed to reload team data:', error);
        } finally {
            teamData.setIsLoading(false);
        }
    };

    // User CRUD
    const handleOpenUserModal = async (mode: 'CREATE' | 'EDIT', userToEdit?: User) => {
        setUserModalMode(mode);
        setValidationError('');
        setTraineeTeamMemberships([]);
        
        if (mode === 'EDIT' && userToEdit) {
            setSelectedUser(userToEdit);
            setUserFormName(userToEdit.name);
            setUserFormEmail(userToEdit.email || '');
            setIsExistingUser(false);
            
            // Parse phone number to get country code and national number
            const parsed = parsePhoneNumber(userToEdit.phoneNumber);
            if (parsed) {
                setSelectedCountry(parsed.country);
                setUserFormPhone(parsed.nationalNumber);
            } else {
                setSelectedCountry(detectedCountry);
                setUserFormPhone('');
            }
            
            // Load team memberships for candidates
            if (currentView === 'candidates') {
                setIsLoadingMemberships(true);
                try {
                    const memberships = await DataService.loadTraineeTeamMemberships(
                        userToEdit.id,
                        user.teamsadminnest
                    );
                    setTraineeTeamMemberships(memberships);
                } catch (error) {
                    console.error('Failed to load trainee memberships:', error);
                } finally {
                    setIsLoadingMemberships(false);
                }
            }
        } else {
            setSelectedUser(null);
            setIsExistingUser(false);
            setUserFormName('');
            setUserFormEmail('');
            setUserFormEmailConfirm('');
            setUserFormPhone('');
            setSelectedCountry(detectedCountry);
            setSelectedExistingUsers([]);
            setUserSearchQuery('');
            setShowEmailFields(false);
            setShowPhoneFields(false);
        }
        setIsUserModalOpen(true);
    };

    const handleSaveUser = async () => {
        if (!activeOrgId) return;
        
        setValidationError('');
        setIsSubmitting(true);

        try {
            if (userModalMode === 'EDIT' && selectedUser) {
                // Edit existing user via TrainingRoom API
                if (!userFormName.trim()) {
                    setValidationError('Name is required');
                    setIsSubmitting(false);
                    return;
                }
                
                const email = userFormEmail?.trim() || '';
                const phoneNumber = userFormPhone?.trim() ? selectedCountry.dialCode + userFormPhone : '';
                
                // Require at least one contact method
                if (!email && !phoneNumber) {
                    setValidationError('Email or phone number is required');
                    setIsSubmitting(false);
                    return;
                }
                
                // Validate email format if provided
                if (email && !/.+@.+\..+/.test(email)) {
                    setValidationError('E-mail must be valid');
                    setIsSubmitting(false);
                    return;
                }
                
                // Get Firebase auth token
                const firebaseUser = auth.currentUser;
                if (!firebaseUser) {
                    setValidationError('You must be logged in');
                    setIsSubmitting(false);
                    return;
                }
                const token = await firebaseUser.getIdToken();
                
                console.log('📤 Updating user:', {
                    uid: selectedUser.id,
                    name: userFormName,
                    email,
                    phoneNumber
                });
                
                const response = await fetch(
                    'https://europe-west2-tr-staging-8964c.cloudfunctions.net/api/user/updateUserDetails',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': token
                        },
                        body: JSON.stringify({
                            uid: selectedUser.id,
                            name: userFormName,
                            email,
                            phoneNumber
                        })
                    }
                );
                
                const result = await response.json();
                console.log('📥 Update response:', result);
                
                if (result.status === 1) {
                    // Compute what changed for activity logging
                    const changes = computeChanges(
                        { 
                            name: selectedUser.name, 
                            email: selectedUser.email, 
                            phoneNumber: selectedUser.phoneNumber 
                        },
                        { name: userFormName, email, phoneNumber },
                        ['name', 'email', 'phoneNumber']
                    );
                    
                    // Optimistically update local state
                    const updatedUser = {
                        ...selectedUser,
                        name: userFormName,
                        email,
                        phoneNumber
                    };
                    
                    if (currentView === 'admins') {
                        teamData.setAdmins(teamData.admins.map(u => u.id === selectedUser.id ? updatedUser : u));
                        // Log activity with changes
                        await logAdminActivity(
                            activeOrgId,
                            'UPDATED',
                            selectedUser.id,
                            userFormName,
                            user.id,
                            user.name,
                            { changes }
                        );
                    } else {
                        teamData.setCandidates(teamData.candidates.map(u => u.id === selectedUser.id ? updatedUser : u));
                        // Log activity with changes
                        await logCandidateActivity(
                            activeOrgId,
                            'UPDATED',
                            selectedUser.id,
                            userFormName,
                            user.id,
                            user.name,
                            { changes }
                        );
                    }
                    setActivityRefreshKey(k => k + 1);
                    
                    setIsUserModalOpen(false);
                } else {
                    setValidationError(result.error || result.message || 'Failed to update user');
                }
                
                setIsSubmitting(false);
                return;
            }

            // CREATE mode
            if (isExistingUser) {
                // Add existing users to team
                if (selectedExistingUsers.length === 0) {
                    setValidationError('Please select at least one user');
                    setIsSubmitting(false);
                    return;
                }

                const db = getFirestore();
                const teamRef = doc(db, 'teams', activeOrgId);
                const currentUser = await AuthService.getCurrentUser();
                
                // Determine which field to update based on role
                const fieldName = currentView === 'admins' ? 'admins' : 'trainees';
                const updateObj: any = {};
                
                selectedExistingUsers.forEach(userId => {
                    if (currentView === 'admins') {
                        updateObj[`${fieldName}.${userId}`] = { triggerignore: false };
                    } else {
                        // For trainees (candidates)
                        updateObj[`${fieldName}.${userId}`] = { role: 'CANDIDATE' };
                    }
                });
                
                updateObj.updateby = currentUser?.uid || 'unknown';
                updateObj.updatedate = Date.now();
                
                await updateDoc(teamRef, updateObj);
                
                // Optimistically add users to local state
                const usersToAdd = teamData.availableUsers.filter(u => selectedExistingUsers.includes(u.id));
                if (currentView === 'admins') {
                    const newAdmins = usersToAdd.map(u => ({
                        id: u.id,
                        name: u.name,
                        email: u.email || '',
                        phoneNumber: u.phoneNumber,
                        isRemovable: true, // New admin at current team level is removable
                    }));
                    teamData.setAdmins([...teamData.admins, ...newAdmins]);
                } else {
                    const newCandidates = usersToAdd.map(u => ({
                        id: u.id,
                        name: u.name,
                        email: u.email || '',
                        phoneNumber: u.phoneNumber,
                        role: 'CANDIDATE',
                    }));
                    teamData.setCandidates([...teamData.candidates, ...newCandidates]);
                }
                // Remove added users from available list
                teamData.setAvailableUsers(teamData.availableUsers.filter(u => !selectedExistingUsers.includes(u.id)));
                
                // Log activity for each added user
                for (const addedUser of usersToAdd) {
                    await logActivity(
                        activeOrgId,
                        currentView === 'admins' ? 'ADMIN' : 'CANDIDATE',
                        'CREATED',
                        addedUser.id,
                        addedUser.name,
                        user.id,
                        user.name
                    );
                }
                
                setIsUserModalOpen(false);
            } else {
                // Create new user via TrainingRoom API
                if (!userFormName) {
                    setValidationError('Name is required');
                    setIsSubmitting(false);
                    return;
                }
                
                const phoneNumber = (showPhoneFields && userFormPhone && userFormPhone.trim()) 
                    ? selectedCountry.dialCode + userFormPhone 
                    : '';
                const email = (showEmailFields && userFormEmail && userFormEmail.trim()) 
                    ? userFormEmail.trim() 
                    : '';
                
                if (!phoneNumber && !email) {
                    setValidationError('You need to add and fill in either email or phone number');
                    setIsSubmitting(false);
                    return;
                }
                
                // Validate email format if provided
                if (email && !/.+@.+\..+/.test(email)) {
                    setValidationError('E-mail must be valid');
                    setIsSubmitting(false);
                    return;
                }
                
                // Only validate email confirmation if email is provided
                if (email && email !== userFormEmailConfirm) {
                    setValidationError('Email addresses do not match');
                    setIsSubmitting(false);
                    return;
                }
                
                const userType = currentView === 'admins' ? 'admin' : 'trainee';
                const role = currentView === 'admins' ? undefined : 'CANDIDATE';
                
                // Get Firebase auth user for token
                const firebaseUser = auth.currentUser;
                if (!firebaseUser) {
                    setValidationError('You must be logged in to add users');
                    setIsSubmitting(false);
                    return;
                }
                const token = await firebaseUser.getIdToken();
                
                const requestBody = {
                    userList: [{
                        email: email,
                        firstName: userFormName,
                        userType,
                        role,
                        phoneNumber
                    }],
                    teamid: activeOrgId
                };
                
                console.log('📤 Sending user creation request:', {
                    email,
                    firstName: userFormName,
                    userType,
                    role,
                    phoneNumber,
                    teamid: activeOrgId
                });
                
                const response = await fetch(
                    'https://europe-west2-tr-staging-8964c.cloudfunctions.net/api/user/addNewUserWithTeam',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': token || ''
                        },
                        body: JSON.stringify(requestBody)
                    }
                );
                
                const result = await response.json();
                console.log('📥 User creation response:', result);
                
                if (result[0]?.status === 1) {
                    // Optimistically add new user to local state
                    const newUser: User = {
                        id: result[0].uid,
                        name: result[0].displayName || userFormName,
                        email: result[0].email || email,
                        phoneNumber: phoneNumber || '',
                        role: role
                    };
                    
                    if (currentView === 'admins') {
                        teamData.setAdmins([...teamData.admins, newUser]);
                    } else {
                        teamData.setCandidates([...teamData.candidates, newUser]);
                    }
                    
                    // Log activity
                    await logActivity(
                        activeOrgId,
                        currentView === 'admins' ? 'ADMIN' : 'CANDIDATE',
                        'CREATED',
                        newUser.id,
                        newUser.name,
                        user.id,
                        user.name
                    );
                    
                    setIsUserModalOpen(false);
                } else {
                    setValidationError(result[0]?.result || 'Failed to add user');
                }
            }
        } catch (error: any) {
            console.error('Failed to save user:', error);
            console.error('Error details:', {
                message: error?.message,
                name: error?.name,
                stack: error?.stack,
                toString: error?.toString(),
                fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
            });
            setValidationError(error?.message || error?.toString() || 'Failed to save user');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveAdmin = async (userId: string, userName: string) => {
        if (!activeOrgId) {
            alert('No active organization selected');
            return;
        }
        
        if (confirm(`Are you sure you want to remove ${userName} as an admin? They will be removed from this team and all sub-teams.`)) {
            try {
                const result = await DataService.removeAdmin(activeOrgId, userId);
                if (result.success) {
                    alert(result.message + (result.teamsRemoved ? ` (${result.teamsRemoved} teams affected)` : ''));
                    // Optimistically remove admin from local state
                    teamData.setAdmins(teamData.admins.filter(a => a.id !== userId));
                    
                    // Log activity
                    await logActivity(
                        activeOrgId,
                        'ADMIN',
                        'DELETED',
                        userId,
                        userName,
                        user.id,
                        user.name
                    );
                } else {
                    alert('Error: ' + result.message);
                }
            } catch (error: any) {
                console.error('Failed to remove admin:', error);
                alert('Failed to remove admin: ' + (error.message || 'Unknown error'));
            }
        }
    };

    const handleMoveRemoveCandidates = async () => {
        if (!activeOrgId) {
            alert('No active organization selected');
            return;
        }
        
        if (selectedCandidates.length === 0) {
            alert('No candidates selected');
            return;
        }
        
        if (moveRemoveAction === 'Move' && !moveTargetTeamId) {
            alert('Please select a target team');
            return;
        }
        
        try {
            const uids = selectedCandidates.map(c => c.id);
            const result = await DataService.moveRemoveTrainees(
                uids,
                moveRemoveAction,
                moveRemoveAction === 'Move' ? moveTargetTeamId : null,
                activeOrgId
            );
            
            if (result.status === 1) {
                // Build result message
                let message = `The following candidates were ${moveRemoveAction === 'Move' ? 'moved' : 'removed'}:\n`;
                selectedCandidates.forEach(c => {
                    const status = result[c.id] || 'Processed';
                    message += `- ${c.name}: ${status}\n`;
                });
                alert(message);
                
                // Log activity for each affected candidate
                for (const candidate of selectedCandidates) {
                    await logActivity(
                        activeOrgId,
                        'CANDIDATE',
                        'DELETED',
                        candidate.id,
                        candidate.name,
                        user.id,
                        user.name,
                        { action: moveRemoveAction }
                    );
                }
                
                // Clear selection and close modal
                setSelectedCandidates([]);
                setIsMoveRemoveModalOpen(false);
                setMoveRemoveAction('Move');
                setMoveTargetTeamId('');
                
                // Optimistically remove affected candidates from local state
                const affectedIds = uids;
                teamData.setCandidates(teamData.candidates.filter(c => !affectedIds.includes(c.id)));
            } else {
                alert('Error: ' + (result.error || result.message || 'Operation failed'));
            }
        } catch (error: any) {
            console.error('Failed to move/remove candidates:', error);
            alert('Failed to process candidates: ' + (error.message || 'Unknown error'));
        }
    };

    const handleToggleCandidateSelection = (candidate: { id: string; name: string }) => {
        setSelectedCandidates(prev => {
            const exists = prev.find(c => c.id === candidate.id);
            if (exists) {
                return prev.filter(c => c.id !== candidate.id);
            } else {
                return [...prev, candidate];
            }
        });
    };

    const handleSelectAllCandidates = () => {
        const directCandidates = teamData.candidates.filter(c => !!(c as any).role);
        if (selectedCandidates.length === directCandidates.length) {
            // Deselect all
            setSelectedCandidates([]);
        } else {
            // Select all direct candidates
            setSelectedCandidates(directCandidates.map(c => ({ id: c.id, name: c.name })));
        }
    };

    // Get teams list for Move dropdown
    const teamsListForMove = useMemo(() => {
        if (!user.teamsadminnest) return [];
        const teams: Array<{ id: string; name: string }> = [];
        const extractTeams = (data: any) => {
            if (!data || typeof data !== 'object') return;
            Object.entries(data).forEach(([key, value]: [string, any]) => {
                if (value && typeof value === 'object' && value.name) {
                    teams.push({ id: key, name: value.name });
                    if (value.children) {
                        extractTeams(value.children);
                    }
                }
            });
        };
        extractTeams(user.teamsadminnest);
        return teams.sort((a, b) => a.name.localeCompare(b.name));
    }, [user.teamsadminnest]);

    const handleSaveOrg = async () => {
        if (!orgFormName) {
            alert("Organization name is required.");
            return;
        }
        
        try {
            // TODO: Implement organization save functionality
            console.log("Saving org:", { name: orgFormName, desc: orgFormDesc, parentId: orgFormParentId });
            alert("Organization save functionality not yet implemented.");
            setIsOrgModalOpen(false);
        } catch (error) {
            console.error("Failed to save organization", error);
            alert("Failed to save organization.");
        }
    };

    // Team Edit/Add Handlers
    const handleOpenEditTeam = (node: OrgTreeItemType) => {
        setTeamModalMode('EDIT');
        setSelectedTeamNode({ teamid: node.teamid, name: node.name, active: node.active });
        setTeamFormName(node.name);
        setIsTeamModalOpen(true);
    };

    const handleOpenAddSubTeam = (parentNode: OrgTreeItemType) => {
        setTeamModalMode('ADD');
        setSelectedTeamNode({ teamid: parentNode.teamid, name: parentNode.name, active: parentNode.active, parentTeamId: parentNode.teamid });
        setTeamFormName('');
        setIsTeamModalOpen(true);
    };

    const handleToggleTeamActive = async (node: OrgTreeItemType) => {
        const newActive = !node.active;
        const action = newActive ? 'activate' : 'deactivate';
        if (!confirm(`Are you sure you want to ${action} "${node.name}"?`)) return;
        
        try {
            const db = getFirestore();
            const currentUser = await AuthService.getCurrentUser();
            await updateDoc(doc(db, 'teams', node.teamid), {
                active: newActive,
                updateby: currentUser?.uid || 'unknown',
                updatedate: Date.now()
            });
            
            // Optimistically update local tree
            const updateNodeActive = (nodes: TreeItem[]): TreeItem[] => {
                return nodes.map(n => {
                    if (n.teamid === node.teamid) {
                        return { ...n, active: newActive };
                    }
                    if (n.children) {
                        return { ...n, children: updateNodeActive(n.children) };
                    }
                    return n;
                });
            };
            setOrgTree(updateNodeActive(orgTree));
            
            alert(`Team "${node.name}" has been ${newActive ? 'activated' : 'deactivated'}.`);
        } catch (error) {
            console.error('Failed to toggle team active status:', error);
            alert('Failed to update team status.');
        }
    };

    const handleSaveTeam = async () => {
        if (!teamFormName.trim()) {
            alert('Team name is required.');
            return;
        }
        if (!selectedTeamNode) return;
        
        setIsTeamSaving(true);
        
        try {
            const db = getFirestore();
            const currentUser = await AuthService.getCurrentUser();
            
            if (teamModalMode === 'EDIT') {
                // Update existing team name
                await updateDoc(doc(db, 'teams', selectedTeamNode.teamid), {
                    name: teamFormName.trim(),
                    updateby: currentUser?.uid || 'unknown',
                    updatedate: Date.now()
                });
                
                // Optimistically update local tree
                const updateNodeName = (nodes: TreeItem[]): TreeItem[] => {
                    return nodes.map(n => {
                        if (n.teamid === selectedTeamNode.teamid) {
                            return { ...n, name: teamFormName.trim() };
                        }
                        if (n.children) {
                            return { ...n, children: updateNodeName(n.children) };
                        }
                        return n;
                    });
                };
                setOrgTree(updateNodeName(orgTree));
                
                alert(`Team name updated to "${teamFormName.trim()}".`);
            } else {
                // Add new sub-team
                const { v4: uuidv4 } = await import('uuid');
                const newTeamId = uuidv4();
                const newTeam = {
                    active: true,
                    admins: { [currentUser?.uid || 'unknown']: { triggerignore: true } },
                    trainees: {},
                    createdby: currentUser?.uid || 'unknown',
                    name: teamFormName.trim(),
                    parent: selectedTeamNode.teamid,
                    updateby: currentUser?.uid || 'unknown',
                    updatedate: Date.now()
                };
                
                await setDoc(doc(db, 'teams', newTeamId), newTeam);
                
                // Optimistically add to local tree
                const addChildToNode = (nodes: TreeItem[]): TreeItem[] => {
                    return nodes.map(n => {
                        if (n.teamid === selectedTeamNode.teamid) {
                            const newChild: TreeItem = {
                                id: Date.now(),
                                name: teamFormName.trim(),
                                teamid: newTeamId,
                                active: true,
                                children: []
                            };
                            return { ...n, children: [...(n.children || []), newChild] };
                        }
                        if (n.children) {
                            return { ...n, children: addChildToNode(n.children) };
                        }
                        return n;
                    });
                };
                setOrgTree(addChildToNode(orgTree));
                
                alert(`Sub-team "${teamFormName.trim()}" created successfully.`);
            }
            
            setIsTeamModalOpen(false);
        } catch (error) {
            console.error('Failed to save team:', error);
            alert('Failed to save team.');
        } finally {
            setIsTeamSaving(false);
        }
    };

    const OrgManager = () => {
        const [teamSearch, setTeamSearch] = useState('');
        const [expandedTeams, setExpandedTeams] = useState<Set<string>>(() => {
            // Initialize with all teams expanded
            const allTeamIds = new Set<string>();
            const collectIds = (nodes: typeof orgTree) => {
                nodes.forEach(node => {
                    allTeamIds.add(node.teamid);
                    if (node.children) collectIds(node.children);
                });
            };
            collectIds(orgTree);
            return allTeamIds;
        });
        const [showSearchResults, setShowSearchResults] = useState(false);

        // Flatten tree to get all teams with their ancestor paths
        const flattenedTeams = useMemo(() => {
            const result: Array<{ teamid: string; name: string; path: string; ancestors: string[] }> = [];
            const traverse = (nodes: typeof orgTree, ancestors: string[], pathParts: string[]) => {
                nodes.forEach(node => {
                    const currentPath = [...pathParts, node.name].join(' > ');
                    result.push({
                        teamid: node.teamid,
                        name: node.name,
                        path: currentPath,
                        ancestors: ancestors
                    });
                    if (node.children && node.children.length > 0) {
                        traverse(node.children, [...ancestors, node.teamid], [...pathParts, node.name]);
                    }
                });
            };
            traverse(orgTree, [], []);
            return result;
        }, [orgTree]);

        // Filter teams based on search
        const searchResults = useMemo(() => {
            if (!teamSearch.trim()) return [];
            const query = teamSearch.toLowerCase();
            return flattenedTeams.filter(t => t.name.toLowerCase().includes(query)).slice(0, 10);
        }, [teamSearch, flattenedTeams]);

        const handleToggleExpand = (teamid: string) => {
            setExpandedTeams(prev => {
                const next = new Set(prev);
                if (next.has(teamid)) {
                    next.delete(teamid);
                } else {
                    next.add(teamid);
                }
                return next;
            });
        };

        const handleSelectSearchResult = (team: { teamid: string; ancestors: string[] }) => {
            // Expand all ancestors to reveal the team
            setExpandedTeams(prev => {
                const next = new Set(prev);
                team.ancestors.forEach(ancestorId => next.add(ancestorId));
                return next;
            });
            // Switch to the selected team
            onSwitchOrg(team.teamid, false);
            // Clear search
            setTeamSearch('');
            setShowSearchResults(false);
        };

        return (
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-lg font-bold text-aero-900">Organizations</h3>
                        <p className="text-sm text-aero-500">Your team hierarchy from TrainingRoom</p>
                    </div>
                </div>
                
                {/* Team Search */}
                <div className="relative">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aero-400" />
                        <input
                            type="text"
                            placeholder="Search teams..."
                            value={teamSearch}
                            onChange={(e) => { setTeamSearch(e.target.value); setShowSearchResults(true); }}
                            onFocus={() => setShowSearchResults(true)}
                            onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                            className="w-full pl-10 pr-10 py-2 border border-aero-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aero-500 focus:border-transparent"
                        />
                        {teamSearch && (
                            <button
                                onClick={() => { setTeamSearch(''); setShowSearchResults(false); }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-aero-400 hover:text-aero-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    
                    {/* Search Results Dropdown */}
                    {showSearchResults && searchResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-aero-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {searchResults.map(team => (
                                <button
                                    key={team.teamid}
                                    onClick={() => handleSelectSearchResult(team)}
                                    className="w-full px-4 py-2 text-left hover:bg-aero-50 flex flex-col border-b border-aero-100 last:border-b-0"
                                >
                                    <span className="font-medium text-aero-900">{team.name}</span>
                                    <span className="text-xs text-aero-500 truncate">{team.path}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {showSearchResults && teamSearch && searchResults.length === 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-aero-200 rounded-lg shadow-lg p-4 text-center text-aero-500">
                            No teams found matching "{teamSearch}"
                        </div>
                    )}
                </div>

                <div className="bg-white p-4 rounded-xl border border-aero-200 min-h-[300px]">
                    {orgTree.length > 0 ? (
                        <ul className="space-y-1">
                            {orgTree.map(node => (
                                <OrgTreeItem 
                                    key={node.id} 
                                    node={node as OrgTreeItemType} 
                                    depth={0} 
                                    activeOrgId={activeOrgId}
                                    onSwitchOrg={onSwitchOrg}
                                    expandedTeams={expandedTeams}
                                    onToggleExpand={handleToggleExpand}
                                    onEditTeam={handleOpenEditTeam}
                                    onAddSubTeam={handleOpenAddSubTeam}
                                    onToggleActive={handleToggleTeamActive}
                                />
                            ))}
                        </ul>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-aero-400">
                            <Building2 className="w-12 h-12 mb-2 opacity-20" />
                            <p>No organizations found.</p>
                            <p className="text-xs mt-1">Organizations are managed in TrainingRoom Admin</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const NoOrgSelected = () => (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-aero-50 rounded-full flex items-center justify-center text-aero-300 mb-4"><Building2 className="w-8 h-8" /></div>
            <h3 className="text-lg font-bold text-aero-900">No Organization Selected</h3>
            <p className="text-aero-500 max-w-xs mt-1">Please select an organization from the "Organizations" view to manage users.</p>
        </div>
    );

    const renderContent = () => {
        switch (currentView) {
            case 'dashboard': return <DashboardOverview activeOrgId={activeOrgId} activityRefreshKey={activityRefreshKey} />;
            case 'pathways': return <PathwaysManager activeOrgId={activeOrgId} orgCandidates={teamData.candidates} initialPathwayId={pendingPathwayId} onPathwaySelected={() => setPendingPathwayId(null)} />;
            case 'ojt': return <OjtScheduler activeOrgId={activeOrgId} />;
            case 'orgs': return <OrgManager />;
            case 'candidates': return activeOrgId ? (
                <CandidatesManager 
                    candidates={teamData.candidates}
                    isLoading={teamData.isLoading}
                    activeOrgName={ActiveOrgName || 'this organization'}
                    selectedCandidates={selectedCandidates}
                    onOpenUserModal={handleOpenUserModal}
                    onViewPathways={handleViewPathways}
                    onToggleCandidateSelection={handleToggleCandidateSelection}
                    onSelectAllCandidates={handleSelectAllCandidates}
                    onClearSelection={() => setSelectedCandidates([])}
                    onOpenMoveRemoveModal={() => setIsMoveRemoveModalOpen(true)}
                />
            ) : <NoOrgSelected />;
            case 'admins': return activeOrgId ? (
                <AdminsManager 
                    admins={teamData.admins}
                    isLoading={teamData.isLoading}
                    activeOrgName={ActiveOrgName || 'this organization'}
                    onOpenUserModal={handleOpenUserModal}
                    onRemoveAdmin={handleRemoveAdmin}
                />
            ) : <NoOrgSelected />;
            case 'notifications': return <div className="flex flex-col items-center justify-center h-[60vh] text-aero-300"><AlertCircle className="w-10 h-10 mb-4" /><h3 className="text-2xl font-bold">Notifications</h3></div>;
            case 'messaging': return <div className="flex flex-col items-center justify-center h-[60vh] text-aero-300"><Users className="w-10 h-10 mb-4" /><h3 className="text-2xl font-bold">Messaging</h3></div>;
            default: return <DashboardOverview activeOrgId={activeOrgId} activityRefreshKey={activityRefreshKey} />;
        }
    };

    return (
        <div className="pb-12">
            {renderContent()}
            
            {/* Org Edit/Create Modal */}
            <Modal isOpen={isOrgModalOpen} onClose={() => setIsOrgModalOpen(false)} title={editingOrg ? "Edit Organization" : "Register New Organization"}>
                <div className="space-y-4">
                    <Input label="Organization Name" value={orgFormName} onChange={e => setOrgFormName(e.target.value)} placeholder="e.g. West Coast Logistics" />
                    <Input label="Business Description" value={orgFormDesc} onChange={e => setOrgFormDesc(e.target.value)} placeholder="Operational focus or region details" />
                    {!editingOrg && (
                        <div className="w-full space-y-1">
                            <label className="text-sm font-medium text-aero-900 leading-none">Parent Organization</label>
                            <select className={`flex h-10 w-full rounded-md border border-aero-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-aero-500 ${isAddingSubTeam ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`} value={orgFormParentId} onChange={e => setOrgFormParentId(e.target.value)} disabled={isAddingSubTeam}>
                                <option value="ROOT">No Parent (Top Level Organization)</option>
                                {orgs.map(org => (<option key={org.id} value={org.id}>{org.name}</option>))}
                            </select>
                        </div>
                    )}
                    <div className="flex justify-end gap-2 mt-6"><Button variant="ghost" onClick={() => setIsOrgModalOpen(false)}>Cancel</Button><Button onClick={handleSaveOrg}>Confirm Registration</Button></div>
                </div>
            </Modal>

            {/* Team Edit/Add Modal */}
            <Modal 
                isOpen={isTeamModalOpen} 
                onClose={() => setIsTeamModalOpen(false)} 
                title={teamModalMode === 'ADD' ? 'Add Sub Team' : 'Edit Team'}
            >
                <div className="space-y-4">
                    {teamModalMode === 'EDIT' && selectedTeamNode && (
                        <div className="bg-aero-50 p-3 rounded-lg text-xs text-aero-600 border border-aero-100">
                            <span className="font-medium">Team ID:</span> {selectedTeamNode.teamid}
                        </div>
                    )}
                    
                    {teamModalMode === 'ADD' && selectedTeamNode && (
                        <div className="bg-aero-50 p-3 rounded-lg text-xs text-aero-600 border border-aero-100">
                            <span className="font-medium">Parent Team:</span> {selectedTeamNode.name}
                        </div>
                    )}
                    
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-aero-900">Team Name</label>
                        <input
                            type="text"
                            value={teamFormName}
                            onChange={(e) => setTeamFormName(e.target.value)}
                            placeholder="Enter team name"
                            className="w-full px-3 py-2 border border-aero-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-aero-500"
                            autoFocus
                        />
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                        <Button variant="ghost" onClick={() => setIsTeamModalOpen(false)} disabled={isTeamSaving}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveTeam} disabled={isTeamSaving || !teamFormName.trim()}>
                            {isTeamSaving ? 'Saving...' : teamModalMode === 'ADD' ? 'Add Team' : 'Save'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Move/Remove Candidates Modal */}
            <Modal 
                isOpen={isMoveRemoveModalOpen} 
                onClose={() => {
                    setIsMoveRemoveModalOpen(false);
                    setMoveRemoveAction('Move');
                    setMoveTargetTeamId('');
                }} 
                title="Move/Remove Candidates"
            >
                <div className="space-y-4">
                    {selectedCandidates.length === 0 ? (
                        <div className="text-center py-8 text-aero-400 italic">
                            No candidates selected
                        </div>
                    ) : (
                        <>
                            <div className="bg-aero-50 p-3 rounded-lg border border-aero-100">
                                <p className="text-sm font-medium text-aero-700 mb-2">
                                    The following candidates will be processed:
                                </p>
                                <ul className="text-sm text-aero-600 space-y-1 max-h-40 overflow-y-auto">
                                    {selectedCandidates.map(c => (
                                        <li key={c.id} className="flex items-center gap-2">
                                            <UserCircle className="w-4 h-4 text-aero-400" />
                                            {c.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="space-y-3">
                                <div className="w-full space-y-1">
                                    <label className="text-sm font-medium text-aero-900 leading-none">Action</label>
                                    <select 
                                        className="flex h-10 w-full rounded-md border border-aero-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-aero-500"
                                        value={moveRemoveAction}
                                        onChange={(e) => setMoveRemoveAction(e.target.value as 'Move' | 'Remove')}
                                    >
                                        <option value="Move">Move</option>
                                        <option value="Remove">Remove</option>
                                    </select>
                                </div>

                                {moveRemoveAction === 'Move' && (
                                    <div className="w-full space-y-1">
                                        <label className="text-sm font-medium text-aero-900 leading-none">Target Team</label>
                                        <select 
                                            className="flex h-10 w-full rounded-md border border-aero-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-aero-500"
                                            value={moveTargetTeamId}
                                            onChange={(e) => setMoveTargetTeamId(e.target.value)}
                                        >
                                            <option value="">Select a team...</option>
                                            {teamsListForMove.map(team => (
                                                <option key={team.id} value={team.id} disabled={team.id === activeOrgId}>
                                                    {team.name}{team.id === activeOrgId ? ' (current)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {moveRemoveAction === 'Remove' && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                        <p className="text-sm text-red-700">
                                            <strong>Warning:</strong> This will remove the selected candidates from this team. 
                                            They will also be removed from any parent teams.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-2 mt-6">
                                <Button 
                                    variant="ghost" 
                                    onClick={() => {
                                        setIsMoveRemoveModalOpen(false);
                                        setMoveRemoveAction('Move');
                                        setMoveTargetTeamId('');
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant={moveRemoveAction === 'Remove' ? 'primary' : 'primary'}
                                    onClick={handleMoveRemoveCandidates}
                                    disabled={moveRemoveAction === 'Move' && !moveTargetTeamId}
                                    className={moveRemoveAction === 'Remove' ? 'bg-red-600 hover:bg-red-700' : ''}
                                >
                                    {moveRemoveAction === 'Move' ? (
                                        <>
                                            <MoveHorizontal className="w-4 h-4 mr-2" />
                                            Move Candidates
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Remove Candidates
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            {/* User CRUD Modal */}
            <Modal isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)} title={`${userModalMode === 'CREATE' ? 'Add' : 'Edit'} ${currentView === 'admins' ? 'Admin' : 'Candidate'}`}>
                <div className="space-y-4">
                    {userModalMode === 'CREATE' && (
                        <>
                            <div className="bg-aero-50 p-3 rounded-lg text-xs text-aero-600 mb-4 border border-aero-100">
                                Adding a user here will add them to <span className="font-bold">{ActiveOrgName || 'this organization'}</span>.
                            </div>
                            
                            {/* Toggle Existing/New User */}
                            <div className="flex items-center gap-3 p-3 bg-white border border-aero-200 rounded-lg">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={isExistingUser} 
                                        onChange={(e) => setIsExistingUser(e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-aero-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-aero-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-aero-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-aero-600"></div>
                                </label>
                                <span className="text-sm font-medium text-aero-900">
                                    {isExistingUser ? 'Existing User' : 'New User'}
                                </span>
                            </div>
                        </>
                    )}
                    
                    {validationError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {validationError}
                        </div>
                    )}
                    
                    {userModalMode === 'CREATE' && isExistingUser ? (
                        // Existing User Selection
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-aero-900">Select Users</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={userSearchQuery}
                                    onChange={(e) => setUserSearchQuery(e.target.value)}
                                    placeholder="Search by name, email, or phone..."
                                    className="w-full px-4 py-2 border border-aero-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-aero-500"
                                />
                                {userSearchQuery && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-aero-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {teamData.availableUsers
                                            .filter(u => 
                                                u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                                                u.email.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                                                (u.phoneNumber && u.phoneNumber.includes(userSearchQuery))
                                            )
                                            .map(u => (
                                                <button
                                                    key={u.id}
                                                    type="button"
                                                    onClick={() => {
                                                        if (!selectedExistingUsers.includes(u.id)) {
                                                            setSelectedExistingUsers([...selectedExistingUsers, u.id]);
                                                        }
                                                        setUserSearchQuery('');
                                                    }}
                                                    className="w-full text-left px-4 py-2 hover:bg-aero-50 border-b border-aero-100 last:border-b-0"
                                                >
                                                    <div className="font-medium text-aero-900">{u.name}</div>
                                                    <div className="text-xs text-aero-500">
                                                        {u.email} {u.phoneNumber && `• ${u.phoneNumber}`}
                                                    </div>
                                                </button>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                            {selectedExistingUsers.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {selectedExistingUsers.map(userId => {
                                        const user = teamData.availableUsers.find(u => u.id === userId);
                                        return user ? (
                                            <div key={userId} className="flex items-center gap-2 bg-aero-100 text-aero-900 px-3 py-1 rounded-full text-sm">
                                                <span>{user.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedExistingUsers(selectedExistingUsers.filter(id => id !== userId))}
                                                    className="hover:text-red-600"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            )}
                        </div>
                    ) : (
                        // New User or Edit User Form
                        <>
                            {userModalMode === 'EDIT' && selectedUser && (
                                <div className="bg-aero-50 p-3 rounded-lg text-xs text-aero-600 mb-4 border border-aero-100">
                                    <span className="font-medium">UID:</span> {selectedUser.id}
                                </div>
                            )}
                            
                            <Input 
                                label="Full Name" 
                                value={userFormName} 
                                onChange={e => setUserFormName(e.target.value)} 
                                placeholder="John Doe" 
                            />
                            
                            {userModalMode === 'CREATE' && (
                                <div className="space-y-3">
                                    <div className="text-sm text-aero-600 font-medium">Contact Information (at least one required)</div>
                                    
                                    {/* Email Button/Fields */}
                                    {!showEmailFields ? (
                                        <Button 
                                            variant="secondary" 
                                            onClick={() => setShowEmailFields(true)}
                                            className="w-full"
                                        >
                                            + Add Email Address
                                        </Button>
                                    ) : (
                                        <div className="space-y-3 p-4 bg-aero-50 rounded-lg border border-aero-200">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-medium text-aero-900">Email Address</span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowEmailFields(false);
                                                        setUserFormEmail('');
                                                        setUserFormEmailConfirm('');
                                                    }}
                                                    className="text-xs text-red-600 hover:text-red-800"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                            <Input 
                                                label="Email" 
                                                value={userFormEmail} 
                                                onChange={e => setUserFormEmail(e.target.value)} 
                                                placeholder="john@example.com"
                                                type="email"
                                            />
                                            <Input 
                                                label="Confirm Email" 
                                                value={userFormEmailConfirm} 
                                                onChange={e => setUserFormEmailConfirm(e.target.value)} 
                                                placeholder="john@example.com"
                                                type="email"
                                            />
                                        </div>
                                    )}
                                    
                                    {/* Phone Button/Fields */}
                                    {!showPhoneFields ? (
                                        <Button 
                                            variant="secondary" 
                                            onClick={() => setShowPhoneFields(true)}
                                            className="w-full"
                                        >
                                            + Add Phone Number
                                        </Button>
                                    ) : (
                                        <div className="space-y-3 p-4 bg-aero-50 rounded-lg border border-aero-200">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-medium text-aero-900">Phone Number</span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowPhoneFields(false);
                                                        setUserFormPhone('');
                                                    }}
                                                    className="text-xs text-red-600 hover:text-red-800"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-aero-900">Country Code</label>
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                                        className="w-full px-4 py-2 border border-aero-300 rounded-lg text-left flex justify-between items-center hover:border-aero-400 focus:outline-none focus:ring-2 focus:ring-aero-500"
                                                    >
                                                        <span>{selectedCountry.dialCode} {selectedCountry.name}</span>
                                                        <ChevronDown className="w-4 h-4 text-aero-400" />
                                                    </button>
                                                    {showCountryDropdown && (
                                                        <div className="absolute z-10 w-full mt-1 bg-white border border-aero-200 rounded-lg shadow-lg">
                                                            <input
                                                                type="text"
                                                                placeholder="Search countries..."
                                                                value={countrySearch}
                                                                onChange={(e) => setCountrySearch(e.target.value)}
                                                                className="w-full px-4 py-2 border-b border-aero-200 focus:outline-none"
                                                            />
                                                            <div className="max-h-60 overflow-y-auto">
                                                                {countries
                                                                    .filter(c => 
                                                                        c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                                                        c.dialCode.includes(countrySearch)
                                                                    )
                                                                    .map((country) => (
                                                                        <button
                                                                            key={country.iso2}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSelectedCountry(country);
                                                                                setShowCountryDropdown(false);
                                                                                setCountrySearch('');
                                                                            }}
                                                                            className="w-full text-left px-4 py-2 hover:bg-aero-50 border-b border-aero-100 last:border-b-0"
                                                                        >
                                                                            {country.dialCode} {country.name}
                                                                        </button>
                                                                    ))
                                                                }
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <Input 
                                                label="Mobile/Cell Number" 
                                                value={userFormPhone} 
                                                onChange={e => setUserFormPhone(e.target.value.replace(/[^0-9]/g, ''))} 
                                                placeholder="1234567890"
                                                type="tel"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {userModalMode === 'EDIT' && (
                                <div className="space-y-4">
                                    <Input 
                                        label="Email Address (leave blank to delete)" 
                                        value={userFormEmail} 
                                        onChange={e => setUserFormEmail(e.target.value)} 
                                        type="email"
                                    />
                                    
                                    {/* Phone Number */}
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-aero-900">Country Code</label>
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                                className="w-full px-4 py-2 border border-aero-300 rounded-lg text-left flex justify-between items-center hover:border-aero-400 focus:outline-none focus:ring-2 focus:ring-aero-500"
                                            >
                                                <span>{selectedCountry.dialCode} {selectedCountry.name}</span>
                                                <ChevronDown className="w-4 h-4 text-aero-400" />
                                            </button>
                                            {showCountryDropdown && (
                                                <div className="absolute z-10 w-full mt-1 bg-white border border-aero-200 rounded-lg shadow-lg">
                                                    <input
                                                        type="text"
                                                        placeholder="Search countries..."
                                                        value={countrySearch}
                                                        onChange={(e) => setCountrySearch(e.target.value)}
                                                        className="w-full px-4 py-2 border-b border-aero-200 focus:outline-none"
                                                    />
                                                    <div className="max-h-60 overflow-y-auto">
                                                        {countries
                                                            .filter(c => 
                                                                c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                                                c.dialCode.includes(countrySearch)
                                                            )
                                                            .map((country) => (
                                                                <button
                                                                    key={country.iso2}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSelectedCountry(country);
                                                                        setShowCountryDropdown(false);
                                                                        setCountrySearch('');
                                                                    }}
                                                                    className="w-full text-left px-4 py-2 hover:bg-aero-50 border-b border-aero-100 last:border-b-0"
                                                                >
                                                                    {country.dialCode} {country.name}
                                                                </button>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <Input 
                                        label="Mobile/Cell Number (leave blank to delete)" 
                                        value={userFormPhone} 
                                        onChange={e => setUserFormPhone(e.target.value.replace(/[^0-9]/g, ''))} 
                                        type="tel"
                                    />
                                    <p className="text-xs text-aero-500">At least one contact method (email or phone) is required.</p>
                                    
                                    {/* Admin Team Access - Show only for admins */}
                                    {currentView === 'admins' && selectedUser && (selectedUser as any).teamsadminnest && (
                                        <div className="mt-4 pt-4 border-t border-aero-200">
                                            <h4 className="text-sm font-medium text-aero-900 mb-3 flex items-center gap-2">
                                                <Building2 className="w-4 h-4" />
                                                Team Access (within your hierarchy)
                                            </h4>
                                            <div className="bg-aero-50 rounded-lg border border-aero-100 p-3 max-h-60 overflow-y-auto">
                                                {(() => {
                                                    // Filter admin's teamsadminnest to only show teams that appear anywhere in current user's teamsadminnest
                                                    const adminNest = (selectedUser as any).teamsadminnest;
                                                    const userNest = user.teamsadminnest || {};
                                                    
                                                    // Recursively collect all team IDs from the user's teamsadminnest
                                                    const collectAllTeamIds = (nest: any): Set<string> => {
                                                        const ids = new Set<string>();
                                                        if (!nest || typeof nest !== 'object') return ids;
                                                        
                                                        Object.entries(nest).forEach(([teamId, teamData]: [string, any]) => {
                                                            if (!teamData || typeof teamData !== 'object' || !teamData.name) return;
                                                            
                                                            // Add this team ID
                                                            ids.add(teamId);
                                                            
                                                            // Recursively add children from the children property
                                                            if (teamData.children && typeof teamData.children === 'object') {
                                                                const childIds = collectAllTeamIds(teamData.children);
                                                                childIds.forEach(id => ids.add(id));
                                                            }
                                                        });
                                                        
                                                        return ids;
                                                    };
                                                    
                                                    const userTeamIds = collectAllTeamIds(userNest);
                                                    const visibleTeams = Object.keys(adminNest).filter(teamId => userTeamIds.has(teamId));
                                                    
                                                    if (visibleTeams.length === 0) {
                                                        return (
                                                            <p className="text-xs text-aero-400 italic">
                                                                No team access within your hierarchy
                                                            </p>
                                                        );
                                                    }
                                                    
                                                    // Recursive function to render team tree from children object
                                                    const renderTeamTree = (childrenObj: any, depth: number = 0): React.ReactNode => {
                                                        if (!childrenObj || typeof childrenObj !== 'object') return null;
                                                        
                                                        return Object.entries(childrenObj).map(([teamId, teamData]: [string, any]) => {
                                                            // Skip if not a valid team object
                                                            if (!teamData || typeof teamData !== 'object' || !teamData.name) return null;
                                                            
                                                            const teamName = teamData.name;
                                                            const isActive = teamData.active !== false;
                                                            const hasChildren = teamData.children && typeof teamData.children === 'object' && Object.keys(teamData.children).length > 0;
                                                            
                                                            return (
                                                                <div key={teamId} style={{ marginLeft: `${depth * 16}px` }}>
                                                                    <div className={`flex items-center gap-2 py-1 ${!isActive ? 'opacity-50' : ''}`}>
                                                                        <Building2 className="w-3 h-3 text-aero-400" />
                                                                        <span className={`text-xs ${!isActive ? 'line-through' : ''}`}>
                                                                            {teamName}
                                                                        </span>
                                                                    </div>
                                                                    {hasChildren && renderTeamTree(teamData.children, depth + 1)}
                                                                </div>
                                                            );
                                                        });
                                                    };
                                                    
                                                    // Render visible top-level teams and their children
                                                    return visibleTeams.map(teamId => {
                                                        const teamData = adminNest[teamId];
                                                        const teamName = teamData?.name || teamId;
                                                        const isActive = teamData?.active !== false;
                                                        const hasChildren = teamData?.children && typeof teamData.children === 'object' && Object.keys(teamData.children).length > 0;
                                                        
                                                        return (
                                                            <div key={teamId} className="mb-2">
                                                                <div className={`flex items-center gap-2 py-1 font-medium ${!isActive ? 'opacity-50' : ''}`}>
                                                                    <Building2 className="w-3 h-3 text-aero-600" />
                                                                    <span className={`text-xs ${!isActive ? 'line-through' : ''}`}>
                                                                        {teamName}
                                                                    </span>
                                                                </div>
                                                                {hasChildren && renderTeamTree(teamData.children, 1)}
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Candidate Team Memberships - Show only for candidates */}
                                    {currentView === 'candidates' && selectedUser && userModalMode === 'EDIT' && (
                                        <div className="mt-4 pt-4 border-t border-aero-200">
                                            <h4 className="text-sm font-medium text-aero-900 mb-3 flex items-center gap-2">
                                                <Building2 className="w-4 h-4" />
                                                Team Memberships
                                            </h4>
                                            <div className="bg-aero-50 rounded-lg border border-aero-100 p-3">
                                                {isLoadingMemberships ? (
                                                    <div className="flex items-center justify-center py-4 text-aero-400">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-aero-400 border-t-transparent mr-2"></div>
                                                        Loading team memberships...
                                                    </div>
                                                ) : traineeTeamMemberships.length === 0 ? (
                                                    <p className="text-xs text-aero-400 italic">
                                                        No direct team memberships within your hierarchy
                                                    </p>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {traineeTeamMemberships.map(membership => (
                                                            <div key={membership.teamId} className="flex items-center justify-between py-1.5 px-2 bg-white rounded border border-aero-100">
                                                                <div className="flex items-center gap-2">
                                                                    <Building2 className="w-3 h-3 text-aero-600" />
                                                                    <span className="text-xs font-medium text-aero-900">{membership.teamName}</span>
                                                                </div>
                                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-aero-100 text-aero-600 uppercase font-medium">
                                                                    {membership.role}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    <div className="flex justify-end gap-2 mt-6">
                        <Button variant="ghost" onClick={() => setIsUserModalOpen(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveUser} disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : userModalMode === 'CREATE' ? (isExistingUser ? 'Add Users' : 'Create User') : 'Save Changes'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* View Progress Modal */}
            <Modal isOpen={isProgressModalOpen} onClose={() => setIsProgressModalOpen(false)} title={`Pathways for ${viewingUser?.name}`}>
                <div className="space-y-4">
                    {userPathways.length === 0 ? (
                        <div className="text-center py-8 text-aero-400 italic">
                            No pathways assigned to this candidate.
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                            {userPathways.map((p, idx) => (
                                <div
                                    key={idx}
                                    className="bg-white border border-aero-200 p-4 rounded-xl cursor-pointer hover:border-aero-400 hover:shadow-sm transition-all"
                                    onClick={() => {
                                        setIsProgressModalOpen(false);
                                        setPathwayProgressViewer({ isOpen: true, candidateId: viewingUser?.id || '', candidateName: viewingUser?.name || '', pathwayId: p.pathwayId, selectedTrack: p.selectedTrack });
                                    }}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-bold text-aero-900">{p.pathwayTitle}</h4>
                                        <div className="flex items-center gap-2">
                                            {p.selectedTrack && TRACK_LABELS[p.selectedTrack] && (
                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${TRACK_LABELS[p.selectedTrack].badge}`}>
                                                    {TRACK_LABELS[p.selectedTrack].label}
                                                </span>
                                            )}
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${
                                                p.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                                p.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                                {p.status.replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 bg-aero-100 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full transition-all duration-500 ${p.progress === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                                                style={{ width: `${p.progress}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-xs font-bold text-aero-600">{p.progress}%</span>
                                    </div>
                                    <div className="mt-2 text-xs text-aero-400">
                                        Last Activity: {new Date(p.lastActivity).toLocaleDateString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex justify-end pt-4">
                        <Button variant="secondary" onClick={() => setIsProgressModalOpen(false)}>Close</Button>
                    </div>
                </div>
            </Modal>

            {/* Pathway Progress Viewer */}
            <PathwayProgressViewerModal
                isOpen={pathwayProgressViewer.isOpen}
                onClose={() => setPathwayProgressViewer(v => ({ ...v, isOpen: false }))}
                candidateName={pathwayProgressViewer.candidateName}
                candidateId={pathwayProgressViewer.candidateId}
                pathwayId={pathwayProgressViewer.pathwayId}
                selectedTrack={pathwayProgressViewer.selectedTrack}
            />
        </div>
    );
};